/// <reference lib="dom" />
import { toast } from 'sonner';

// ========== 导入 Mock 数据 ==========
import {
  MOCK_COMMUNITIES,
  MOCK_PRODUCTS,
  MOCK_ARTICLES,
  MOCK_VIDEOS,
  MOCK_ANCESTORS,
  MOCK_FRIENDS,
  MOCK_CHAT_MESSAGES,
  MOCK_DIGITAL_HUMANS,
  MOCK_DATING_PROFILES,
  MOCK_IMAGES,
  MOCK_VIDEO_THUMBNAILS,
  MOCK_USERS,
  MOCK_FRIEND_RELATIONS,
  MOCK_FRIENDS_JINFENG,
  MOCK_FRIENDS_USER,
  MOCK_ARTICLES_JINFENG,
  MOCK_VIDEOS_JINFENG,
  MOCK_PRODUCTS_JINFENG,
  MOCK_ARTICLES_USER,
  MOCK_VIDEOS_USER,
  MOCK_PRODUCTS_USER,
  MOCK_COMMUNITIES_EXTENDED,
  MOCK_CHAT_MESSAGES_EXTENDED,
  MOCK_COMMUNITY_MEMBERS,
  MOCK_CHAT_ROOMS,
} from './mockData';

// 导出 Mock 数据供页面使用
export const mockData = {
  communities: MOCK_COMMUNITIES,
  products: MOCK_PRODUCTS,
  articles: MOCK_ARTICLES,
  videos: MOCK_VIDEOS,
  ancestors: MOCK_ANCESTORS,
  friends: MOCK_FRIENDS,
  chatMessages: MOCK_CHAT_MESSAGES,
  digitalHumans: MOCK_DIGITAL_HUMANS,
  datingProfiles: MOCK_DATING_PROFILES,
  images: MOCK_IMAGES,
  videoThumbnails: MOCK_VIDEO_THUMBNAILS,
  // 新增的用户数据
  users: MOCK_USERS,
  friendRelations: MOCK_FRIEND_RELATIONS,
  // jinfeng 的数据
  friendsJinfeng: MOCK_FRIENDS_JINFENG,
  articlesJinfeng: MOCK_ARTICLES_JINFENG,
  videosJinfeng: MOCK_VIDEOS_JINFENG,
  productsJinfeng: MOCK_PRODUCTS_JINFENG,
  // user 的数据
  friendsUser: MOCK_FRIENDS_USER,
  articlesUser: MOCK_ARTICLES_USER,
  videosUser: MOCK_VIDEOS_USER,
  productsUser: MOCK_PRODUCTS_USER,
  // 扩展的社区和聊天数据
  communitiesExtended: MOCK_COMMUNITIES_EXTENDED,
  chatMessagesExtended: MOCK_CHAT_MESSAGES_EXTENDED,
  communityMembers: MOCK_COMMUNITY_MEMBERS,
  chatRooms: MOCK_CHAT_ROOMS,
};

// 社区类型定义
export interface Community {
  id: string;
  name: string;
  avatar: string;
  coverImage?: string;
  category: string;
  type: string;
  description: string;
  memberCount: number;
  joinDate?: string;
  announcement?: string;
  totalPoints?: number;
  hasLeader?: boolean;
  leaderName?: string;
  leaderAvatar?: string;
  leaderVideo?: string;
  activities?: any[];
  products?: any[];
  oneVillageOneProduct?: any;
  history?: any[];
  videos?: any[];
  news?: any[];
  jobPosts?: any[];
  datingPosts?: any[];
  ancestors?: any[];
}

// 基础URL - 从环境变量中获取
// 开发环境强制使用相对路径，通过Vite代理转发到Gateway，避免浏览器网络连接问题
// 生产环境使用完整URL
// 强制开发环境使用空字符串（相对路径），忽略环境变量中的完整URL
// 测试环境：开发模式使用相对路径（通过代理），构建模式使用环境变量中的完整URL

// 远程和本地API地址（实际请求用 getApiBaseUrl 相对路径；降级逻辑注释中会引用）
const _REMOTE_API_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.globewe.com';
const _LOCAL_API_URL = 'http://localhost:8080';
void _REMOTE_API_URL;
void _LOCAL_API_URL;

// API地址配置：统一使用相对路径，通过Nginx代理访问远程接口
// 开发环境：使用相对路径，通过Vite代理转发到远程接口
// 生产环境：使用相对路径，通过Nginx代理转发到远程接口（避免SSL证书问题）
const getApiBaseUrl = (): string => {
  // 开发环境和生产环境都使用相对路径
  // 开发环境：通过Vite代理转发到远程接口
  // 生产环境：通过 Nginx 代理转发（用户访问 https://admin.globewe.com，/api/ 代理到 https://api.globewe.com）
  
  // 清除任何降级标记，确保使用相对路径
  const hasFallbackMark = localStorage.getItem('useLocalApi') === 'true';
  if (hasFallbackMark) {
    console.warn('[API] 检测到降级标记，已自动清除。应始终使用相对路径（通过代理）');
    localStorage.removeItem('useLocalApi');
    localStorage.removeItem('lastRemoteErrorTime');
  }
  
  // 始终使用相对路径（如 /api/...）
  // 开发环境：Vite代理会根据 .env.local 中的 VITE_API_BASE_URL 转发到远程接口
  // 生产环境：admin.globewe.com 的 Nginx 会将 /api/ 请求转发到接口域名 https://api.globewe.com
  return '';
};

// 注意：BASE_URL不再使用，改为在request函数中动态调用getApiBaseUrl()
// 这样可以确保每次请求都获取最新的API地址（支持自动降级）

// 创建带超时的fetch请求
// 增加超时时间以应对Gateway LoadBalancer路由延迟
const fetchWithTimeout = (url: string, options: RequestInit, timeout: number = 30000): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);
  
  // 合并请求选项，确保fetch配置正确
  // 注意：使用相对路径时，不需要cors mode（同源请求）
  const fetchOptions: RequestInit = {
    ...options,
    signal: controller.signal,
    // 使用相对路径时，mode应该是'same-origin'或'cors'（默认）
    // credentials只在需要cookies时使用
    // 确保headers正确设置
    headers: {
      ...(options.headers || {}),
      'Content-Type': (options.headers as Record<string, string>)?.['Content-Type'] || 'application/json'
    } as HeadersInit
  };
  
  return fetch(url, fetchOptions).then(response => {
    clearTimeout(timeoutId);
    return response;
  }).catch(error => {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('请求超时');
    }
    // 提供更详细的网络错误信息
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      // 检测是否是SSL证书错误（浏览器会阻止请求，导致Failed to fetch）
      // 注意：浏览器控制台会显示具体的SSL错误（如ERR_CERT_AUTHORITY_INVALID）
      const networkError = new Error('网络连接失败。如果浏览器显示SSL证书错误（ERR_CERT_AUTHORITY_INVALID），这是服务器端SSL证书问题，需要服务器管理员修复证书。');
      networkError.name = 'NetworkError';
      throw networkError;
    }
    throw error;
  });
};

// 统一的请求处理函数
const request = async (
  url: string, 
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST', 
  data?: any, 
  useToken: boolean = true
) => {
  try {
    // 构建请求头
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (useToken) {
      // 获取token
      let token = null;
      try {
        const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
        if (authData) {
          const auth = JSON.parse(authData);
          token = auth.token || auth.authToken;
        }
      } catch (e) {
        // 如果解析失败，尝试直接获取authToken
      }
      
      // 如果还没有token，尝试从authToken获取
      if (!token) {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      }
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        // 开发环境：记录Token已找到（但不显示完整Token）
        if (import.meta.env.DEV) {
          console.debug(`[API] Token已找到，长度: ${token.length}，前缀: ${token.substring(0, 20)}...`);
        }
      } else {
        // 调试日志：记录token缺失情况
        if (import.meta.env.DEV) {
          console.warn(`[API Warning] 未找到token，请求可能失败。检查localStorage中的auth和authToken`);
          // 检查localStorage中的token
          const authData = localStorage.getItem('auth');
          const authToken = localStorage.getItem('authToken');
          console.warn(`[API Debug] localStorage检查:`, {
            hasAuth: !!authData,
            hasAuthToken: !!authToken,
            authData: authData ? JSON.parse(authData) : null
          });
        }
      }
    }
    
    // 构建请求选项
    const requestOptions: RequestInit = {
      method,
      headers,
    };
    
    // GET请求将参数拼接到URL
    if (method === 'GET' && data) {
      const params = new URLSearchParams();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          params.append(key, String(data[key]));
        }
      });
      url = `${url}?${params.toString()}`;
    } else if (data) {
      // 其他请求将数据放入body
      requestOptions.body = JSON.stringify(data);
    }
    
    // 所有请求都通过Gateway（统一认证和路由）
    // 开发环境：使用相对路径，通过Vite代理转发到Gateway（避免浏览器网络连接问题）
    // 生产环境：使用完整URL，支持自动降级到本地接口
    
    // 获取当前应该使用的API基础URL（支持自动降级）
    let apiBaseUrl = getApiBaseUrl();
    
    // ⚠️ 开发环境和生产环境都强制使用相对路径
    // 开发环境：通过Vite代理转发
    // 生产环境：通过 Nginx 代理（admin.globewe.com 的 /api/ -> https://api.globewe.com）
    apiBaseUrl = '';
    if (import.meta.env.DEV) {
      const envApiUrl = import.meta.env.VITE_API_BASE_URL || '';
      console.debug(`[API] 开发环境：使用相对路径（Vite代理），代理目标: ${envApiUrl || 'http://127.0.0.1:8080'}`);
    } else if (import.meta.env.PROD) {
      console.debug(`[API] 生产环境：使用相对路径（Nginx 代理），代理目标（接口域名）: https://api.globewe.com`);
    }
    
    // 检查当前是否使用远程接口（用于判断是否需要降级）
    // 在开发环境中，如果环境变量配置了远程接口，即使使用相对路径（通过Vite代理），也应该视为远程接口
    const envApiUrl = import.meta.env.VITE_API_BASE_URL || '';
    const isEnvRemote = envApiUrl && 
                        !envApiUrl.includes('localhost') && 
                        !envApiUrl.includes('127.0.0.1') &&
                        (envApiUrl.startsWith('http://') || envApiUrl.startsWith('https://'));
    
    const _isUsingRemote = (apiBaseUrl && 
                            apiBaseUrl !== '' && 
                            !apiBaseUrl.includes('localhost') && 
                            !apiBaseUrl.includes('127.0.0.1') &&
                            (apiBaseUrl.startsWith('http://') || apiBaseUrl.startsWith('https://'))) ||
                           (!import.meta.env.PROD && isEnvRemote); // 开发环境但配置了远程接口
    void _isUsingRemote;
    
    // 如果BASE_URL为空，使用相对路径（通过Vite代理）
    // 如果BASE_URL包含localhost，强制替换为127.0.0.1
    if (apiBaseUrl && apiBaseUrl.includes('localhost')) {
      apiBaseUrl = apiBaseUrl.replace('localhost', '127.0.0.1');
    }
    
    // 开发环境：记录使用的API基础URL
    if (import.meta.env.DEV) {
      console.debug(`[API] 使用${apiBaseUrl ? '完整URL' : '相对路径（Vite代理）'}: ${apiBaseUrl || '(相对路径)'}`);
    }
    
    // 构建完整URL（如果apiBaseUrl为空，则使用相对路径）
    let fullUrl = apiBaseUrl ? `${apiBaseUrl}${url}` : url;

    // 对敏感数据进行脱敏处理（仅用于日志）
    const maskSensitiveData = (data: any): any => {
      if (!data || typeof data !== 'object') return data;

      const masked = { ...data };

      // 脱敏手机号（11位数字）
      Object.keys(masked).forEach(key => {
        const value = masked[key];
        if (typeof value === 'string' && /^\d{11}$/.test(value)) {
          masked[key] = `${value.substring(0, 3)}****${value.substring(7)}`;
        }
        // 脱敏身份证号（15或18位）
        else if (typeof value === 'string' && /^\d{15}(\d{2}[0-9Xx])?$/.test(value)) {
          const id = value;
          const len = id.length;
          masked[key] = `${id.substring(0, 3)}**********${id.substring(len - 4)}`;
        }
        // 脱敏email
        else if (typeof value === 'string' && value.includes('@')) {
          const [name, domain] = value.split('@');
          masked[key] = `${name.substring(0, 2)}***@${domain}`;
        }
      });

      return masked;
    };

    // 检查Token是否传递
    const hasToken = !!headers['Authorization'];
    console.log(`[API Request] ${method} ${fullUrl}`, {
      hasToken,
      tokenPrefix: hasToken ? headers['Authorization']?.substring(0, 20) + '...' : 'none',
      headers: { ...headers, Authorization: hasToken ? 'Bearer ***' : 'none' },
      data: data ? maskSensitiveData(data) : null,
      timestamp: new Date().toISOString()
    });
    
    // 如果使用Token但未找到，记录警告
    if (useToken && !hasToken) {
      console.warn(`[API Warning] 请求需要Token但未找到: ${method} ${fullUrl}`);
    }
    
    // 发送请求（带超时保护，默认30秒，应对Gateway LoadBalancer路由延迟）
    let response: Response;
    try {
      response = await fetchWithTimeout(fullUrl, requestOptions, 30000);
    } catch (error: any) {
      // 检测SSL证书错误或其他网络错误，触发自动降级
      const isNetworkError = error.name === 'TypeError' || 
                            error.name === 'NetworkError' ||
                            error.message === 'Failed to fetch' ||
                            error.message?.includes('ERR_CERT') ||
                            error.message?.includes('证书') ||
                            error.message?.includes('SSL') ||
                            error.message?.includes('TLS');
      
      // 注意：已禁用自动降级功能，统一使用远程接口 https://api.globewe.com
      // 如果需要启用降级功能（网络错误时切换到本地接口），可以取消下面的注释
      /*
      // 如果是远程接口的网络错误（SSL证书错误、连接失败等），尝试降级到本地接口
      const shouldFallback = isNetworkError && (
        _isUsingRemote ||
        (!import.meta.env.PROD && fullUrl.startsWith('https://')) ||
        (!import.meta.env.PROD && !apiBaseUrl && import.meta.env.VITE_API_BASE_URL?.includes('https://'))
      );
      
      if (shouldFallback) {
        console.warn(`[API Fallback] 远程接口网络错误（可能是SSL证书问题），尝试切换到本地接口...`);
        console.warn(`[API Fallback] 错误信息: ${error.message}`);
        console.warn(`[API Fallback] 原始URL: ${fullUrl}`);
        
        localStorage.setItem('useLocalApi', 'true');
        localStorage.setItem('lastRemoteErrorTime', Date.now().toString());
        
        let localFullUrl: string;
        if (import.meta.env.PROD) {
          const localApiUrl = _LOCAL_API_URL.replace('localhost', '127.0.0.1');
          localFullUrl = `${localApiUrl}${url}`;
        } else {
          console.warn(`[API Fallback] 开发环境：清除降级标记，下次请求将重新尝试远程接口`);
          localStorage.removeItem('useLocalApi');
          localStorage.removeItem('lastRemoteErrorTime');
          localFullUrl = url;
        }
        
        console.log(`[API Fallback] 重试请求到本地接口: ${localFullUrl}`);
        
        try {
          const retryResponse = await fetchWithTimeout(localFullUrl, requestOptions, 30000);
          
          if (retryResponse.ok) {
            console.log(`[API Fallback] ✅ 本地接口请求成功`);
            response = retryResponse;
            const responseText = await response.text();
            let result;
            try {
              result = JSON.parse(responseText);
            } catch {
              result = { code: response.status, message: response.statusText, data: responseText };
            }
            return result;
          } else {
            console.error(`[API Fallback] ❌ 本地接口也返回错误: ${retryResponse.status}`);
            response = retryResponse;
          }
        } catch (retryError: any) {
          console.error(`[API Fallback] ❌ 本地接口请求失败:`, retryError.message);
        }
      }
      */
      
      // 提供更详细的错误信息
      const errorDetails: any = {
        error: error.message,
        errorType: error.name,
        url: fullUrl,
        method: method,
        timestamp: new Date().toISOString()
      };
      
      // 如果是网络错误，提供更多信息
      if (isNetworkError) {
        // 检查浏览器控制台是否有SSL证书错误
        const hasSSLError = error.message?.includes('ERR_CERT') || 
                           error.message?.includes('证书') ||
                           error.message?.includes('SSL') ||
                           error.message?.includes('TLS');
        
        if (hasSSLError) {
          errorDetails.suggestion = '⚠️ SSL证书验证失败。\n\n如果使用相对路径（/api/）仍然出现此错误，请检查：\n1) Nginx代理配置是否正确\n2) Nginx是否正常运行\n3) 服务器端SSL证书配置\n\n建议：确保Nginx配置了正确的API代理（location /api/）。';
          errorDetails.errorType = 'SSLCertificateError';
        } else {
          errorDetails.suggestion = '网络连接失败。请检查：\n1) API服务器是否正常运行\n2) Nginx代理配置是否正确（location /api/）\n3) 网络连接是否正常\n4) 浏览器控制台Network标签页查看详细错误';
        }
      } else if (error.message === '请求超时') {
        errorDetails.suggestion = '请求超时，可能是服务响应慢或网络问题';
      }
      
      // 输出详细的错误信息，方便调试
      console.error(`[API Request Failed] ${method} ${fullUrl}`);
      console.error('错误详情:', {
        error: error.message,
        errorType: error.name,
        url: fullUrl,
        method: method,
        timestamp: new Date().toISOString(),
        suggestion: errorDetails.suggestion || '请检查网络连接和服务状态'
      });
      console.error('原始错误对象:', error);
      throw error;
    }
    
    // 仅对成功响应打 [API Response]，避免与 [API Error] 重复
    if (response.ok) {
      console.log(`[API Response] ${method} ${fullUrl}`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
    }
    
    // 检查响应状态
    if (!response.ok) {
      let errorText = await response.text();
      
      // 注意：已禁用自动降级功能，统一使用远程接口 https://api.globewe.com
      // 如果需要启用降级功能（500/503错误时切换到本地接口），可以取消下面的注释
      const _isRemoteError = false; // 禁用降级：统一使用远程接口
      void _isRemoteError;
      /*
      const isRemoteError = _isUsingRemote && (response.status === 500 || response.status === 503);
      
      if (isRemoteError) {
        console.warn(`[API Fallback] 远程接口返回${response.status}错误，尝试切换到本地接口...`);
        console.warn(`[API Fallback] 原始URL: ${fullUrl}`);
        
        // 标记使用本地接口
        localStorage.setItem('useLocalApi', 'true');
        localStorage.setItem('lastRemoteErrorTime', Date.now().toString());
        
        // 切换到本地接口重试
        let localFullUrl: string;
        if (import.meta.env.PROD) {
          // 生产环境：使用完整的本地URL
          const localApiUrl = _LOCAL_API_URL.replace('localhost', '127.0.0.1');
          localFullUrl = `${localApiUrl}${url}`;
        } else {
          // 开发环境：清除降级标记，使用相对路径让Vite代理处理
          // 如果Vite代理配置了远程接口但失败，这里不应该绕过代理直接访问本地
          // 而是应该清除标记，让系统重新尝试远程接口（通过Vite代理）
          console.warn(`[API Fallback] 开发环境：清除降级标记，下次请求将重新尝试远程接口`);
          localStorage.removeItem('useLocalApi');
          localStorage.removeItem('lastRemoteErrorTime');
          // 使用相对路径，让Vite代理处理
          localFullUrl = url;
        }
        
        console.log(`[API Fallback] 重试请求到本地接口: ${localFullUrl}`);
        
        try {
          // 使用本地接口重试请求
          const retryResponse = await fetchWithTimeout(localFullUrl, requestOptions, 30000);
          
          if (retryResponse.ok) {
            console.log(`[API Fallback] ✅ 本地接口请求成功`);
            // 继续处理响应
            response = retryResponse;
            // 重新读取响应内容
            const retryResponseText = await response.text();
            
            // 解析响应并返回
            let result;
            try {
              result = JSON.parse(retryResponseText);
            } catch {
              result = { code: response.status, message: response.statusText, data: retryResponseText };
            }
            
            // 如果响应成功，直接返回结果
            if (result.code === 200 || result.code === 0 || response.ok) {
              return result;
            }
            
            // 更新errorText以便后续处理
            errorText = retryResponseText;
          } else {
            // 本地接口也失败，继续原有错误处理流程
            console.error(`[API Fallback] ❌ 本地接口也返回错误: ${retryResponse.status}`);
            response = retryResponse;
            // 更新errorText
            errorText = await retryResponse.text();
          }
        } catch (retryError: any) {
          console.error(`[API Fallback] ❌ 本地接口请求失败:`, retryError.message);
          // 本地接口也失败，继续原有错误处理流程
        }
      }
      */
      
      // 404错误处理：对于已知未实现的API，静默处理
      const isKnownUnimplementedAPI = url.includes('/api/friends/requests/pending') || 
                                      url.includes('/api/admin/users/') ||
                                      url.includes('/api/admin/admins') ||
                                      url.includes('/api/admin/credit/') ||
                                      url.includes('/api/community/election-rules') ||
                                      url.includes('/api/community/meeting-rules') ||
                                      url.includes('/api/community/ad-spaces') ||
                                      url.includes('/api/documents') ||
                                      url.includes('/api/surname/') ||
                                      (url.includes('/api/community/communities/') && url.includes('/history'));
      
      if (response.status === 404 && isKnownUnimplementedAPI) {
        // 静默处理，只记录信息（不显示错误）
        console.log(`[API Info] ${method} ${fullUrl} - API未实现（404），这是预期的，前端将使用降级方案`);
        // 直接返回404响应，不抛出错误
        return {
          code: 404,
          message: 'API未实现',
          data: null,
          status: 404
        };
      } else {
        // 占位，下面解析后统一打日志
      }
      
      let errorData: { code?: number; message?: string; data?: { code?: number; message?: string }; error?: string; detail?: string; path?: string; timestamp?: string };
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: `HTTP ${response.status} ${response.statusText}` };
      }
      
      // 接口返回结构兼容：res.data.code / res.data.message 或 res.code / res.message
      const code = errorData.code ?? errorData.data?.code ?? response.status;
      const apiMessage = errorData.message ?? errorData.data?.message ?? errorData.error ?? errorData.detail;
      const errorMessage = apiMessage ?? `HTTP ${response.status} ${response.statusText}`;
      const is503 = response.status === 503;
      
      // 503 用 warn 减少刷屏，其他错误用 error
      if (is503) {
        console.warn(`[API 503] ${method} ${fullUrl} 服务暂时不可用`);
      } else {
        console.error(`[API Error] ${method} ${fullUrl} code=${code} message=${String(apiMessage ?? '')}`);
      }
      
      // 检查是否为服务不可用错误（Service not found / Nacos）
      const isServiceUnavailable = errorMessage.includes('Service not found') || 
                                   errorMessage.includes('Nacos') ||
                                   errorText.includes('Service not found') ||
                                   errorText.includes('Nacos');
      
      // 检查是否为已知的数据库错误（社区会议API的数据库操作失败）
      const isKnownDatabaseError = response.status === 500 && 
                                   (errorMessage.includes('数据库操作失败') || 
                                    errorMessage.includes('数据库') ||
                                    url.includes('/api/community/meetings/active/'));
      
      // 如果是服务不可用错误，标记为静默处理
      if (isServiceUnavailable) {
        console.warn(`[API Service Unavailable] ${method} ${fullUrl} - 服务未找到，可能是服务未注册到Nacos`);
      } else if (response.status === 500 && !isKnownDatabaseError) {
        // 如果是500错误，提供更详细的调试信息（但已知的数据库错误静默处理）
        console.error(`[API 500 Error] ${method} ${fullUrl}`, {
          status: response.status,
          errorData: errorData,
          path: errorData.path,
          timestamp: errorData.timestamp,
          suggestion: '请检查后端服务日志以获取详细错误信息'
        });
      } else if (isKnownDatabaseError) {
        // 已知的数据库错误，完全静默处理，不输出任何日志
        // 这是后端已知问题，前端已实现降级处理（CommunityMeetingWindow.tsx）
      }
      
      // 创建错误对象，添加 status、code 等属性
      const error: any = new Error(errorMessage);
      error.status = response.status;
      error.code = code;
      error.response = errorText;
      error.isServiceUnavailable = isServiceUnavailable;
      error.is503 = is503; // 标记 503，catch 里用 warn 减少刷屏
      
      // 对于已知未实现的API的404错误，标记为静默处理
      if (response.status === 404 && isKnownUnimplementedAPI) {
        error.silent = true; // 标记为静默错误
      }
      
      // 根据 code 做提示：401 跳转登录，403 未登录时引导重新登录、否则权限不足，422/40003/503 友好文案
      if (!error.silent) {
        if (code === 401) {
          localStorage.removeItem('auth');
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('auth');
          sessionStorage.removeItem('authToken');
          window.dispatchEvent(new CustomEvent('auth:token-expired', { detail: { message: errorMessage } }));
          toast.error('未登录或登录已过期，请重新登录');
          setTimeout(() => { window.location.href = '/admin/login'; }, 800);
        } else if (code === 403 || response.status === 403) {
          const msg = errorMessage || '权限不足';
          const isLoginRequired = msg.includes('未登录') || msg.includes('请先登录') || msg.includes('请重新登录') ||
            msg.includes('登录已过期') || msg.includes('token') || msg.includes('Token') || msg.includes('认证') ||
            msg.includes('CORS');
          if (isLoginRequired) {
            localStorage.removeItem('auth');
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('auth');
            sessionStorage.removeItem('authToken');
            window.dispatchEvent(new CustomEvent('auth:token-expired', { detail: { message: '未登录或登录已过期' } }));
            toast.error('未登录或登录已过期，请重新登录');
            setTimeout(() => { window.location.href = '/admin/login'; }, 800);
          } else {
            toast.error(msg || '权限不足，无法执行此操作');
          }
        } else if (code === 422) {
          toast.error(errorMessage || '请求参数有误');
        } else if (code === 40003) {
          toast.error('库存不足');
        } else if (is503) {
          toast.error('服务暂时不可用，请稍后重试');
        } else {
          toast.error(errorMessage || '请求失败，请稍后重试');
        }
      }
      
      throw error;
    }
    
    const result = await response.json();
    
    // 检查是否为已知的数据库错误（社区会议API的数据库操作失败）
    const isKnownDatabaseError = result.code === 500 && 
                                 (result.message?.includes('数据库操作失败') || 
                                  result.message?.includes('数据库') ||
                                  url.includes('/api/community/meetings/active/'));
    
    // 已知的数据库错误，静默处理，不输出任何日志，直接返回
    if (isKnownDatabaseError) {
      return result;
    }
    
    // 其他情况正常输出日志
    console.log(`[API Success] ${method} ${fullUrl}`, {
      code: result.code,
      message: result.message,
      hasData: !!result.data
    });
    
    // 检查业务状态码
    if (result.code !== 200 && result.code !== 0) {
      if (result.code === 503) {
        console.warn(`[API 503] ${method} ${fullUrl} 服务暂时不可用`);
      } else {
        console.error(`[API Error] ${method} ${fullUrl} code=${result.code} message=${String(result.message ?? '')}`);
      }
      
      // 如果是401未授权错误，或 403 且文案表明需重新登录，提示并跳转登录
      const msg = result.message ?? '';
      const isAuthError = result.code === 401 || msg.includes('未授权') || msg.includes('请先登录');
      const is403LoginRequired = result.code === 403 && (msg.includes('未登录') || msg.includes('请先登录') || msg.includes('请重新登录') || msg.includes('登录已过期') || msg.includes('token') || msg.includes('Token') || msg.includes('认证') || msg.includes('CORS'));
      if (isAuthError || is403LoginRequired) {
        const hasToken = !!headers['Authorization'];
        console.error(`[API Auth Error] ${method} ${fullUrl}`, {
          code: result.code,
          message: result.message,
          hasToken,
          requestUrl: fullUrl,
          suggestion: '请检查：1) 用户是否已登录 2) Token是否有效 3) Token是否已传递'
        });
        
        const isAuthEndpoint = url.includes('/auth/login') || 
                              url.includes('/auth/register') || 
                              url.includes('/auth/refresh');
        
        if (hasToken && !isAuthEndpoint) {
          console.warn('[API] Token已过期或无效，清除本地认证数据');
          localStorage.removeItem('auth');
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('auth');
          sessionStorage.removeItem('authToken');
          window.dispatchEvent(new CustomEvent('auth:token-expired', {
            detail: { message: result.message || '登录已过期，请重新登录' }
          }));
          toast.error('未登录或登录已过期，请重新登录');
          setTimeout(() => { window.location.href = '/admin/login'; }, 800);
        } else {
          toast.error('未登录或登录已过期，请重新登录');
          setTimeout(() => { window.location.href = '/admin/login'; }, 800);
        }
      } else {
        // 根据 code 做提示：503 友好文案，422/40003/403 等
        if (result.code === 503) {
          toast.error('服务暂时不可用，请稍后重试');
        } else if (result.code === 422) {
          toast.error(result.message || '请求参数有误');
        } else if (result.code === 40003) {
          toast.error('库存不足');
        } else if (result.code === 403) {
          toast.error(result.message || '权限不足，无法执行此操作');
        } else {
          toast.error(result.message || '请求失败，请稍后重试');
        }
      }
      
      // 对于业务错误（如409、400、40401、40402等），直接返回响应，不要抛出异常
      // 这样调用方可以正确处理业务错误，而不是被当作网络错误
      // 只有真正的系统错误（如500）才抛出异常
      // 注意：业务错误码可能是3位数（如400、404）或5位数（如40401、40402）
      // 5位数的业务错误码格式：4xxyy，其中4xx是HTTP状态码，yy是具体业务错误码
      const codeStr = String(result.code);
      const isBusinessError = (result.code >= 400 && result.code < 500) || 
                              (result.code >= 40000 && result.code < 50000) ||
                              codeStr.startsWith('4');
      
      if (isBusinessError) {
        // 业务错误，直接返回（保留原始错误码）
        if (import.meta.env.DEV) {
          console.log(`[API Business Error] ${method} ${fullUrl}`, {
            code: result.code,
            message: result.message,
            isBusinessError: true
          });
        }
        return result;
      }
      
      // 系统错误（500+）才抛出异常
      throw new Error(result.message || '请求失败');
    }
    
    return result;
  } catch (error: any) {
    // 返回一个默认的错误响应，而不是抛出异常，让调用方可以处理
    // 这样页面不会因为API错误而完全无法显示
    // 区分网络错误和服务器错误
    const errorMessage = error.message || '网络请求失败';
    
    // 判断是否为网络错误（合并所有网络错误判断条件）
    const isNetworkError = error.name === 'NetworkError' || 
                          error.name === 'TypeError' ||
                          error.message?.includes('网络连接失败') ||
                          error.message?.includes('Failed to fetch') ||
                          error.message?.includes('请求超时') ||
                          error.message?.includes('timeout') ||
                          error.message === 'Failed to fetch';
    
    // 检查是否为已知的数据库错误（社区会议API的数据库操作失败）
    const isKnownDatabaseError = errorMessage.includes('数据库操作失败') || 
                                 errorMessage.includes('数据库') ||
                                 error?.response?.includes('/api/community/meetings/active/') ||
                                 (error?.status === 200 && errorMessage.includes('数据库'));
    
    // 503 已在上面用 warn 打过，这里不再重复 error 日志
    const is503 = error?.status === 503 || error?.is503 === true;
    
    // 已知的数据库错误完全静默处理，不输出任何日志
    if (!isKnownDatabaseError && !is503) {
      // 只在开发环境或非网络错误时显示toast
      if (!isNetworkError && import.meta.env.DEV) {
        console.warn('[API Error]', errorMessage);
      }
    }
    
    const isServerError = is503 || errorMessage.includes('500') || 
                         errorMessage.includes('Internal Server Error') ||
                         (errorMessage.includes('服务器') && !isNetworkError);
    
    // 网络错误使用-1，服务器错误使用500
    const errorCode = isServerError ? 500 : -1;
    
    if (is503) {
      console.warn('[API Error] HTTP 503 Service Temporarily Unavailable');
    } else if (isServerError && !isKnownDatabaseError) {
      // 非已知数据库错误的服务器错误才输出日志
      console.error(`[API Server Error] 服务器内部错误，请检查后端服务状态`, {
        error: error.message,
        suggestion: '请检查：1) 后端服务是否正常运行 2) 后端日志中的具体错误信息 3) 数据库连接是否正常'
      });
    } else if (isNetworkError && !isKnownDatabaseError) {
      // 非已知数据库错误的网络错误才输出日志
      const sslErrorHint = typeof window !== 'undefined' && window.location.protocol === 'https:' 
        ? '\n\n💡 提示：如果浏览器控制台显示 ERR_CERT_AUTHORITY_INVALID，这是SSL证书问题，需要服务器管理员修复证书。'
        : '';
      
      console.error(`[API Network Error] 网络连接失败`, {
        error: error.message,
        suggestion: `请检查：1) API服务器（https://api.globewe.com）是否正常运行 2) 浏览器网络连接 3) 防火墙或安全软件设置 4) 查看浏览器控制台Network标签页获取详细错误信息${sslErrorHint}`
      });
    }
    
    // 后端服务原因（503/500 等）或网络异常：给用户友好提示
    if (is503) {
      toast.error('服务暂时不可用，请稍后重试');
    } else if (isServerError && !isKnownDatabaseError) {
      toast.error('后端服务异常，请稍后重试');
    } else if (isNetworkError) {
      toast.error('网络异常，请检查网络后重试');
    }
    
    return {
      code: errorCode,
      message: is503 ? '服务暂时不可用，请稍后重试' : (isServerError ? '后端服务异常，请稍后重试' : errorMessage),
      data: null
    };
  }
};

// 用户相关API
export const userAPI = {
  /**
   * 管理端登录（显式指定 ADMIN 身份，不依赖 Header 自动检测）
   * - 密码登录：管理员账号 + 密码
   * - 验证码登录：绑定手机号 + 验证码
   * 使用推荐接口 /api/user/auth/login/admin
   */
  adminLogin: async (data: {
    loginType: 'password' | 'sms';
    username: string;
    password?: string;
    phone?: string;
    code?: string;
  }) => {
    try {
      const requestData: Record<string, string> = {};

      if (data.loginType === 'sms') {
        // 验证码登录：手机号 + 验证码（后端自动识别 admin 身份）
        requestData.phone = (data.phone || '').trim();
        requestData.code = (data.code || '').trim();
      } else {
        // 密码登录：账号 + 密码
        requestData.username = data.username.trim();
        requestData.password = data.password || '';
      }

      if (import.meta.env.DEV) {
        console.log('[Admin Login] 请求:', {
          loginType: data.loginType,
          username: requestData.username,
          hasPassword: !!requestData.password,
          phone: requestData.phone,
          hasCode: !!requestData.code,
        });
      }

      // 使用新推荐接口，显式指定 admin 身份，兼容所有代理方式
      const response = await request('/api/user/auth/login/admin', 'POST', requestData, false);

      if (import.meta.env.DEV) {
        console.log('[Admin Login] 响应:', response);
      }

      return response;
    } catch (error: any) {
      console.error('[Admin Login] 异常:', error);
      return {
        code: 500,
        message: error.message || '登录失败，请检查网络连接或联系管理员',
        data: null,
      };
    }
  },

  // 登录（不需要token）
  // C 端 / 通用：Auth 服务；管理端请使用 adminLogin
  login: async (data: {
    username?: string;
    password?: string;
    phone?: string;
    email?: string;
    smsCode?: string;
    code?: string; // 后端期望的参数名
    countryCode?: string; // 国家代码，默认+86
    loginType?: 'password' | 'sms'; // v1.0新增：登录类型
  }) => {
    try {
      if (import.meta.env.DEV) {
        console.log('[User Login] 尝试登录:', {
          username: data.username,
          phone: data.phone,
          email: data.email,
          hasPassword: !!data.password,
          hasSmsCode: !!data.smsCode,
          hasCode: !!data.code,
          countryCode: data.countryCode,
          loginType: data.loginType
        });
      }

      // 构建请求参数（符合管理端登录接口v1.0规范）
      const requestData: any = {
        loginType: data.loginType || 'password' // 默认密码登录
      };
      
      // 处理用户名/手机号（必须带国际区号）
      if (data.loginType === 'sms') {
        // 短信验证码登录
        requestData.phone = data.phone || data.username;
        // 确保手机号带国际区号
        if (requestData.phone && !requestData.phone.startsWith('+')) {
          requestData.phone = (data.countryCode || '+86') + requestData.phone;
        }
        requestData.code = data.code || data.smsCode;
      } else {
        // 密码登录
        requestData.username = data.username || data.phone || data.email;
        // 确保username带国际区号（如果是手机号）
        if (requestData.username && /^[0-9]{11}$/.test(requestData.username)) {
          requestData.username = (data.countryCode || '+86') + requestData.username;
        }
        requestData.password = data.password;
      }

      // 使用正确的路径：/api/auth/login（Auth服务）
      const response = await request('/api/auth/login', 'POST', requestData, false);

      if (import.meta.env.DEV) {
        console.log('[User Login] 登录响应:', response);
      }

      // 如果返回500错误，提供更详细的错误信息
      if (response && (response.code === 500 || response.code === -1)) {
        console.error('[User Login] 登录失败，服务器错误:', {
          code: response.code,
          message: response.message,
          response: response
        });
      }

      return response;
    } catch (error: any) {
      // 错误信息始终输出，方便调试
      console.error('[User Login] 登录异常:', {
        error: error.message,
        stack: error.stack,
        name: error.name,
        data: data
      });
      return {
        code: 500,
        message: error.message || '登录失败，请检查网络连接或联系管理员',
        data: null
      };
    }
  },

  // 注册（公开接口，不需要token）
  register: async (data: { 
    username: string; 
    password: string; 
    nickname?: string;
    avatar?: string; // 头像 URL（若注册前已上传）
    email?: string;
    phone?: string;
    phoneNumber?: string; // 兼容字段，会转换为phone
    regType?: number; // 兼容字段，会被忽略
    confirmPassword?: string; // 兼容字段，会被忽略
    countryCode?: string;
    regionCode?: string;
    language?: string;
    timezone?: string;
    smsCode?: string;
    verificationCode?: string;
    captchaVerifyToken?: string;
  }) => {
    try {
      // 转换数据格式以匹配后端RegisterRequest
      const requestData: any = {
        username: data.username,
        password: data.password,
      };
      
      // 添加可选字段
      if (data.nickname) requestData.nickname = data.nickname;
      if (data.avatar) requestData.avatar = data.avatar;
      if (data.phone) requestData.phone = data.phone;
      if (data.phoneNumber) requestData.phone = data.phoneNumber; // 兼容phoneNumber字段
      if (data.email) requestData.email = data.email;
      if (data.countryCode) requestData.countryCode = data.countryCode;
      if (data.regionCode) requestData.regionCode = data.regionCode;
      if (data.language) requestData.language = data.language;
      if (data.timezone) requestData.timezone = data.timezone;
      if (data.smsCode) requestData.smsCode = data.smsCode;
      if (data.verificationCode) requestData.verificationCode = data.verificationCode;
      if (data.captchaVerifyToken) requestData.captchaVerifyToken = data.captchaVerifyToken;
      
      // 使用正确的路径：/api/user/auth/register，且不需要token
      const response = await request('/api/user/auth/register', 'POST', requestData, false);
      return response;
    } catch (error: any) {
      console.error('Register error:', error);
      return { 
        code: 500, 
        message: error.message || '注册失败', 
        data: null 
      };
    }
  },

  // 获取用户资料
  getProfile: async () => {
    try {
      const response = await request('/api/user/profile', 'GET');
      return response;
    } catch (error) {
      console.error('Get profile error:', error);
      return { code: 500, message: '获取用户资料失败', data: null };
    }
  },

  // 更新用户资料
  updateProfile: async (data: any) => {
    try {
      const response = await request('/api/user/profile', 'PUT', data);
      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      return { code: 500, message: '更新用户资料失败', data: null };
    }
  },

  // 退出登录
  logout: async () => {
    try {
      const response = await request('/api/auth/logout', 'POST');
      return response;
    } catch (error) {
      console.error('Logout error:', error);
      return { code: 500, message: '退出登录失败', data: null };
    }
  },

  // 修改密码
  changePassword: async (data: { 
    oldPassword: string; 
    newPassword: string; 
    confirmNewPassword?: string;
  }) => {
    try {
      const response = await request('/api/user/auth/change-password', 'POST', {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword ?? data.newPassword
      });
      return response;
    } catch (error) {
      console.error('Change password error:', error);
      return { code: 500, message: '修改密码失败', data: null };
    }
  },

  // 发送密码重置验证码 §3.4
  sendPasswordResetCode: async (phone: string) => {
    try {
      const phoneWithCode = phone.startsWith('+') ? phone : `+86${phone}`;
      return await request('/api/auth/password-reset/send-code', 'POST', { phone: phoneWithCode }, false);
    } catch (error: any) {
      console.error('Send password reset code error:', error);
      return { code: 500, message: error.message || '发送验证码失败', data: null };
    }
  },

  // 验证验证码并重置密码 §3.4
  verifyPasswordReset: async (data: { phone: string; code: string; newPassword: string }) => {
    try {
      const phoneWithCode = data.phone.startsWith('+') ? data.phone : `+86${data.phone}`;
      return await request('/api/auth/password-reset/verify', 'POST', {
        phone: phoneWithCode,
        code: data.code,
        newPassword: data.newPassword
      }, false);
    } catch (error: any) {
      console.error('Verify password reset error:', error);
      return { code: 500, message: error.message || '密码重置失败', data: null };
    }
  },

  // ========== 微信 H5 授权登录（从微信入口进入：授权获取 openid/昵称/手机号 -> 注册 -> 设置密码；已绑定手机后再次进入免登） ==========
  /** 用微信回调的 code 换 openid 与会话，后端返回 needRegister/needSetPassword 或直接登录 */
  wechatCode2Session: async (code: string) => {
    try {
      const response = await request('/api/user/auth/wechat/code2session', 'POST', { code }, false);
      return response;
    } catch (error: any) {
      console.error('Wechat code2session error:', error);
      return { code: 500, message: error.message || '微信授权失败', data: null };
    }
  },
  /** 微信授权注册：提交 openid、昵称、手机号（或加密数据），后端创建用户并绑定；返回 needSetPassword 时前端跳转设置密码页 */
  wechatRegister: async (data: {
    openid: string;
    wechatSessionToken?: string;
    nickname?: string;
    avatarUrl?: string;
    encryptedData?: string;
    iv?: string;
    phone?: string;
  }) => {
    try {
      const response = await request('/api/user/auth/wechat/register', 'POST', data, false);
      return response;
    } catch (error: any) {
      console.error('Wechat register error:', error);
      return { code: 500, message: error.message || '微信注册失败', data: null };
    }
  },
  /** 微信用户设置密码（首次注册后或未设密用户） */
  wechatSetPassword: async (data: { openid: string; wechatSessionToken?: string; newPassword: string }) => {
    try {
      const response = await request('/api/user/auth/wechat/set-password', 'POST', data, false);
      return response;
    } catch (error: any) {
      console.error('Wechat set password error:', error);
      return { code: 500, message: error.message || '设置密码失败', data: null };
    }
  },
  /** 微信登录：传 code 或 openid（与后端约定），已绑定手机后可直接拿 token，无需再次授权 */
  wechatLogin: async (data: { code?: string; openid?: string; wechatSessionToken?: string }) => {
    try {
      const response = await request('/api/user/auth/wechat/login', 'POST', data, false);
      return response;
    } catch (error: any) {
      console.error('Wechat login error:', error);
      return { code: 500, message: error.message || '微信登录失败', data: null };
    }
  },
  /** 获取微信网页授权跳转 URL（后端生成，前端跳转后微信会带 code 回调到 redirect_uri） */
  getWechatAuthUrl: async (redirectUri?: string, state?: string) => {
    try {
      const params = new URLSearchParams();
      if (redirectUri) params.set('redirectUri', redirectUri);
      if (state) params.set('state', state);
      const response = await request(`/api/user/auth/wechat/authorize-url?${params.toString()}`, 'GET', undefined, false);
      return response;
    } catch (error: any) {
      console.error('Get wechat auth url error:', error);
      return { code: 500, message: error.message || '获取授权链接失败', data: null };
    }
  },

  // 获取实名认证状态
  getRealNameStatus: async () => {
    try {
      const response = await request('/api/user/real-name/status', 'GET');
      return response;
    } catch (error: any) {
      console.error('Get real name status error:', error);
      return {
        code: 500,
        message: error.message || '获取实名认证状态失败',
        data: null
      };
    }
  },

  // 实名认证提交（支持多证件类型）
  // 对接说明：idType 可选，不传则后端按 "ID_CARD" 处理；传则用 data.idType（ID_CARD、id_card、passport、driver_license 等）
  // idCardFrontUrl/idCardBackUrl 可选，用于证件正反面照片，便于后端 OCR 与存档
  submitAliyunRealNameVerification: async (data: {
    firstName: string;
    lastName: string;
    idNumber: string;
    phone: string;
    verificationCode: string;
    idType?: string; // 可选。不传则后端按 ID_CARD 处理；传则用此值（如 ID_CARD、id_card、passport、driver_license）
    idCardFrontUrl?: string; // 可选。证件正面照片 URL，便于 OCR 与存档
    idCardBackUrl?: string; // 可选。证件背面照片 URL，便于 OCR 与存档
  }) => {
    try {
      const requestData: Record<string, unknown> = {
        firstName: data.firstName,
        lastName: data.lastName,
        idCardNumber: data.idNumber,
        phoneNumber: data.phone,
        verificationCode: data.verificationCode,
        countryCode: 'CN',
        regionCode: null
      };
      // idType：可选，不传则后端按 ID_CARD 处理
      if (data.idType != null && data.idType !== '') {
        requestData.idType = data.idType;
      }
      // 证件照 URL：可选，有值再传便于后端 OCR 与存档
      if (data.idCardFrontUrl != null && data.idCardFrontUrl !== '') {
        requestData.idCardFrontUrl = data.idCardFrontUrl;
      }
      if (data.idCardBackUrl != null && data.idCardBackUrl !== '') {
        requestData.idCardBackUrl = data.idCardBackUrl;
      }
      const response = await request('/api/user/real-name/submit', 'POST', requestData);
      return response;
    } catch (error: any) {
      console.error('Real name verification error:', error);
      return {
        code: 500,
        message: error.message || '实名认证失败，请检查信息是否正确',
        data: null
      };
    }
  },

  // 发送手机验证码
  sendPhoneCode: async (data: {
    phone: string;
    type?: string; // 验证码类型：register/login/reset/bind/change
    captchaToken?: string; // 阿里云验证码2.0 token
  }) => {
    try {
      // 后端API使用@RequestParam，参数应该在URL查询字符串中
      const scene = data.type || 'register';
      let url = `/api/user/sms/send?phoneNumber=${encodeURIComponent(data.phone)}&scene=${scene}`;
      
      // 如果提供了验证码token，添加到URL参数
      if (data.captchaToken) {
        url += `&captchaVerifyToken=${encodeURIComponent(data.captchaToken)}`;
      }

      // 对手机号进行脱敏处理（仅用于日志）
      const maskPhone = (phone: string) => {
        if (!phone || phone.length !== 11) return phone;
        return `${phone.substring(0, 3)}****${phone.substring(7)}`;
      };
      const maskedPhone = maskPhone(data.phone);

      if (import.meta.env.DEV) {
        console.log('[Send Phone Code] 发送验证码请求:', {
          phone: maskedPhone,
          type: scene,
          url: url
        });
      }

      // 使用统一的request函数，POST请求但参数在URL中，发送空的JSON对象作为body
      const response = await request(url, 'POST', {}, false);

      if (import.meta.env.DEV) {
        console.log('[Send Phone Code] 验证码发送响应:', response);
        // 开发环境下，如果后端返回了验证码，在控制台显示
        if (response.data && (response.data.code || response.data.verificationCode || typeof response.data === 'string')) {
          const code = response.data.code || response.data.verificationCode || response.data;
          console.log(`[Send Phone Code] 开发环境验证码: ${code}`);
        }
      }

      // 如果返回的不是成功状态，抛出错误
      if (response.code !== 200 && response.code !== 0) {
        // 始终在控制台输出错误信息（使用脱敏手机号）
        console.error('[Send Phone Code] 发送验证码失败:', {
          code: response.code,
          message: response.message,
          phone: maskedPhone,
          type: scene
        });
        throw new Error(response.message || '发送验证码失败');
      }

      // 验证码发送成功，但提醒用户可能需要等待
      if (import.meta.env.DEV) {
        console.log('[Send Phone Code] 验证码发送成功，请检查手机短信（可能需要等待几秒钟）');
      }

      return response;
    } catch (error: any) {
      // 始终在控制台输出错误信息，方便调试（使用脱敏手机号）
      console.error('[Send Phone Code] 发送验证码异常:', {
        error: error.message,
        stack: error.stack,
        name: error.name,
        phone: data?.phone && String(data.phone).length >= 11 ? `${String(data.phone).substring(0, 3)}****${String(data.phone).substring(7)}` : (data?.phone || 'N/A'),
        type: data.type
      });
      
      // 提供更友好的错误信息
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        throw new Error('无法连接到服务器，请检查后端服务是否运行');
      }
      // 如果错误消息包含后端返回的具体错误，直接抛出
      if (error.message && !error.message.includes('无法连接到服务器')) {
        throw error;
      }
      throw new Error(error.message || '发送验证码失败，请稍后重试');
    }
  },

  // 检查用户名是否已被注册（公开接口，不需要token）
  // 检查用户名是否已被注册（公开接口，不需要token）
  // 返回值：data: true 表示已注册，data: false 表示未注册
  checkUsername: async (username: string) => {
    try {
      const response = await request(`/api/user/check-username?username=${encodeURIComponent(username)}`, 'GET', null, false);
      return response;
    } catch (error: any) {
      console.error('检查用户名失败:', error);
      return {
        code: 500,
        message: error.message || '检查用户名失败',
        data: false
      };
    }
  },

  // 检查手机号是否已被注册（公开接口，不需要token）
  // 返回值：data: true 表示已注册，data: false 表示未注册
  // countryCode 参数可选，默认 "CN"
  checkPhone: async (phone: string, countryCode?: string) => {
    try {
      // 默认使用 "CN" 作为国家代码
      const finalCountryCode = countryCode || 'CN';
      const url = `/api/user/check-phone?phone=${encodeURIComponent(phone)}&countryCode=${encodeURIComponent(finalCountryCode)}`;
      const response = await request(url, 'GET', null, false);
      return response;
    } catch (error: any) {
      console.error('检查手机号失败:', error);
      return {
        code: 500,
        message: error.message || '检查手机号失败',
        data: false
      };
    }
  },

  // 获取高德地图前端配置（公开接口，无需登录）
  getAmapConfig: async () => {
    try {
      const response = await request('/api/user/config/amap', 'GET', undefined, false);
      return response;
    } catch (error: any) {
      console.error('获取高德配置失败:', error);
      return {
        code: 500,
        message: error.message || '获取高德配置失败',
        data: null
      };
    }
  },

  /**
   * 获取微信 JS-SDK 配置（用于微信内 H5 定位等，公开接口，无需登录）
   * @param url 当前页完整 URL，不含 # 及后面片段（与微信内打开地址一致）
   * 见：微信内H5-地理位置接口说明.md
   */
  getWechatJssdkConfig: async (url: string) => {
    try {
      const response = await request(
        `/api/user/config/wechat/jssdk?url=${encodeURIComponent(url)}`,
        'GET',
        undefined,
        false
      );
      return response;
    } catch (error: any) {
      console.error('获取微信 JS-SDK 配置失败:', error);
      return {
        code: 500,
        message: error.message || '获取微信 JS-SDK 配置失败',
        data: null
      };
    }
  },

  /**
   * 地点关键词搜索（走后端代理，无需 PlaceSearch 插件、无需地图实例）
   * 前端使用方式示例：
   *   const keywords = encodeURIComponent('北京西站');
   *   const res = await fetch(`/api/user/config/amap/place-search?keywords=${keywords}`).then(r => r.json());
   *   if (res.code === 200 && res.data && res.data.pois) {
   *     const pois = res.data.pois; // 用于展示或选点
   *   }
   * 或直接调用：userAPI.searchPlace(keywords)
   */
  searchPlace: async (keywords: string) => {
    try {
      const response = await request(
        `/api/user/config/amap/place-search?keywords=${encodeURIComponent(keywords)}`,
        'GET',
        undefined,
        false
      );
      return response;
    } catch (error: any) {
      console.error('地点搜索失败:', error);
      return {
        code: 500,
        message: error.message || '地点搜索失败',
        data: null
      };
    }
  },

  // ========== 交友资料 API ==========
  // 获取交友资料
  getDatingProfile: async () => {
    try {
      const response = await request('/api/user/dating-profile', 'GET');
      return response;
    } catch (error: any) {
      console.error('获取交友资料失败:', error);
      return {
        code: 500,
        message: error.message || '获取交友资料失败',
        data: null
      };
    }
  },

  // 保存或更新交友资料
  updateDatingProfile: async (data: {
    nickname?: string;
    gender?: string;
    age?: number;
    birthday?: string; // MM-DD格式
    location?: string;
    height?: number;
    education?: string;
    job?: string;
    salary?: string;
    bio?: string;
    photos?: string[]; // 照片URL数组，后端会序列化为JSON字符串
    videoUrl?: string;
    interests?: string[]; // 兴趣标签数组，后端会序列化为JSON字符串
    preferredGender?: string;
    preferredAgeMin?: number;
    preferredAgeMax?: number;
    communityId?: string;
    pointsRequired?: number;
  }) => {
    try {
      // 转换数据格式：将数组转换为JSON字符串
      const requestData: any = { ...data };
      if (data.photos && Array.isArray(data.photos)) {
        requestData.photos = JSON.stringify(data.photos);
      }
      if (data.interests && Array.isArray(data.interests)) {
        requestData.interests = JSON.stringify(data.interests);
      }
      
      // 尝试PUT，如果不存在则使用POST
      const response = await request('/api/user/dating-profile', 'PUT', requestData);
      return response;
    } catch (error: any) {
      // 如果PUT失败，尝试POST
      if (error.message?.includes('405') || error.message?.includes('Method Not Allowed')) {
        try {
          const requestData: any = { ...data };
          if (data.photos && Array.isArray(data.photos)) {
            requestData.photos = JSON.stringify(data.photos);
          }
          if (data.interests && Array.isArray(data.interests)) {
            requestData.interests = JSON.stringify(data.interests);
          }
          return await request('/api/user/dating-profile', 'POST', requestData);
        } catch (postError: any) {
          console.error('保存交友资料失败:', postError);
          return {
            code: 500,
            message: postError.message || '保存交友资料失败',
            data: null
          };
        }
      }
      console.error('保存交友资料失败:', error);
      return {
        code: 500,
        message: error.message || '保存交友资料失败',
        data: null
      };
    }
  },

  // 删除交友资料
  deleteDatingProfile: async () => {
    try {
      const response = await request('/api/user/dating-profile', 'DELETE');
      return response;
    } catch (error: any) {
      console.error('删除交友资料失败:', error);
      return {
        code: 500,
        message: error.message || '删除交友资料失败',
        data: null
      };
    }
  },

  // ========== 简历 API ==========
  // 获取简历/求职意向
  getResume: async () => {
    try {
      const response = await request('/api/user/resume', 'GET');
      return response;
    } catch (error: any) {
      console.error('获取简历失败:', error);
      return {
        code: 500,
        message: error.message || '获取简历失败',
        data: null
      };
    }
  },

  // 保存或更新简历/求职意向
  updateResume: async (data: {
    desiredPosition?: string;
    desiredIndustry?: string;
    desiredLocation?: string;
    desiredSalary?: string;
    jobType?: string;
    availability?: string;
    selfIntroduction?: string;
  }) => {
    try {
      const response = await request('/api/user/resume', 'PUT', data);
      return response;
    } catch (error: any) {
      // 如果PUT失败，尝试POST
      if (error.message?.includes('405') || error.message?.includes('Method Not Allowed')) {
        try {
          return await request('/api/user/resume', 'POST', data);
        } catch (postError: any) {
          console.error('保存简历失败:', postError);
          return {
            code: 500,
            message: postError.message || '保存简历失败',
            data: null
          };
        }
      }
      console.error('保存简历失败:', error);
      return {
        code: 500,
        message: error.message || '保存简历失败',
        data: null
      };
    }
  },

  // ========== 工作经历 API ==========
  // 获取工作经历列表
  getWorkExperiences: async () => {
    try {
      const response = await request('/api/user/resume/work-experiences', 'GET');
      return response;
    } catch (error: any) {
      console.error('获取工作经历失败:', error);
      return {
        code: 500,
        message: error.message || '获取工作经历失败',
        data: []
      };
    }
  },

  // 添加工作经历
  addWorkExperience: async (data: {
    company: string;
    position: string;
    startDate: string; // YYYY-MM格式
    endDate?: string | null; // YYYY-MM格式，null表示至今
    isCurrent?: boolean;
    description?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request('/api/user/resume/work-experiences', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('添加工作经历失败:', error);
      return {
        code: 500,
        message: error.message || '添加工作经历失败',
        data: null
      };
    }
  },

  // 更新工作经历
  updateWorkExperience: async (id: string, data: {
    company?: string;
    position?: string;
    startDate?: string;
    endDate?: string | null;
    isCurrent?: boolean;
    description?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request(`/api/user/resume/work-experiences/${id}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('更新工作经历失败:', error);
      return {
        code: 500,
        message: error.message || '更新工作经历失败',
        data: null
      };
    }
  },

  // 删除工作经历
  deleteWorkExperience: async (id: string) => {
    try {
      const response = await request(`/api/user/resume/work-experiences/${id}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('删除工作经历失败:', error);
      return {
        code: 500,
        message: error.message || '删除工作经历失败',
        data: null
      };
    }
  },

  // ========== 技能 API ==========
  // 获取技能列表
  getSkills: async () => {
    try {
      const response = await request('/api/user/resume/skills', 'GET');
      return response;
    } catch (error: any) {
      console.error('获取技能列表失败:', error);
      return {
        code: 500,
        message: error.message || '获取技能列表失败',
        data: []
      };
    }
  },

  // 添加技能
  addSkill: async (data: {
    skillName: string;
    level: string; // beginner/intermediate/advanced/expert
    sortOrder?: number;
  }) => {
    try {
      const response = await request('/api/user/resume/skills', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('添加技能失败:', error);
      return {
        code: 500,
        message: error.message || '添加技能失败',
        data: null
      };
    }
  },

  // 更新技能
  updateSkill: async (id: string, data: {
    skillName?: string;
    level?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request(`/api/user/resume/skills/${id}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('更新技能失败:', error);
      return {
        code: 500,
        message: error.message || '更新技能失败',
        data: null
      };
    }
  },

  // 删除技能
  deleteSkill: async (id: string) => {
    try {
      const response = await request(`/api/user/resume/skills/${id}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('删除技能失败:', error);
      return {
        code: 500,
        message: error.message || '删除技能失败',
        data: null
      };
    }
  },

  // ========== 证书 API ==========
  // 获取证书列表
  getCertificates: async () => {
    try {
      const response = await request('/api/user/resume/certificates', 'GET');
      return response;
    } catch (error: any) {
      console.error('获取证书列表失败:', error);
      return {
        code: 500,
        message: error.message || '获取证书列表失败',
        data: []
      };
    }
  },

  // 添加证书
  addCertificate: async (data: {
    certificateName: string;
    issuer?: string;
    issueDate?: string; // YYYY-MM格式
    expiryDate?: string | null; // YYYY-MM格式，null表示永久有效
    certificateUrl?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request('/api/user/resume/certificates', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('添加证书失败:', error);
      return {
        code: 500,
        message: error.message || '添加证书失败',
        data: null
      };
    }
  },

  // 更新证书
  updateCertificate: async (id: string, data: {
    certificateName?: string;
    issuer?: string;
    issueDate?: string;
    expiryDate?: string | null;
    certificateUrl?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request(`/api/user/resume/certificates/${id}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('更新证书失败:', error);
      return {
        code: 500,
        message: error.message || '更新证书失败',
        data: null
      };
    }
  },

  // 删除证书
  deleteCertificate: async (id: string) => {
    try {
      const response = await request(`/api/user/resume/certificates/${id}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('删除证书失败:', error);
      return {
        code: 500,
        message: error.message || '删除证书失败',
        data: null
      };
    }
  },

  // ========== 用户信誉分系统 API (v3.4新增) ==========

  // 查询用户信誉分
  getUserCredit: async (userId?: string) => {
    try {
      const params = userId ? { userId } : {};
      return await request('/api/user/credit', 'GET', params);
    } catch (error: any) {
      console.error('Get user credit error:', error);
      return {
        code: 500,
        message: error.message || '获取信誉分失败',
        data: null
      };
    }
  },

  // 查询信誉分历史
  getCreditHistory: async (userId?: string, page?: number, limit?: number) => {
    try {
      const params: any = {};
      if (userId) params.userId = userId;
      if (page) params.page = page;
      if (limit) params.limit = limit;
      return await request('/api/user/credit/history', 'GET', params);
    } catch (error: any) {
      console.error('Get credit history error:', error);
      return {
        code: 500,
        message: error.message || '获取信誉分历史失败',
        data: { records: [], total: 0, size: 20, current: 1, pages: 0 }
      };
    }
  },

  // 管理员调整信誉分（对接 globewe-credit-score §15.4）
  adjustCredit: async (data: { userId: string; changeAmount: number; reason: string }) => {
    try {
      return await request('/api/credit-score/admin/adjust', 'POST', {
        userId: data.userId,
        amount: data.changeAmount,
        reason: data.reason
      });
    } catch (error: any) {
      console.error('Adjust credit error:', error);
      return {
        code: 500,
        message: error.message || '调整信誉分失败',
        data: null
      };
    }
  },

  // 信誉分排行榜（管理端分页列表 API 未实现，暂用 ranking）
  getCreditList: async (page?: number, limit?: number, level?: string) => {
    try {
      const pageSize = limit || 20;
      const response = await request('/api/credit-score/ranking', 'GET', {
        page: page || 1,
        limit: pageSize,
        type: level
      });
      if (response.code === 200 && Array.isArray(response.data)) {
        const records = response.data.map((item: any) => ({
          userId: item.user_id || item.userId,
          score: item.score,
          level: item.level,
          levelName: item.levelName,
          updateTime: item.last_update_time || item.lastUpdateTime,
          ranking: item.ranking
        }));
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            records,
            total: records.length,
            current: page || 1,
            size: pageSize,
            pages: 1
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get credit list error:', error);
      return {
        code: 500,
        message: error.message || '获取信誉分列表失败',
        data: { records: [], total: 0, size: 20, current: 1, pages: 0 }
      };
    }
  }
};

// 社区相关API
export const communityAPI = {
  // 获取社区列表（公开接口，不需要token）
  getCommunities: async (params?: { page?: number; limit?: number; keyword?: string }) => {
    try {
      const response = await request('/api/community/communities', 'GET', params, false);
      return response;
    } catch (error) {
      console.error('Get communities error:', error);
      return { code: 500, message: '获取社区列表失败', data: null };
    }
  },

  // 获取我的社区
  getMyCommunities: async (params?: { page?: number; limit?: number }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request('/api/community/my-communities', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get my communities error:', error);
      return {
        code: 500,
        message: error.message || '获取我的社区列表失败',
        data: null
      };
    }
  },

  // 根据分类获取用户已加入的社区
  getMyCommunityByCategory: async (category: string) => {
    try {
      const response = await request(`/api/community/my-communities/by-category?category=${encodeURIComponent(category)}`, 'GET');
      // 如果返回200且data为null，表示没有已加入的社区（这是正常情况）
      if (response.code === 200) {
        return response;
      }
      // 如果是404，表示没有已加入的社区（正常情况）
      if (response.code === 404) {
        return {
          code: 200,
          message: '操作成功',
          data: null
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get my community by category error:', error);
      // 如果是404错误，表示没有已加入的社区（正常情况）
      if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        return {
          code: 200,
          message: '操作成功',
          data: null
        };
      }
      return {
        code: 500,
        message: error.message || '获取社区失败',
        data: null
      };
    }
  },

  // 检查社区名称是否已存在（公开接口，不需要token）
  checkCommunityName: async (name: string) => {
    try {
      const response = await request(`/api/community/communities/check-name?name=${encodeURIComponent(name)}`, 'GET', null, false);
      return response;
    } catch (error: any) {
      console.error('检查社区名称失败:', error);
      return {
        code: 500,
        message: error.message || '检查社区名称失败',
        data: false
      };
    }
  },

  // 创建社区
  createCommunity: async (data: { 
    name: string; 
    description: string; 
    category: string;
    coverImage?: string;
    isOpen?: boolean;
    location?: string;
    latitude?: number;
    longitude?: number;
    organizationId?: string;
    communityType?: string;
    avatarUrl?: string;
    surname?: string;
    countries?: string;
  }) => {
    try {
      const body: Record<string, any> = {
        name: data.name,
        category: data.category,
        description: data.description,
        isOpen: data.isOpen !== undefined ? data.isOpen : true,
        avatarUrl: data.avatarUrl || data.coverImage
      };
      if (data.location !== undefined) body.location = data.location;
      if (data.latitude !== undefined) body.latitude = data.latitude;
      if (data.longitude !== undefined) body.longitude = data.longitude;
      if (data.organizationId !== undefined) body.organizationId = data.organizationId;
      if (data.communityType !== undefined) body.communityType = data.communityType;
      if (data.surname !== undefined) body.surname = data.surname;
      if (data.countries !== undefined) body.countries = data.countries;
      return await request('/api/community/communities', 'POST', body);
    } catch (error: any) {
      console.error('Create community error:', error);
      return {
        code: 500,
        message: error.message || '创建社区失败',
        data: null
      };
    }
  },
  
  // 获取社区详情
  getCommunityDetail: async (communityId: string) => {
    try {
      const response = await request(`/api/community/communities/${communityId}`, 'GET', undefined, false);
      // 如果返回404，表示社区不存在
      if (response.code === 404 || response.status === 404) {
        return {
          code: 404,
          message: '社区不存在',
          data: null,
          status: 404
        };
      }
      return response;
    } catch (error: any) {
      // 404错误表示社区不存在，这是正常情况（历史数据中的社区可能已被删除）
      if (error.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
        return {
          code: 404,
          message: '社区不存在',
          data: null,
          status: 404
        };
      }
      // 其他错误才输出日志
      console.error('Get community detail error:', error);
      return {
        code: 500,
        message: error.message || '获取社区详情失败',
        data: null
      };
    }
  },
  
  // 获取社区历史（可选接口，404错误静默处理）
  getCommunityHistory: async (communityId: string) => {
    try {
      return await request(`/api/community/communities/${communityId}/history`, 'GET', undefined, false);
    } catch (error: any) {
      // 404错误表示接口未实现，静默处理
      if (error.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
        return {
          code: 404,
          message: '历史数据接口未实现',
          data: []
        };
      }
      // 其他错误才输出日志
      console.error('Get community history error:', error);
      return {
        code: 500,
        message: error.message || '获取社区历史失败',
        data: []
      };
    }
  },
  
  // 加入社区
  joinCommunity: async (data: {
    communityId: string;
    joinReason?: string;
    inviteCode?: string;
    communityType?: string;
    surname?: string;
  }) => {
    try {
      return await request(`/api/community/communities/${data.communityId}/join`, 'POST', {
        joinReason: data.joinReason,
        inviteCode: data.inviteCode
      });
    } catch (error: any) {
      console.error('Join community error:', error);
      return {
        code: 500,
        message: error.message || '加入社区失败',
        data: null
      };
    }
  },
  
  // 离开社区
  leaveCommunity: async (communityId: string) => {
    try {
      return await request(`/api/community/communities/${communityId}/leave`, 'DELETE');
    } catch (error: any) {
      console.error('Leave community error:', error);
      return {
        code: 500,
        message: error.message || '离开社区失败',
        data: null
      };
    }
  },
  
  // 获取社区成员列表
  getCommunityMembers: async (communityId: string, params?: { page?: number; limit?: number }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request(`/api/community/communities/${communityId}/members`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Get community members error:', error);
      return {
        code: 500,
        message: error.message || '获取社区成员列表失败',
        data: null
      };
    }
  },
  
  // 上传竞选视频（使用文件服务接口）
  uploadCampaignVideo: async (formData: FormData) => {
    try {
      // 获取token - 使用与其他API函数一致的逻辑
      let token = null;
      try {
        const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
        if (authData) {
          const auth = JSON.parse(authData);
          token = auth.token || auth.authToken;
        }
      } catch (e) {
        // 如果解析失败，尝试直接获取authToken
      }
      
      // 如果还没有token，尝试从authToken获取
      if (!token) {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      }
      
      if (!token) {
        console.warn('[uploadCampaignVideo] 未找到token，请求可能失败');
        throw new Error('未授权，请先登录');
      }
      
      // 统一使用相对路径上传，与主 API 一致，避免手机端（含微信）跨域导致 Failed to fetch
      // 开发环境：Vite 代理转发；生产环境：Nginx 将 /api/ 代理到后端
      const uploadUrl = '/api/file/upload';
      
      // 添加category参数
      if (!formData.has('category')) {
        formData.append('category', 'campaign_video');
      }
      
      // 调试日志
      if (import.meta.env.DEV) {
        console.log('[uploadCampaignVideo] 准备上传视频:', {
          url: uploadUrl,
          hasToken: !!token,
          tokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
          formDataKeys: Array.from(formData.keys())
        });
      }
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // 不要设置Content-Type，让浏览器自动设置multipart/form-data和boundary
        },
        body: formData
      });
      
      // 检查响应状态
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('未授权，请先登录');
        }
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: `上传失败: ${response.statusText}` };
        }
        
        // Gateway 已配置，不再需要降级方案
        throw new Error(errorData.message || '视频上传失败');
      }
      
      const result = await response.json();
      
      if (result.code === 200 && result.data) {
        // 返回文件URL
        return result.data.fileUrl || result.data.url || (typeof result.data === 'string' ? result.data : '');
      } else {
        // 检查是否是401未授权错误
        if (result.code === 401 || result.message?.includes('未授权') || result.message?.includes('请先登录')) {
          throw new Error('未授权，请先登录');
        }
        
        // Gateway 已配置，不再需要降级方案
        throw new Error(result.message || '视频上传失败');
      }
    } catch (error: any) {
      // 便于排查：打印实际错误类型与信息
      console.error('[uploadCampaignVideo] 上传失败', {
        name: error?.name,
        message: error?.message,
        code: error?.code
      });
      
      // 如果是未授权错误，提供更明确的提示
      if (error.message?.includes('未授权') || error.message?.includes('401') || error.message?.includes('403')) {
        throw new Error('未授权，请先登录后再上传视频');
      }
      
      // 网络/跨域错误：在手机端（尤其微信内）更常见，给出可操作建议
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError' || error.message?.includes('NetworkError')) {
        throw new Error(
          '上传失败，请检查：1) 网络是否正常 2) 如在微信内可尝试用手机浏览器打开 3) 视频较大时建议使用 Wi-Fi。若问题持续请联系管理员。'
        );
      }
      
      // 如果是服务未找到错误
      if (error.message?.includes('Service not found') || error.message?.includes('Nacos')) {
        throw new Error('Gateway 服务未找到，请检查 Gateway 配置或联系管理员');
      }
      
      throw new Error(error.message || '视频上传失败，请稍后重试');
    }
  },
  
  // 生成视频缩略图（后端API）
  generateVideoThumbnails: async (videoUrl: string, count: number = 10) => {
    try {
      let token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        try {
          const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
          if (authData) {
            const auth = JSON.parse(authData);
            token = auth.token || auth.authToken;
          }
        } catch (e) {
          // 忽略解析错误
        }
      }
      
      if (!token) {
        throw new Error('未授权，请先登录');
      }
      
      const gatewayUrl = import.meta.env.VITE_GATEWAY_URL;
      const apiUrl = gatewayUrl 
        ? `${gatewayUrl}/api/file/video/thumbnails`
        : '/api/file/video/thumbnails';
      
      // 添加超时控制（30秒）
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000);
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            videoUrl: videoUrl,
            count: count
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[生成缩略图] API响应错误:', {
            status: response.status,
            statusText: response.statusText,
            errorText
          });
          throw new Error(`生成缩略图失败: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result.code === 200 && result.data) {
          return result.data.thumbnails || [];
        } else {
          throw new Error(result.message || '生成缩略图失败');
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('生成缩略图超时，请稍后重试或使用前端生成');
        }
        throw fetchError;
      }
    } catch (error: any) {
      console.error('生成视频缩略图失败:', error);
      throw error;
    }
  },
  
  // 直接访问文件服务的降级方案
  uploadCampaignVideoDirect: async (formData: FormData, token: string) => {
    const fileApiUrl = import.meta.env.VITE_FILE_API_URL || 'http://localhost:8090';
    const uploadUrl = `${fileApiUrl}/api/file/upload`;
    
    console.log('[uploadCampaignVideoDirect] 直接访问文件服务:', uploadUrl);
    
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('未授权，请先登录');
      }
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: `上传失败: ${response.statusText}` };
      }
      throw new Error(errorData.message || '视频上传失败');
    }
    
    const result = await response.json();
    
    if (result.code === 200 && result.data) {
      return result.data.fileUrl || result.data.url || (typeof result.data === 'string' ? result.data : '');
    } else {
      if (result.code === 401 || result.message?.includes('未授权') || result.message?.includes('请先登录')) {
        throw new Error('未授权，请先登录');
      }
      throw new Error(result.message || '视频上传失败');
    }
  },
  
  // 创建竞选活动
  createCampaign: async (data: {
    title: string;
    manifesto: string;
    videoUrl: string;
    category: string;
    communityId: string;
  }) => {
    try {
      // 模拟API调用延迟
      // 模拟返回数据
      return {
        id: `campaign_${Date.now()}`,
        title: data.title,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Create campaign error:', error);
      throw new Error('提交失败，请稍后重试');
    }
  },
  
  // 获取社区加入申请列表
  getJoinRequests: async (data: {
    communityId: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: data.page || 1,
        limit: data.limit || 10
      };
      if (data.status) {
        queryParams.status = data.status;
      }
      
      return await request(`/api/community/communities/${data.communityId}/join-requests`, 'GET', queryParams);
    } catch (error: any) {
      // 404错误表示接口未实现，返回空列表
      if (error?.status === 404 || error?.message?.includes('404')) {
        return {
          code: 404,
          message: '接口未实现',
          data: {
            total: 0,
            pages: 0,
            current: data.page || 1,
            size: data.limit || 10,
            records: []
          }
        };
      }
      console.error('Get join requests error:', error);
      return {
        code: 500,
        message: error.message || '获取申请列表失败',
        data: {
          total: 0,
          pages: 0,
          current: data.page || 1,
          size: data.limit || 10,
          records: []
        }
      };
    }
  },
  
  // 处理社区加入申请
  processJoinRequest: async (data: {
    requestId: string;
    action: 'approve' | 'reject';
    reason?: string;
  }) => {
    try {
      return await request(`/api/community/join-requests/${data.requestId}/${data.action}`, 'POST', {
        reason: data.reason
      });
    } catch (error: any) {
      console.error('Process join request error:', error);
      return {
        code: 500,
        message: error.message || '处理申请失败',
        data: null
      };
    }
  },
  
  // 管理社区成员
  manageCommunityMember: async (data: {
    communityId: string;
    userId: string;
    role?: 'admin' | 'moderator' | 'member';
    action: 'add' | 'remove' | 'update';
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/communities/members/manage', 'POST', data);
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
        relationId: `REL${Date.now()}`,
        userId: data.userId,
        role: data.role || 'member',
        status: 'active'
        }
      };
    } catch (error) {
      console.error('Manage community member error:', error);
      return { code: 200, message: '操作失败', data: null };
    }
  },
  
  // 检查社区竞选状态
  // 后端约定：GET /api/community/communities/{communityId}/election-status
  checkCommunityElectionStatus: async (_communityId: string) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request(`/api/community/communities/${_communityId}/election-status`, 'GET');
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
        hasLeader: Math.random() > 0.5,
        leaderId: Math.random() > 0.5 ? `LEADER${Math.floor(Math.random() * 10000)}` : null,
        leaderName: Math.random() > 0.5 ? '社区领袖' : null,
        electionStatus: ['pending', 'in_progress', 'completed', 'upcoming'][Math.floor(Math.random() * 4)],
        minLegalMembers: 50,
        currentMemberCount: Math.floor(Math.random() * 1000) + 100,
        termEndDate: Math.random() > 0.5 ? new Date(Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString() : null
        }
      };
    } catch (error) {
      console.error('Check community election status error:', error);
      return { code: 200, message: '获取社区竞选状态失败', data: null };
    }
  },
  
  // 社区会议/竞选投票
  villagerVote: async (_data: {
    groupId: string;
    userId: string;
    candidateId?: string;
    voteType: 'election' | 'meeting';
    option?: string;
  }) => {
    try {
      return await request('/api/community/villager-vote', 'POST', _data);
    } catch (error: any) {
      console.error('Villager vote error:', error);
      return { code: 500, message: error.message || '投票失败', data: null };
    }
  },
  
  // ========== 管理端专用API ==========
  
  // 获取竞选规则列表（管理端）
  getElectionRules: async (params?: { page?: number; limit?: number; communityType?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.communityType) queryParams.communityType = params.communityType;
      
      return await request('/api/community/election-rules', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get election rules error:', error);
      return {
        code: 500,
        message: error.message || '获取竞选规则失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },
  
  // 创建竞选规则（管理端）
  createElectionRule: async (data: {
    communityType: string;
    minMembers: number;
    requiredSupporters: number;
    requiredSupportersType?: 'fixed' | 'percentage';
    minVotesPercentage: number;
    campaignDuration: number;
    enableCrimeRecordCheck?: boolean;
    enableFraudHistoryCheck?: boolean;
  }) => {
    try {
      return await request('/api/community/election-rules', 'POST', data);
    } catch (error: any) {
      console.error('Create election rule error:', error);
      return { code: 500, message: error.message || '创建竞选规则失败', data: null };
    }
  },
  
  // 更新竞选规则（管理端）
  updateElectionRule: async (ruleId: string, data: any) => {
    try {
      return await request(`/api/community/election-rules/${ruleId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update election rule error:', error);
      return { code: 500, message: error.message || '更新竞选规则失败', data: null };
    }
  },
  
  // 删除竞选规则（管理端）
  deleteElectionRule: async (ruleId: string) => {
    try {
      return await request(`/api/community/election-rules/${ruleId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete election rule error:', error);
      return { code: 500, message: error.message || '删除竞选规则失败', data: null };
    }
  },
  
  // 获取会议规则（管理端）
  getMeetingRules: async (params?: { page?: number; limit?: number; communityId?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.communityId) queryParams.communityId = params.communityId;
      
      return await request('/api/community/meeting-rules', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get meeting rules error:', error);
      return {
        code: 500,
        message: error.message || '获取会议规则失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },
  
  // 创建会议规则（管理端）
  createMeetingRule: async (data: {
    communityId: string;
    minParticipants: number;
    meetingFrequency: 'weekly' | 'monthly' | 'quarterly' | 'halfyearly' | 'yearly';
    meetingNoticeDays: number;
    meetingDurationLimit: number;
    allowOnlineMeeting?: boolean;
  }) => {
    try {
      return await request('/api/community/meeting-rules', 'POST', data);
    } catch (error: any) {
      console.error('Create meeting rule error:', error);
      return { code: 500, message: error.message || '创建会议规则失败', data: null };
    }
  },
  
  // 更新会议规则（管理端）
  updateMeetingRule: async (ruleId: string, data: any) => {
    try {
      return await request(`/api/community/meeting-rules/${ruleId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update meeting rule error:', error);
      return { code: 500, message: error.message || '更新会议规则失败', data: null };
    }
  },
  
  // 删除会议规则（管理端）
  deleteMeetingRule: async (ruleId: string) => {
    try {
      return await request(`/api/community/meeting-rules/${ruleId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete meeting rule error:', error);
      return { code: 500, message: error.message || '删除会议规则失败', data: null };
    }
  },
  
  // 获取广告位列表（管理端）
  getAdSpaces: async (params?: { page?: number; limit?: number; communityId?: string; status?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.communityId) queryParams.communityId = params.communityId;
      if (params?.status) queryParams.status = params.status;
      
      return await request('/api/community/ad-spaces', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get ad spaces error:', error);
      return {
        code: 500,
        message: error.message || '获取广告位失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },
  
  // 创建广告位（管理端）
  createAdSpace: async (data: {
    communityId: string;
    position: string;
    price: number;
    status?: 'available' | 'rented' | 'reserved';
  }) => {
    try {
      return await request('/api/community/ad-spaces', 'POST', data);
    } catch (error: any) {
      console.error('Create ad space error:', error);
      return { code: 500, message: error.message || '创建广告位失败', data: null };
    }
  },
  
  // 更新广告位（管理端）
  updateAdSpace: async (adSpaceId: string, data: any) => {
    try {
      return await request(`/api/community/ad-spaces/${adSpaceId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update ad space error:', error);
      return { code: 500, message: error.message || '更新广告位失败', data: null };
    }
  },
  
  // 删除广告位（管理端）
  deleteAdSpace: async (adSpaceId: string) => {
    try {
      return await request(`/api/community/ad-spaces/${adSpaceId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete ad space error:', error);
      return { code: 500, message: error.message || '删除广告位失败', data: null };
    }
  },
  
  // 投票接口
  villagerVote: async (_data: {
    modelId: string;
    status: number;
    upType: string;
    toUserId?: string;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/group_info/villager_vote', 'POST', _data);
      
      // 返回模拟数据
      return { code: 200, message: '操作成功', data: '402889ea6a15f115016a15f2b0eb0001' };
    } catch (error) {
      console.error('Vote error:', error);
      return { code: 200, message: '投票失败', data: null };
    }
  },
  
  // 我要竞选
  // 后端约定：POST /home/group_info/user_candidate
  // noticeType: 'resolution'（默认）或 'announcement'，用于区分 noticeId 是决议ID还是公告ID
  userCandidate: async (_data: {
    groupId: string;
    userId: string;
    content: string;
    noticeId: string;
    introduce: string;
    noticeType?: 'resolution' | 'announcement'; // 可选，默认 'resolution'
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/group_info/user_candidate', 'POST', _data);
      
      // 返回模拟数据
      return { code: 200, message: '操作成功', data: '2c918aee6a2073af016a207495e30000' };
    } catch (error) {
      console.error('Candidate error:', error);
      return { code: 200, message: '竞选失败', data: null };
    }
  },
  
  // 村长商户审核接口
  userTradeOper: async (data: {
    userId: string;
    groupId: string;
    auditUserIds: string;
    status: number; // 2审核通过 3拒绝
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/trade/user_trade_oper', 'POST', data);
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
        groupId: data.groupId,
        status: data.status
      }
      };
    } catch (error) {
      console.error('User trade operation error:', error);
      return { code: 200, message: '操作失败', data: null };
    }
  },
  
  // 获取活跃会议
  getActiveMeeting: async (communityId: string) => {
    try {
      return await request(`/api/community/meetings/active/${communityId}`, 'GET');
    } catch (error: any) {
      console.error('Get active meeting error:', error);
      return {
        code: 500,
        message: error.message || '获取活跃会议失败',
        data: null
      };
    }
  },
  
  // 创建会议
  createMeeting: async (data: {
    communityId: string;
    title: string;
    description?: string;
    startTime?: string;
    endTime?: string;
  }) => {
    try {
      return await request('/api/community/meetings', 'POST', data);
    } catch (error: any) {
      console.error('Create meeting error:', error);
      return {
        code: 500,
        message: error.message || '创建会议失败',
        data: null
      };
    }
  },
  
  // 获取会议消息列表
  getMeetingMessages: async (communityId: string, params?: { page?: number; limit?: number }) => {
    try {
      const queryParams = {
        page: params?.page || 1,
        limit: params?.limit || 100
      };
      return await request(`/api/community/meetings/messages/${communityId}`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Get meeting messages error:', error);
      return {
        code: 500,
        message: error.message || '获取会议消息失败',
        data: []
      };
    }
  },
  
  // 发送会议消息
  sendMeetingMessage: async (data: {
    communityId: string;
    content: string;
    meetingId?: string;
  }) => {
    try {
      return await request('/api/community/meetings/messages', 'POST', data);
    } catch (error: any) {
      console.error('Send meeting message error:', error);
      return {
        code: 500,
        message: error.message || '发送消息失败',
        data: null
      };
    }
  },
  
  // 获取会议决议列表（根据可见性过滤：只有好友可见的议题）
  getResolutions: async (communityId: string, page: number = 1, limit: number = 100) => {
    try {
      return await request(`/api/community/meetings/resolutions/list/${communityId}`, 'GET', { page, limit });
    } catch (error: any) {
      console.error('Get resolutions error:', error);
      return {
        code: 500,
        message: error.message || '获取决议列表失败',
        data: { records: [], total: 0 }
      };
    }
  },
  
  // 支持议题（只有社区内好友可以支持）
  supportResolution: async (resolutionId: string) => {
    try {
      return await request(`/api/community/meetings/resolutions/${resolutionId}/support`, 'POST');
    } catch (error: any) {
      console.error('Support resolution error:', error);
      return {
        code: 500,
        message: error.message || '支持议题失败',
        data: null
      };
    }
  },
  
  // 开启会议（创始人直接推送议题到群聊）
  startMeeting: async (data: {
    meetingId?: string;
    communityId: string;
    type: string;
    title: string;
    description: string;
    endTime: string;
    remark?: string;
    beneficiaryIds?: string[];
    pointsAmount?: number;
    visibilityType?: 'public' | 'friends_only';
    supportThreshold?: number;
  }) => {
    try {
      return await request('/api/community/meetings/start', 'POST', data);
    } catch (error: any) {
      console.error('Start meeting error:', error);
      return {
        code: 500,
        message: error.message || '开启会议失败',
        data: null
      };
    }
  },
  
  // 获取决议详情
  getResolutionDetail: async (resolutionId: string) => {
    try {
      return await request(`/api/community/meetings/resolutions/detail/${resolutionId}`, 'GET');
    } catch (error: any) {
      console.error('Get resolution detail error:', error);
      return {
        code: 500,
        message: error.message || '获取决议详情失败',
        data: null
      };
    }
  },
  
  // 创建决议
  createResolution: async (data: {
    meetingId?: string;
    communityId: string;
    title: string;
    description: string;
    type: string;
    endTime?: string;
    remark?: string;
    beneficiaryIds?: string[];
    pointsAmount?: number;
    visibilityType?: 'public' | 'friends_only';
    supportThreshold?: number;
  }) => {
    try {
      return await request('/api/community/meetings/resolutions', 'POST', data);
    } catch (error: any) {
      console.error('Create resolution error:', error);
      return {
        code: 500,
        message: error.message || '创建决议失败',
        data: null
      };
    }
  },
  
  // 投票
  voteOnResolution: async (data: {
    resolutionId: string;
    meetingId: string;
    communityId: string;
    voteType: 'approve' | 'reject' | 'abstain';
    candidateId?: string;
  }) => {
    try {
      return await request('/api/community/meetings/resolutions/vote', 'POST', data);
    } catch (error: any) {
      console.error('Vote on resolution error:', error);
      return {
        code: 500,
        message: error.message || '投票失败',
        data: null
      };
    }
  },
  
  // 获取候选人列表
  getCandidates: async (communityId: string) => {
    try {
      return await request(`/api/community/meetings/candidates/${communityId}`, 'GET');
    } catch (error: any) {
      console.error('Get candidates error:', error);
      return {
        code: 500,
        message: error.message || '获取候选人列表失败',
        data: []
      };
    }
  },
  
  // 参选
  runForLeader: async (data: {
    resolutionId: string;
    title: string;
    manifesto: string;
    videoUrl?: string;
  }) => {
    try {
      return await request('/api/community/meetings/candidates', 'POST', data);
    } catch (error: any) {
      console.error('Run for leader error:', error);
      return {
        code: 500,
        message: error.message || '参选失败',
        data: null
      };
    }
  },

  // 获取领袖视频列表（社区会议窗口用；后端未实现时返回空列表）
  getLeaderVideos: async (leaderId: string) => {
    try {
      return await request(`/api/community/leaders/${leaderId}/videos`, 'GET', undefined, true);
    } catch (error: any) {
      if (error?.status === 404 || error?.message?.includes('404')) {
        return { code: 200, data: { records: [] } };
      }
      console.error('Get leader videos error:', error);
      return { code: 500, message: error.message || '获取领袖视频失败', data: { records: [] } };
    }
  },

  // ========== 社区积分池管理 API (v3.4新增) ==========
  
  // 获取社区积分池信息
  getCommunityPointsPool: async (communityId: string) => {
    try {
      return await request(`/api/community/${communityId}/points-pool`, 'GET');
    } catch (error: any) {
      console.error('Get community points pool error:', error);
      return {
        code: 500,
        message: error.message || '获取社区积分池失败',
        data: null
      };
    }
  },

  // 获取社区积分收支明细
  getCommunityPointsHistory: async (communityId: string, params?: { page?: number; limit?: number }) => {
    try {
      const queryParams = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request(`/api/community/${communityId}/points-history`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Get community points history error:', error);
      return {
        code: 500,
        message: error.message || '获取积分历史失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 月度自动积分分配（通常由系统定时任务调用）
  autoDistributePoints: async (communityId: string) => {
    try {
      return await request(`/api/community/${communityId}/points/auto-distribute`, 'POST');
    } catch (error: any) {
      console.error('Auto distribute points error:', error);
      return {
        code: 500,
        message: error.message || '积分分配失败',
        data: null
      };
    }
  }
};

// 分享/海报相关 API（专属海报、扫码注册后看分享用户所在社区详情）
export const shareAPI = {
  /**
   * 生成分享链接（含 referrerId、communityId，扫码注册后可跳转分享用户所在社区详情）
   * @param params.userId 分享用户 ID（必填）
   * @param params.communityId 可选，分享用户所在社区 ID，落地页可展示该社区详情
   * @param params.platform 可选，平台标识：wechat/qq/xiaohongshu/phone，用于统计
   */
  getShareLink: async (params: { userId: string; communityId?: string; platform?: string }) => {
    try {
      const response = await request('/api/share/generate-link', 'POST', {
        userId: params.userId,
        communityId: params.communityId,
        platform: params.platform,
      }, true);
      return response;
    } catch (error: any) {
      console.error('Get share link error:', error);
      return { code: 500, message: error.message || '获取分享链接失败', data: null };
    }
  },
  /**
   * 获取分享链接对应的二维码图片 URL（可选，若后端在 getShareLink 中已返回 qrCodeImageUrl 可不用）
   */
  getShareQrCode: async (shareUrl: string, size?: number) => {
    try {
      const query = new URLSearchParams();
      query.set('url', shareUrl);
      if (size) query.set('size', String(size));
      const response = await request(`/api/share/qr-code?${query.toString()}`, 'GET', undefined, false);
      return response;
    } catch (error: any) {
      console.error('Get share qr code error:', error);
      return { code: 500, message: error.message || '获取二维码失败', data: null };
    }
  },
  /**
   * 上报分享事件（可选，用于统计分享到各平台的效果）
   */
  trackShareEvent: async (data: { shareId?: string; eventType: string; platform?: string }) => {
    try {
      await request('/api/share/track-event', 'POST', data, true);
    } catch {
      // 统计失败静默
    }
  }
};

// 文章相关API
export const articleAPI = {
  // 获取文章详情
  getArticleDetail: async (articleId: string) => {
    try {
      console.log('[articleAPI] 调用getArticleDetail，articleId:', articleId);
      const response = await request(`/api/article/articles/${articleId}`, 'GET', undefined, false);
      console.log('[articleAPI] getArticleDetail响应:', { code: response.code, hasData: !!response.data });
      return response;
    } catch (error: any) {
      console.error('[articleAPI] getArticleDetail错误:', error);
      // 404错误表示文章不存在，这是正常情况
      if (error.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
        console.log('[articleAPI] 文章不存在（404）');
        return {
          code: 404,
          message: '文章不存在',
          data: null,
          status: 404
        };
      }
      console.error('[articleAPI] getArticleDetail其他错误:', error);
      return {
        code: 500,
        message: error.message || '获取文章详情失败',
        data: null
      };
    }
  },
  
  // 获取文章列表
  getArticleList: async (params?: {
    page?: number;
    limit?: number;
    communityId?: string;
    category?: string;
    keyword?: string;
    authorId?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.communityId) queryParams.communityId = params.communityId;
      if (params?.category) queryParams.category = params.category;
      if (params?.keyword) queryParams.keyword = params.keyword;
      if (params?.authorId) queryParams.authorId = params.authorId;
      
      const response = await request('/api/article/articles', 'GET', queryParams, false);
      return response;
    } catch (error: any) {
      console.error('Get article list error:', error);
      return {
        code: 500,
        message: error.message || '获取文章列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },
  
  // 创建文章
  createArticle: async (data: {
    title: string;
    content: string;
    category?: string;
    tags?: string[];
    communityId?: string;
    coverImage?: string;
  }) => {
    try {
      return await request('/api/article/articles', 'POST', data);
    } catch (error: any) {
      console.error('Create article error:', error);
      return {
        code: 500,
        message: error.message || '创建文章失败',
        data: null
      };
    }
  },
  
  // 更新文章
  updateArticle: async (articleId: string, data: {
    title?: string;
    content?: string;
    category?: string;
    tags?: string[];
    coverImage?: string;
  }) => {
    try {
      return await request(`/api/article/articles/${articleId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update article error:', error);
      return {
        code: 500,
        message: error.message || '更新文章失败',
        data: null
      };
    }
  },
  
  // 删除文章
  deleteArticle: async (articleId: string) => {
    try {
      return await request(`/api/article/articles/${articleId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete article error:', error);
      return {
        code: 500,
        message: error.message || '删除文章失败',
        data: null
      };
    }
  },
  
  // 获取文章评论列表
  getArticleComments: async (articleId: string, params?: {
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request(`/api/article/articles/${articleId}/comments`, 'GET', queryParams, false);
    } catch (error: any) {
      // 404错误表示接口未实现，静默处理
      if (error.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
        return {
          code: 200,
          message: '评论接口未实现',
          data: {
            records: [],
            total: 0
          }
        };
      }
      console.error('Get article comments error:', error);
      return {
        code: 500,
        message: error.message || '获取文章评论失败',
        data: {
          records: [],
          total: 0
        }
      };
    }
  },
  
  // 点赞文章
  likeArticle: async (articleId: string) => {
    try {
      return await request(`/api/article/articles/${articleId}/like`, 'POST');
    } catch (error: any) {
      console.error('Like article error:', error);
      return {
        code: 500,
        message: error.message || '点赞失败',
        data: null
      };
    }
  },
  
  // 取消点赞
  unlikeArticle: async (articleId: string) => {
    try {
      return await request(`/api/article/articles/${articleId}/unlike`, 'POST');
    } catch (error: any) {
      console.error('Unlike article error:', error);
      return {
        code: 500,
        message: error.message || '取消点赞失败',
        data: null
      };
    }
  }
};

// 积分相关API
export const pointsAPI = {
  // 获取用户积分账户
  getPointsAccount: async () => {
    try {
      return await request('/api/points/account', 'GET');
    } catch (error: any) {
      console.error('Get points account error:', error);
      return {
        code: 500,
        message: error.message || '获取积分账户失败',
        data: null
      };
    }
  },
  
  // 获取积分信息
  getPointsInfo: async (_userId?: string) => {
    try {
      const response = await request('/api/points/info', 'GET');
      return response;
    } catch (error: any) {
      console.error('Get points info error:', error);
      return {
        code: 500,
        message: error.message || '获取积分信息失败',
        data: null
      };
    }
  },
  
  // ==================== 积分规则管理 ====================
  
  // 创建积分规则
  createPointsRule: async (data: {
    name?: string; // 前端兼容字段
    ruleName?: string; // 后端期望字段
    pointsRate: number; // 消费多少元获得积分
    maxPointsPerOrder?: number; // 前端兼容字段
    maxPoints?: number; // 后端期望字段
    pointsExpiryDays: number;
    minPointsToUse: number;
    pointsDeductionRate: number; // 积分抵扣比例（固定为1）
  }) => {
    try {
      // 转换参数以匹配后端期望的字段名
      const requestData: any = {
        pointsRate: data.pointsRate,
        pointsExpiryDays: data.pointsExpiryDays,
        minPointsToUse: data.minPointsToUse,
        pointsDeductionRate: data.pointsDeductionRate,
      };

      // 前端name → 后端ruleName
      if (data.ruleName) {
        requestData.ruleName = data.ruleName;
      } else if (data.name) {
        requestData.ruleName = data.name;
      }

      // 前端maxPointsPerOrder → 后端maxPoints
      if (data.maxPoints !== undefined) {
        requestData.maxPoints = data.maxPoints;
      } else if (data.maxPointsPerOrder !== undefined) {
        requestData.maxPoints = data.maxPointsPerOrder;
      }

      const response = await request('/api/points/rules', 'POST', requestData);
      return response;
    } catch (error: any) {
      console.error('Create points rule error:', error);
      return {
        code: 500,
        message: error.message || '创建积分规则失败',
        data: null
      };
    }
  },
  
  // 获取积分规则列表
  getPointsRuleList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/points/rules', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get points rule list error:', error);
      // 返回标准格式的响应，确保前端可以正常处理
      return {
        code: 500,
        message: error.message || '获取积分规则列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },
  
  // 获取积分规则详情
  getPointsRuleById: async (ruleId: string) => {
    try {
      const response = await request(`/api/points/rules/${ruleId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get points rule by id error:', error);
      return {
        code: 500,
        message: error.message || '获取积分规则详情失败',
        data: null
      };
    }
  },
  
  // 更新积分规则
  updatePointsRule: async (ruleId: string, data: {
    name?: string; // 前端兼容字段
    ruleName?: string; // 后端期望字段
    pointsRate?: number;
    maxPointsPerOrder?: number; // 前端兼容字段
    maxPoints?: number; // 后端期望字段
    pointsExpiryDays?: number;
    minPointsToUse?: number;
    pointsDeductionRate?: number;
  }) => {
    try {
      // 转换参数以匹配后端期望的字段名
      const requestData: any = {};

      if (data.pointsRate !== undefined) requestData.pointsRate = data.pointsRate;
      if (data.pointsExpiryDays !== undefined) requestData.pointsExpiryDays = data.pointsExpiryDays;
      if (data.minPointsToUse !== undefined) requestData.minPointsToUse = data.minPointsToUse;
      if (data.pointsDeductionRate !== undefined) requestData.pointsDeductionRate = data.pointsDeductionRate;

      // 前端name → 后端ruleName
      if (data.ruleName) {
        requestData.ruleName = data.ruleName;
      } else if (data.name) {
        requestData.ruleName = data.name;
      }

      // 前端maxPointsPerOrder → 后端maxPoints
      if (data.maxPoints !== undefined) {
        requestData.maxPoints = data.maxPoints;
      } else if (data.maxPointsPerOrder !== undefined) {
        requestData.maxPoints = data.maxPointsPerOrder;
      }

      const response = await request(`/api/points/rules/${ruleId}`, 'PUT', requestData);
      return response;
    } catch (error: any) {
      console.error('Update points rule error:', error);
      return {
        code: 500,
        message: error.message || '更新积分规则失败',
        data: null
      };
    }
  },
  
  // ==================== 积分充值相关 ====================
  
  // 创建积分充值订单
  createRechargeOrder: async (data: {
    amount: number;
    paymentMethod: string; // alipay, wechat
  }) => {
    try {
      return await request('/api/points/recharge/orders', 'POST', data);
    } catch (error: any) {
      console.error('Create recharge order error:', error);
      return {
        code: 500,
        message: error.message || '创建充值订单失败',
        data: null
      };
    }
  },
  
  // 获取积分充值订单详情
  getRechargeOrder: async (orderId: string) => {
    try {
      return await request(`/api/points/recharge/orders/${orderId}`, 'GET');
    } catch (error: any) {
      console.error('Get recharge order error:', error);
      return {
        code: 500,
        message: error.message || '获取充值订单失败',
        data: null
      };
    }
  },
  
  // 创建积分充值支付（根据支付方式调用不同接口）
  createRechargePayment: async (data: {
    orderId: string;
    paymentMethod: string; // alipay, wechatpay, paypal
    returnUrl?: string;
    notifyUrl?: string;
    cancelUrl?: string;
    openid?: string; // 微信支付需要
  }) => {
    try {
      const { paymentMethod, ...restData } = data;
      
      if (paymentMethod === 'alipay') {
        return await request('/api/points/recharge/payment/alipay', 'POST', restData);
      } else if (paymentMethod === 'wechatpay') {
        return await request('/api/points/recharge/payment/wechatpay', 'POST', restData);
      } else if (paymentMethod === 'paypal') {
        return await request('/api/points/recharge/payment/paypal', 'POST', restData);
      } else {
        throw new Error('不支持的支付方式');
      }
    } catch (error: any) {
      console.error('Create recharge payment error:', error);
      return {
        code: 500,
        message: error.message || '创建支付失败',
        data: null
      };
    }
  },
  
  // 取消积分充值订单
  cancelRechargeOrder: async (orderId: string) => {
    try {
      return await request(`/api/points/recharge/orders/${orderId}/cancel`, 'POST');
    } catch (error: any) {
      console.error('Cancel recharge order error:', error);
      return {
        code: 500,
        message: error.message || '取消订单失败',
        data: null
      };
    }
  },
  
  // 获取积分记录
  getPointsRecords: async (params?: { page?: number; limit?: number; type?: 'income' | 'expense' }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      // 转换类型参数：income -> earn, expense -> deduct
      if (params?.type) {
        queryParams.type = params.type === 'income' ? 'earn' : 'deduct';
      }
      
      const response = await request('/api/points/transactions', 'GET', queryParams);
      
      // 转换响应数据格式以兼容前端
      if (response.data && response.data.records) {
        response.data.records = response.data.records.map((record: any) => ({
          ...record,
          type: record.transactionType === 'earn' ? 'income' : 'expense',
          amount: record.transactionType === 'earn' ? record.amount : -record.amount,
          date: record.date || record.dateTime || record.createdAt
        }));
      }
      
      return response;
    } catch (error: any) {
      console.error('Get points records error:', error);
      return {
        code: 500,
        message: error.message || '获取积分记录失败',
        data: null
      };
    }
  }
};

// 订单相关API
export const orderAPI = {
  // 获取订单列表
  getOrders: async (params?: { page?: number; limit?: number; status?: string; userId?: string }) => {
    try {
      const response = await request('/api/order/orders', 'GET', params);
      return response;
    } catch (error) {
      console.error('Get orders error:', error);
      return { code: 500, message: '获取订单列表失败', data: null };
    }
  },

  // 获取用户订单列表（兼容旧接口）
  getOrderAppusers: async (data: {
    userId: string;
    type?: number;
    payType?: number;
    pageIndex?: number;
    pageSize?: number;
  }) => {
    try {
      const params: any = {
        page: data.pageIndex || 1,
        limit: data.pageSize || 20,
        userId: data.userId
      };
      
      // 转换状态参数（如果需要）
      if (data.type !== undefined) {
        // type映射到status
        const statusMap: Record<number, string> = {
          0: 'pending',      // 待付款
          1: 'paid',         // 已付款
          2: 'shipped',      // 已发货
          3: 'completed',    // 已完成
          4: 'cancelled'     // 已取消
        };
        if (statusMap[data.type]) {
          params.status = statusMap[data.type];
        }
      }
      
      const response = await request('/api/order/orders', 'GET', params);
      
      // 转换响应格式以兼容前端
      if (response.code === 200 && response.data && response.data.records) {
        return {
          ...response,
          data: {
            orderList: response.data.records.map((order: any) => ({
              id: order.id || order.orderId,
              orderNo: order.orderNo || order.orderNumber || order.id,
              totalPrice: order.totalAmount || order.totalPrice || 0,
              status: order.status,
              payType: order.paymentMethod === 'wechat' || order.paymentMethod === '微信支付' ? 2 : 
                       order.paymentMethod === 'alipay' || order.paymentMethod === '支付宝' ? 1 : 0,
              paymentId: order.paymentId || '',
              createTime: order.createTime || order.createdAt,
              completeTime: order.completeTime || order.completedAt,
              goodsList: order.items || order.orderItems || [{
                goodsName: order.productName || '商品',
                productName: order.productName || '商品',
                normName: order.specification || '',
                specName: order.specification || '',
                price: order.totalAmount || order.totalPrice || 0,
                quantity: order.quantity || 1
              }]
            })),
            total: response.data.total || 0,
            pageIndex: response.data.current || 1,
            pageSize: response.data.size || 20
          }
        };
      }
      
      return response;
    } catch (error: any) {
      console.error('Get order list error:', error);
      return {
        code: 500,
        message: error.message || '获取订单列表失败',
        data: null
      };
    }
  },

  // 创建订单
  createOrder: async (data: { 
    productId: string; 
    quantity: number; 
    addressId: string;
    paymentMethod: string;
  }) => {
    try {
      const response = await request('/api/order/orders', 'POST', data);
      return response;
    } catch (error) {
      console.error('Create order error:', error);
      return { code: 500, message: '创建订单失败', data: null };
    }
  },

  // 获取订单详情
  getOrderDetail: async (orderId: string) => {
    try {
      const response = await request(`/api/order/orders/${orderId}`, 'GET');
      return response;
    } catch (error) {
      console.error('Get order detail error:', error);
      return { code: 500, message: '获取订单详情失败', data: null };
    }
  },

  // 取消订单
  cancelOrder: async (orderId: string) => {
    try {
      const response = await request(`/api/order/orders/${orderId}/cancel`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Cancel order error:', error);
      return { code: 500, message: error.message || '取消订单失败', data: null };
    }
  },

  // 确认订单
  confirmOrder: async (orderId: string) => {
    try {
      const response = await request(`/api/order/orders/${orderId}/confirm`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Confirm order error:', error);
      return { code: 500, message: error.message || '确认订单失败', data: null };
    }
  },

  // 微信支付（订单）
  wechatPay: async (orderId: string, amount: number, returnUrl?: string) => {
    try {
      const response = await request('/api/order/payments/wechat', 'POST', { orderId, amount, returnUrl });
      if (response.data) {
        return { ...response, data: { ...response.data, qrCodeUrl: response.data.qrCodeUrl || response.data.codeUrl || response.data.qr_code_url, orderId: response.data.orderId || orderId } };
      }
      return response;
    } catch (error: any) {
      console.error('Wechat pay error:', error);
      return { code: 500, message: error.message || '生成微信支付失败', data: null };
    }
  },

  // 支付宝支付（订单）
  alipay: async (orderId: string, amount: number, returnUrl?: string, notifyUrl?: string) => {
    try {
      const response = await request('/api/order/payments/alipay', 'POST', { orderId, amount, returnUrl, notifyUrl });
      if (response.data) {
        return { ...response, data: { ...response.data, formHtml: response.data.formHtml || response.data.form || response.data.paymentForm, orderId: response.data.orderId || orderId } };
      }
      return response;
    } catch (error: any) {
      console.error('Alipay error:', error);
      return { code: 500, message: error.message || '生成支付宝支付失败', data: null };
    }
  },

  // PayPal 支付（订单）
  paypal: async (orderId: string, amount: number, returnUrl: string, cancelUrl: string) => {
    try {
      const response = await request('/api/order/payments/paypal', 'POST', { orderId, amount, returnUrl, cancelUrl });
      if (response.data) {
        return { ...response, data: { ...response.data, approvalUrl: response.data.approvalUrl || response.data.redirectUrl || response.data.redirect_url, orderId: response.data.orderId || orderId } };
      }
      return response;
    } catch (error: any) {
      console.error('PayPal error:', error);
      return { code: 500, message: error.message || '生成 PayPal 支付失败', data: null };
    }
  },

  // 查询支付状态
  getPaymentStatus: async (paymentId: string) => {
    try {
      return await request(`/api/order/payments/${paymentId}`, 'GET');
    } catch (error: any) {
      console.error('Get payment status error:', error);
      return { code: 500, message: error.message || '查询支付状态失败', data: null };
    }
  },

  // 订单发货
  shipOrder: async (orderId: string, data?: { 
    logisticsCompany?: string;
    trackingNumber?: string;
  }) => {
    try {
      const response = await request(`/api/admin/orders/${orderId}/ship`, 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Ship order error:', error);
      return { code: 500, message: error.message || '订单发货失败', data: null };
    }
  },

  // 更新订单状态
  updateOrderStatus: async (orderId: string, status: string) => {
    try {
      const response = await request(`/api/admin/orders/${orderId}/status`, 'PATCH', { status });
      return response;
    } catch (error: any) {
      console.error('Update order status error:', error);
      return { code: 500, message: error.message || '更新订单状态失败', data: null };
    }
  },

  // 获取商户订单列表
  getMerchantOrders: async (params?: { 
    page?: number; 
    limit?: number; 
    status?: string;
    keyword?: string;
  }) => {
    try {
      const response = await request('/api/order/orders', 'GET', params);
      return response;
    } catch (error: any) {
      console.error('Get merchant orders error:', error);
      return { 
        code: 500, 
        message: error.message || '获取订单列表失败', 
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },

  // 处理退款
  processRefund: async (orderId: string, data: {
    refundAmount?: number;
    refundReason?: string;
  }) => {
    try {
      // 使用订单取消接口或退款接口
      const response = await request(`/api/order/orders/${orderId}/cancel`, 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Process refund error:', error);
      return { code: 500, message: error.message || '处理退款失败', data: null };
    }
  }
};

// 产品相关API
export const productAPI = {
  // 获取产品列表
  getProducts: async (params?: { 
    page?: number; 
    limit?: number; 
    keyword?: string;
    category?: string;
  }) => {
    try {
      const response = await request('/api/product/products', 'GET', params);
      return response;
    } catch (error) {
      console.error('Get products error:', error);
      return { code: 500, message: '获取产品列表失败', data: null };
    }
  },
  
  // 获取产品列表（别名，兼容getProductList调用）
  getProductList: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    category?: string;
    categoryId?: string;
    communityId?: string;
    status?: string;
  }) => {
    try {
      // 构建查询参数
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.keyword) queryParams.keyword = params.keyword;
      if (params?.category) queryParams.category = params.category;
      if (params?.categoryId) queryParams.categoryId = params.categoryId;
      if (params?.communityId) queryParams.communityId = params.communityId; // 添加communityId参数
      if (params?.status) queryParams.status = params.status;

      const response = await request('/api/product/products', 'GET', queryParams, false);
      return response;
    } catch (error: any) {
      console.error('Get product list error:', error);
      return {
        code: 500,
        message: error.message || '获取产品列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },

  // 获取产品详情
  getProductDetail: async (productId: string) => {
    try {
      const response = await request(`/api/product/products/${productId}`, 'GET');
      return response;
    } catch (error) {
      console.error('Get product detail error:', error);
      return { code: 500, message: '获取产品详情失败', data: null };
    }
  },

  /** 分块上传图片（占位：后端未实现时返回 501，uploadUtils 可据此提示） */
  uploadImageChunk: async (
    _productId: string,
    _chunkIndex: number,
    _totalChunks: number,
    _chunkData: string,
    _imageId?: string
  ) => ({ code: 501, message: '暂不支持分块上传图片', data: null }),

  /** 分块上传视频（占位：后端未实现时返回 501） */
  uploadVideoChunk: async (
    _productId: string,
    _chunkIndex: number,
    _totalChunks: number,
    _chunkData: string
  ) => ({ code: 501, message: '暂不支持分块上传视频', data: null }),

  // 获取产品分类列表
  getCategories: async (parentId?: string) => {
    try {
      const params = parentId ? { parentId } : undefined;
      return await request('/api/product/categories', 'GET', params, false);
    } catch (error: any) {
      console.error('Get categories error:', error);
      return {
        code: 500,
        message: error.message || '获取产品分类失败',
        data: null
      };
    }
  },

  // 获取商户商品列表
  getMerchantProducts: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    categoryId?: string;
    status?: string;
  }) => {
    try {
      const queryParams: any = {};
      if (params?.page !== undefined) queryParams.page = params.page;
      if (params?.limit !== undefined) queryParams.limit = params.limit;
      if (params?.keyword) queryParams.keyword = params.keyword;
      if (params?.categoryId) queryParams.categoryId = params.categoryId;
      if (params?.status) queryParams.status = params.status;
      
      return await request('/api/merchant/products', 'GET', queryParams, false);
    } catch (error: any) {
      console.error('Get merchant products error:', error);
      // 返回标准格式的响应，确保前端可以正常处理
      return {
        code: 500,
        message: error.message || '获取商品列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  }
};

/** 好友相关接口若返回“表不存在”等数据库错误，统一提示该文案（后端需创建 friends 表后恢复） */
const FRIEND_SERVICE_UNAVAILABLE_MSG = '好友功能暂未开放，请稍后再试';

function isFriendsTableMissingError(message: string | undefined): boolean {
  if (!message || typeof message !== 'string') return false;
  return message.includes("doesn't exist") && (message.includes('friends') || message.includes('friend'));
}

/** 后端用 code=500 表示“已发送过申请”，前端按成功处理以便按钮变灰 */
function isFriendRequestAlreadySentMessage(message: string | undefined): boolean {
  if (!message || typeof message !== 'string') return false;
  return message.includes('已发送好友申请') || message.includes('请等待对方处理');
}

// 好友管理API
export const friendAPI = {
  // 添加好友(申请)
  addFriend: async (data: {
    friendId: string;
    source: string;
  }) => {
    try {
      const response = await request('/api/friends/request', 'POST', data);
      if (response && response.code === 500 && isFriendsTableMissingError(response.message)) {
        return { ...response, message: FRIEND_SERVICE_UNAVAILABLE_MSG };
      }
      // 后端用 500 表示“已发送过申请”，按成功处理以便前端按钮变灰
      if (response && response.code === 500 && isFriendRequestAlreadySentMessage(response.message)) {
        return { code: 200, message: response.message || '好友请求已发送', data: response.data ?? null };
      }
      return response;
    } catch (error: any) {
      console.error('Add friend error:', error);
      const msg = error?.message || '';
      return {
        code: 500,
        message: isFriendsTableMissingError(msg) ? FRIEND_SERVICE_UNAVAILABLE_MSG : (msg || '申请失败'),
        data: null
      };
    }
  },
  
  // 备注好友
  updateFriend: async (data: {
    friendId: string;
    nickname: string;
  }) => {
    try {
      return await request('/api/friends/update', 'POST', data);
    } catch (error: any) {
      console.error('Update friend error:', error);
      return {
        code: 500,
        message: error.message || '备注失败',
        data: null
      };
    }
  },
  
  // 好友验证/删除/拉黑/恢复/举报
  operateFriend: async (data: {
    friendId: string;
    action: 'accept' | 'reject' | 'delete' | 'block' | 'unblock' | 'report';
    reason?: string;
    requestId?: string;
  }) => {
    try {
      const response = await request('/api/friends/operate', 'POST', data);
      if (response && response.code === 500 && isFriendsTableMissingError(response.message)) {
        return { ...response, message: FRIEND_SERVICE_UNAVAILABLE_MSG };
      }
      return response;
    } catch (error: any) {
      console.error('Operate friend error:', error);
      const msg = error?.message || '';
      return {
        code: 500,
        message: isFriendsTableMissingError(msg) ? FRIEND_SERVICE_UNAVAILABLE_MSG : (msg || '操作失败'),
        data: null
      };
    }
  },
  
  // 好友列表(按村组装)
  getFriendList: async (params?: {
    keyword?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      return await request('/api/friends/list', 'GET', params);
    } catch (error: any) {
      // 检查是否是数据库表不存在的错误
      const isTableNotExistError = error?.message?.includes("doesn't exist") || 
                                   error?.response?.data?.message?.includes("doesn't exist");
      
      if (isTableNotExistError) {
        // 数据库表不存在，返回空列表而不是错误
        console.log('[API] 好友功能暂未启用（数据库表不存在），返回空列表');
        return {
          code: 200,
          message: '好友功能暂未启用',
          data: { records: [] }
        };
      }
      
      console.error('Get friend list error:', error);
      return {
        code: 500,
        message: error.message || '获取好友列表失败',
        data: null
      };
    }
  },
  
  // 获取待处理的好友请求列表（别人发给我的）
  getPendingFriendRequests: async () => {
    try {
      return await request('/api/friends/requests/pending', 'GET');
    } catch (error: any) {
      // 检查是否是数据库表不存在的错误
      const isTableNotExistError = error?.message?.includes("doesn't exist") || 
                                   error?.response?.data?.message?.includes("doesn't exist");
      
      if (isTableNotExistError) {
        // 数据库表不存在，返回空列表而不是错误
        console.log('[API] 好友请求功能暂未启用（数据库表不存在），返回空列表');
        return {
          code: 200,
          message: '好友请求功能暂未启用',
          data: []
        };
      }
      
      // 404错误是预期的（后端可能还未实现），返回空列表
      if (error?.status === 404 || error?.message?.includes('404')) {
        return {
          code: 200,
          message: 'API未实现',
          data: []
        };
      }
      
      console.error('Get pending friend requests error:', error);
      return {
        code: 500,
        message: error.message || '获取待处理好友请求失败',
        data: null
      };
    }
  }
};

// 聊天相关API
export const chatAPI = {
  // 发送消息
  sendMessage: async (data: {
    receiverId?: string;
    groupId?: string;
    content: string;
    type?: 'text' | 'image' | 'voice' | 'video' | 'file';
    fileUrl?: string;
    fileName?: string;
  }) => {
    try {
      return await request('/api/chat/messages', 'POST', data);
    } catch (error: any) {
      console.error('Send message error:', error);
      return {
        code: 500,
        message: error.message || '发送消息失败',
        data: null
      };
    }
  },
  
  // 获取消息列表
  getMessages: async (params: {
    conversationId: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      return await request(`/api/chat/messages/${params.conversationId}`, 'GET', {
        page: params.page || 1,
        limit: params.limit || 50
      });
    } catch (error: any) {
      console.error('Get messages error:', error);
      return {
        code: 500,
        message: error.message || '获取消息列表失败',
        data: []
      };
    }
  },
  
  // 获取会话列表
  getConversations: async () => {
    try {
      return await request('/api/chat/conversations', 'GET');
    } catch (error: any) {
      console.error('Get conversations error:', error);
      return {
        code: 500,
        message: error.message || '获取会话列表失败',
        data: []
      };
    }
  },
  
  // 创建群聊
  createGroupChat: async (data: {
    name: string;
    avatar?: string;
    participants: string[];
  }) => {
    try {
      return await request('/api/chat/groups', 'POST', data);
    } catch (error: any) {
      console.error('Create group chat error:', error);
      return {
        code: 500,
        message: error.message || '创建群聊失败',
        data: null
      };
    }
  },
  
  // 获取群聊信息
  getGroupInfo: async (groupId: string) => {
    try {
      return await request(`/api/chat/groups/${groupId}`, 'GET');
    } catch (error: any) {
      console.error('Get group info error:', error);
      return {
        code: 500,
        message: error.message || '获取群聊信息失败',
        data: null
      };
    }
  },
  
  // 添加群成员
  addGroupMember: async (groupId: string, userId: string) => {
    try {
      return await request(`/api/chat/groups/${groupId}/members`, 'POST', { userId });
    } catch (error: any) {
      console.error('Add group member error:', error);
      return {
        code: 500,
        message: error.message || '添加群成员失败',
        data: null
      };
    }
  },
  
  // 移除群成员
  removeGroupMember: async (groupId: string, userId: string) => {
    try {
      return await request(`/api/chat/groups/${groupId}/members/${userId}`, 'DELETE');
    } catch (error: any) {
      console.error('Remove group member error:', error);
      return {
        code: 500,
        message: error.message || '移除群成员失败',
        data: null
      };
    }
  },

  // 创建通话记录（通话发起/接听时调用，失败不影响通话）
  createCallRecord: async (data: {
    callId: string;
    callType: string;
    receiverId: string;
    direction: 'outgoing' | 'incoming';
  }) => {
    try {
      return await request('/api/chat/call/record', 'POST', data);
    } catch (error: any) {
      console.warn('Create call record error:', error);
      return { code: 500, message: error.message || '创建通话记录失败', data: null };
    }
  },

  // 更新通话记录（通话结束时调用，失败不影响通话）
  updateCallRecord: async (callId: string, data: { status: string; duration?: number; endTime: string }) => {
    try {
      return await request(`/api/chat/call/record/${callId}`, 'PATCH', data);
    } catch (error: any) {
      console.warn('Update call record error:', error);
      return { code: 500, message: error.message || '更新通话记录失败', data: null };
    }
  }
};

// 订单管理模块API - 整合销售模块的订单管理功能

// 竞选相关API
export const campaignAPI = {
    // 视频上传接口
    uploadVideo: async (file: File, _videoType: number = 1, _platSource?: string) => {
      try {
        // 使用文件服务API上传视频（通过 Gateway）
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'campaign_video');
        
        // 获取 token
        let token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (!token) {
          try {
            const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
            if (authData) {
              const auth = JSON.parse(authData);
              token = auth.token || auth.authToken;
            }
          } catch (e) {
            // 忽略解析错误
          }
        }
        
        if (!token) {
          throw new Error('未授权，请先登录');
        }
        
        // 统一使用 Gateway 地址
        const gatewayUrl = import.meta.env.VITE_GATEWAY_URL;
        const uploadUrl = gatewayUrl 
          ? `${gatewayUrl}/api/file/upload`
          : '/api/file/upload';
        
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        const result = await response.json();
        
        if (result.code === 200 && result.data) {
          // 返回文件URL（FileRecord对象可能包含fileUrl字段）
          return result.data.fileUrl || result.data.url || (typeof result.data === 'string' ? result.data : '');
        } else {
          throw new Error(result.message || '视频上传失败');


        }
      } catch (error: any) {
        console.error('Upload video error:', error);
        throw new Error(error.message || '视频上传失败');


      }
    },
  
    // 创建竞选接口
    createCampaign: async (data: {
      title: string;
      manifesto: string;
      videoUrl: string;
      category: string;
      communityId: string;
    }) => {
      try {
        // 在实际项目中，这里应该调用真实的API
        // const response = await request('/home/campaign/create', 'POST', data);
        
        // 返回模拟数据
        return {
          id: `camp${Date.now()}`,
          title: data.title,
          status: 'pending'
        };
      } catch (error) {
        console.error('Create campaign error:', error);
        throw new Error('竞选创建失败');
      }
    },
  
  // 获取竞选列表
  getCampaignList: async (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/campaign/list', 'GET', params);
      
      // 返回模拟数据
      const page = params?.page || 1;
      const pageSize = params?.pageSize || 10;
      
      return {
        code: 200,
        message: '操作成功',
        data: {
          total: 15,
          pages: 2,
          current: page,
          size: pageSize,
          records: Array.from({ length: pageSize }, (_, i) => ({
            id: `camp${Date.now() + i}`,
            title: `社区发展计划${i + 1}`,
            username: `候选人${i + 1}`,
            avatar: `https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=user+avatar&sign=f1f81b57b203e2aa336aa3ec3f6e3f7f`,
            videoUrl: 'https://example.com/video.mp4',
            voteCount: Math.floor(Math.random() * 1000),
            status: params?.status || 'approved',
            createdAt: new Date(Date.now() - i * 86400000).toISOString(),
            campaignDate: new Date(Date.now() + (30 - i) * 86400000).toISOString()
          }))
        }
      };
    } catch (error) {
      console.error('Get campaign list error:', error);
      return { code: 200, message: '获取竞选列表失败', data: null };
    }
  },
  
  // 获取竞选详情
  getCampaignDetail: async (campaignId: string) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request(`/home/campaign/detail/${campaignId}`, 'GET');
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
          id: campaignId,
          title: '社区发展计划',
          description: '我的社区发展计划',
          manifesto: '我承诺将致力于社区发展，改善基础设施，提升居民生活质量...',
          videoUrl: 'https://example.com/video.mp4',
          username: '张三',
          avatar: 'https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=user+avatar&sign=f1f81b57b203e2aa336aa3ec3f6e3f7f',
          voteCount: 125,
          status: 'approved',
          createdAt: '2025-09-20T10:30:00',
          campaignDate: '2025-10-15',
          hasVoted: false,
          userVote: 0
        }
      };
    } catch (error) {
      console.error('Get campaign detail error:', error);
      return { code: 200, message: '获取竞选详情失败', data: null };
    }
  },
  
  // 竞选投票接口
  voteCampaign: async (data: {
    campaignId: string;
    direction: number;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/campaign/vote', 'POST', data);
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
          voteCount: 126,
          hasVoted: true,
          direction: data.direction
        }
      };
    } catch (error) {
      console.error('Vote campaign error:', error);
      return { code: 200, message: '投票失败', data: null };
    }
  },
  
  // 更新竞选状态
  updateCampaignStatus: async (data: {
    campaignId: string;
    status: string;
    remark?: string;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/campaign/updateStatus', 'POST', data);
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
          id: data.campaignId,
          status: data.status,
          updatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Update campaign status error:', error);
      return { code: 200, message: '更新竞选状态失败', data: null };
    }
  }
};

  // 商户相关API
export const merchantAPI = {
  // 商户登录
  // 修复：尝试使用专门的机构端登录接口 /api/merchant/auth/login，如果不存在则回退到 /api/user/auth/login
  // 支持密码登录和验证码登录
  login: async (data: { 
    username?: string; 
    password?: string;
    phone?: string;
    smsCode?: string;
    loginType?: string;
  }) => {
    try {
      // 首先尝试使用专门的机构端登录接口
      let response = await request('/api/merchant/auth/login', 'POST', data, false);
      
      // 如果返回404或500错误，尝试使用用户登录接口作为后备
      if (response && (response.code === 404 || response.code === 500 || response.code === -1)) {
        if (import.meta.env.DEV) {
          console.log('[Merchant Login] 机构端登录接口失败，尝试使用用户登录接口');
        }
        response = await request('/api/user/auth/login', 'POST', data, false);
      }
      
      return response;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Merchant login error:', error);
      }
      // 如果第一个接口失败，尝试使用用户登录接口
      try {
        if (import.meta.env.DEV) {
          console.log('[Merchant Login] 捕获异常，尝试使用用户登录接口');
        }
        const fallbackResponse = await request('/api/user/auth/login', 'POST', data, false);
        return fallbackResponse;
      } catch (fallbackError: any) {
        if (import.meta.env.DEV) {
          console.error('Merchant login fallback error:', fallbackError);
        }
        return { 
          code: 500, 
          message: fallbackError.message || '登录失败，请检查用户名和密码', 
          data: null 
        };
      }
    }
  },
  // 企业认证/编辑
  userEnterprise: async (data: {
    userId?: string; // 注册时传递userId，登录后从token获取
    enterCode: string;
    enterName: string;
    legalIdcardName?: string;
    legalIdcardCode?: string;
    businessLicense?: string;
    contactPhone?: string;
  }) => {
    try {
      // 调用真实的API
      return await request('/api/merchant/verify', 'POST', {
        userId: data.userId, // 传递userId（如果提供）
        enterCode: data.enterCode,
        enterName: data.enterName,
        legalIdcardName: data.legalIdcardName,
        legalIdcardCode: data.legalIdcardCode,
        businessLicense: data.businessLicense,
        contactPhone: data.contactPhone
      });
    } catch (error: any) {
      console.error('User enterprise verification error:', error);
      return {
        code: 500,
        message: error.message || '认证提交失败',
        data: null
      };
    }
  },

  // 获取用户企业认证信息
  getUserEnterprise: async (data?: {
    userId?: string;
  }) => {
    try {
      // 调用真实的API获取企业认证信息
      // 根据后端文档，使用 POST /api/merchant/enterprise/info
      const requestData = data?.userId ? { userId: data.userId } : {};
      return await request('/api/merchant/enterprise/info', 'POST', requestData);
    } catch (error: any) {
      console.error('Get user enterprise error:', error);
      return {
        code: 500,
        message: error.message || '获取企业认证信息失败',
        data: null
      };
    }
  },
  // 交易操作
  userTrade: async (data: {
    userId?: string; // 注册时传递userId，登录后从token获取
    groupId: string;
    status: string;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/merchant/userTrade', 'POST', data);
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: {
          groupId: data.groupId,
          status: data.status
        }
      };
    } catch (error) {
      console.error('User trade operation error:', error);
      return { code: 200, message: '操作失败', data: null };
    }
  },
  
  // 获取用户关联的机构列表
  getUserOrganizations: async () => {
    try {
      return await request('/api/merchant/organizations/user', 'GET');
    } catch (error: any) {
      console.error('Get user organizations error:', error);
      return {
        code: 500,
        message: error.message || '获取机构列表失败',
        data: null
      };
    }
  },

  // 获取商户自定义分类列表
  getSelfCategories: async (merchantId?: string) => {
    try {
      const params = merchantId ? { merchantId } : undefined;
      return await request('/api/merchant/categories/self', 'GET', params, false);
    } catch (error: any) {
      console.error('Get self categories error:', error);
      return {
        code: 500,
        message: error.message || '获取商户自定义分类失败',
        data: []
      };
    }
  },

  // 添加商户自定义分类
  addSelfCategory: async (data: {
    name: string;
    parentId?: string;
    sort?: number;
  }) => {
    try {
      return await request('/api/merchant/categories/self', 'POST', data);
    } catch (error: any) {
      console.error('Add self category error:', error);
      return {
        code: 500,
        message: error.message || '添加商户自定义分类失败',
        data: null
      };
    }
  },

  // 更新商户自定义分类
  updateSelfCategory: async (categoryId: string, data: {
    name?: string;
    parentId?: string;
    sort?: number;
  }) => {
    try {
      return await request(`/api/merchant/categories/self/${categoryId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update self category error:', error);
      return {
        code: 500,
        message: error.message || '更新商户自定义分类失败',
        data: null
      };
    }
  },

  // 删除商户自定义分类
  deleteSelfCategory: async (categoryId: string) => {
    try {
      return await request(`/api/merchant/categories/self/${categoryId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete self category error:', error);
      return {
        code: 500,
        message: error.message || '删除商户自定义分类失败',
        data: null
      };
    }
  },

  // 获取商户订单列表
  getMerchantOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.status) queryParams.status = params.status;
      if (params?.startDate) queryParams.startDate = params.startDate;
      if (params?.endDate) queryParams.endDate = params.endDate;
      
      return await request('/api/merchant/orders', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get merchant orders error:', error);
      return {
        code: 500,
        message: error.message || '获取订单列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  }
};

// 营销相关API - 整合为统一的营销服务
export const marketingAPI = {
  // 客户关怀相关API
  getCustomerCareRecords: async (params?: { page?: number; limit?: number; keyword?: string; type?: string; status?: string }) => {
    try {
      return {
        code: 200,
        message: '操作成功',
        data: {
          total: 100,
          pages: 10,
          current: params?.page || 1,
          size: params?.limit || 10,
          records: Array.from({ length: params?.limit || 10 }, (_, i) => ({
            id: `care${Date.now() + i}`,
            customerId: `CUST${Math.floor(Math.random() * 10000)}`,
            customerName: `客户${Math.floor(Math.random() * 1000)}`,
            careType: ['生日关怀', '节假日关怀', '客户流失预警', '新客户关怀', '会员专属关怀'][Math.floor(Math.random() * 5)],
            content: '发送关怀信息和优惠券',
            status: ['已完成', '进行中', '计划中'][Math.floor(Math.random() * 3)],
            effect: ['非常好', '良好', '一般', '较差', '待评估'][Math.floor(Math.random() * 5)],
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
            nextDate: i % 3 === 0 ? new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0] : null,
            operator: '客服A'
          }))
        }
      };
    } catch (error) {
      console.error('Get customer care records error:', error);
      return { code: 200, message: '获取关怀记录失败', data: null };
    }
  },
  
  getCustomerCarePlans: async (params?: { page?: number; limit?: number; status?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/customer/care-plans', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get customer care plans error:', error);
      return {
        code: 500,
        message: error.message || '获取关怀计划失败',
        data: null
      };
    }
  },
  
  createCustomerCarePlan: async (data: any) => {
    try {
      const response = await request('/api/customer/care-plans', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create customer care plan error:', error);
      return {
        code: 500,
        message: error.message || '创建关怀计划失败',
        data: null
      };
    }
  },
  
  // 获取关怀计划详情
  getCarePlanDetail: async (planId: string) => {
    try {
      const response = await request(`/api/customer/care-plans/${planId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get care plan detail error:', error);
      return {
        code: 500,
        message: error.message || '获取关怀计划详情失败',
        data: null
      };
    }
  },
  
  // 更新关怀计划
  updateCarePlan: async (planId: string, data: any) => {
    try {
      const response = await request(`/api/customer/care-plans/${planId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update care plan error:', error);
      return {
        code: 500,
        message: error.message || '更新关怀计划失败',
        data: null
      };
    }
  },
  
  // 执行关怀计划
  executeCarePlan: async (planId: string) => {
    try {
      const response = await request(`/api/customer/care-plans/${planId}/execute`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Execute care plan error:', error);
      return {
        code: 500,
        message: error.message || '执行关怀计划失败',
        data: null
      };
    }
  },
  
  createCustomerCareRecord: async (data: any) => {
    try {
      // 注意：后端Service中有createCareRecord方法，但Controller中可能没有对应接?      // 这里先使用customer服务的interactions接口作为替代，或者等待后端添加care-records接口
      // 如果后端添加?api/customer/care-records接口，可以替换为?      // return await request('/api/customer/care-records', 'POST', data);
      
      // 暂时使用interactions接口作为替代（创建互动记录）
    const interactionData = {
        customerId: data.customerId,
        interactionType: 'care',
        content: data.content || '客户关怀'
      };
      const response = await request('/api/customer/interactions', 'POST', interactionData);
      return response;
    } catch (error: any) {
      console.error('Create customer care record error:', error);
      return {
        code: 500,
        message: error.message || '创建关怀记录失败',
        data: null
      };
    }
  },
  
  getCustomerCareStats: async () => {
    try {
      // 注意：后端暂无统计接口，这里暂时返回Mock数据
      // 如果后端添加?api/customer/care-stats接口，可以替换为?      // return await request('/api/customer/care-stats', 'GET');
      
      // 暂时使用Mock数据
      return {
        code: 200,
        message: 'success',
        data: {
        thisMonthCareCount: Math.floor(Math.random() * 200) + 50,
        birthdayCareCount: Math.floor(Math.random() * 50) + 10,
        holidayCareCount: Math.floor(Math.random() * 30) + 5,
        inactiveWarningCount: Math.floor(Math.random() * 20) + 5,
        pendingCareCount: Math.floor(Math.random() * 10) + 2,
        conversionRate: Math.floor(Math.random() * 30) + 10
        }
      };
    } catch (error) {
      console.error('Get customer care stats error:', error);
      return {
        code: 500,
        message: '获取关怀统计失败',
        data: null
      };
    }
  },
  // 获取商品营销信息（使用营销服务API）
  getProductMarketingInfo: async (productId: string, _userId: string) => {
    try {
      const response = await request(`/api/marketing/product/info?productId=${encodeURIComponent(productId)}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get product marketing info error:', error);
      return {
        code: 500,
        message: error.message || '获取营销信息失败',
        data: null
      };
    }
  },
  
  // ==================== 优惠券管?====================
  
  // 创建优惠券（商户端）
  createCoupon: async (data: {
    name: string;
    type: string;
    value: number;
    minOrderAmount?: number;
    startTime: string;
    endTime: string;
    limitPerUser?: number;
    totalQuantity: number;
    applicableProducts?: string[];
    applicableCategories?: string[];
  }) => {
    try {
      const response = await request('/api/marketing/coupons/create', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create coupon error:', error);
      return {
        code: 500,
        message: error.message || '创建优惠券失败',
        data: null
      };
    }
  },
  
  // 获取优惠券列表（商户端）
  getCouponList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/marketing/coupons', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get coupon list error:', error);
      // 返回标准格式的响应，确保前端可以正常处理
      return {
        code: 500,
        message: error.message || '获取优惠券列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },
  
  // 获取可用优惠券列表（用户端）
  getAvailableCoupons: async (params?: {
    productId?: string;
    merchantId?: string;
  }) => {
    try {
      const queryParams: any = {};
      
      if (params?.productId) {
        queryParams.productId = params.productId;
      }
      if (params?.merchantId) {
        queryParams.merchantId = params.merchantId;
      }
      
      const response = await request('/api/marketing/coupons/available', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get available coupons error:', error);
      return {
        code: 500,
        message: error.message || '获取可用优惠券失败',
        data: null
      };
    }
  },
  
  // 领取优惠券（用户端）
  claimCoupon: async (couponId: string) => {
    try {
      const response = await request('/api/marketing/coupons/claim', 'POST', { couponId });
      return response;
    } catch (error: any) {
      console.error('Claim coupon error:', error);
      return {
        code: 500,
        message: error.message || '领取优惠券失败',
        data: null
      };
    }
  },
  
  // 使用优惠券（用户端）
  useCoupon: async (data: {
    userCouponId: string;
    orderId: string;
  }) => {
    try {
      const response = await request('/api/marketing/coupons/use', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Use coupon error:', error);
      return {
        code: 500,
        message: error.message || '使用优惠券失败',
        data: null
      };
    }
  },
  
  // 获取我的优惠券列表（用户端）
  getMyCoupons: async (status?: string) => {
    try {
      const queryParams: any = {};
      if (status) {
        queryParams.status = status;
      }
      
      const response = await request('/api/marketing/coupons/my', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get my coupons error:', error);
      return {
        code: 500,
        message: error.message || '获取我的优惠券失败',
        data: null
      };
    }
  },
  
  // ==================== 营销活动管理 ====================
  
  // 创建营销活动（商户端）
  createActivity: async (data: {
    name: string;
    type: string;
    description?: string;
    startTime: string;
    endTime: string;
    products?: Array<{
      productId: string;
      activityPrice: number;
      minQuantity?: number;
      maxQuantity?: number;
      maxQuantityPerPerson?: number;
    }>;
  }) => {
    try {
      const response = await request('/api/marketing/activities/create', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create activity error:', error);
      return {
        code: 500,
        message: error.message || '创建营销活动失败',
        data: null
      };
    }
  },
  
  // 获取营销活动列表（商户端）
  getActivityList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/marketing/activities', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get activity list error:', error);
      return {
        code: 500,
        message: error.message || '获取营销活动列表失败',
        data: null
      };
    }
  },
  
  // ==================== 满减活动管理 ====================
  
  // 创建满减活动
  createFullDiscount: async (data: {
    name: string;
    rules: string; // JSON格式的满减规?    startTime: string;
    endTime: string;
    scopeType?: string;
    scopeData?: string;
  }) => {
    try {
      const response = await request('/api/marketing/full-discounts', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create full discount error:', error);
      return {
        code: 500,
        message: error.message || '创建满减活动失败',
        data: null
      };
    }
  },
  
  // 获取满减活动列表
  getFullDiscountList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/marketing/full-discounts', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get full discount list error:', error);
      // 返回标准格式的响应，确保前端可以正常处理
      return {
        code: 500,
        message: error.message || '获取满减活动列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },
  
  // 获取满减活动详情
  getFullDiscountById: async (discountId: string) => {
    try {
      const response = await request(`/api/marketing/full-discounts/${discountId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get full discount by id error:', error);
      return {
        code: 500,
        message: error.message || '获取满减活动详情失败',
        data: null
      };
    }
  },
  
  // 更新满减活动
  updateFullDiscount: async (discountId: string, data: {
    name?: string;
    rules?: string;
    startTime?: string;
    endTime?: string;
    scopeType?: string;
    scopeData?: string;
  }) => {
    try {
      const response = await request(`/api/marketing/full-discounts/${discountId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update full discount error:', error);
      return {
        code: 500,
        message: error.message || '更新满减活动失败',
        data: null
      };
    }
  },
  
  // 变更满减活动状?
  updateFullDiscountStatus: async (discountId: string, status: string) => {
    try {
      const response = await request(`/api/marketing/full-discounts/${discountId}/status`, 'POST', { status });
      return response;
    } catch (error: any) {
      console.error('Update full discount status error:', error);
      return {
        code: 500,
        message: error.message || '变更满减活动状态失败',
        data: null
      };
    }
  },
  
  // ==================== 限时抢购管理 ====================
  
  // 创建限时抢购活动
  createFlashSale: async (data: {
    name: string;
    productId: string;
    specId?: string;
    flashPrice: number;
    originalPrice: number;
    stock: number;
    startTime: string;
    endTime: string;
  }) => {
    try {
      const response = await request('/api/marketing/flash-sales', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create flash sale error:', error);
      return {
        code: 500,
        message: error.message || '创建限时抢购活动失败',
        data: null
      };
    }
  },
  
  // 获取限时抢购活动列表
  getFlashSaleList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/marketing/flash-sales', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get flash sale list error:', error);
      // 返回标准格式的响应，确保前端可以正常处理
      return {
        code: 500,
        message: error.message || '获取限时抢购活动列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },
  
  // 获取限时抢购活动详情
  getFlashSaleById: async (flashSaleId: string) => {
    try {
      const response = await request(`/api/marketing/flash-sales/${flashSaleId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get flash sale by id error:', error);
      return {
        code: 500,
        message: error.message || '获取限时抢购活动详情失败',
        data: null
      };
    }
  },
  
  // 更新限时抢购活动
  updateFlashSale: async (flashSaleId: string, data: {
    name?: string;
    productId?: string;
    specId?: string;
    flashPrice?: number;
    originalPrice?: number;
    stock?: number;
    startTime?: string;
    endTime?: string;
  }) => {
    try {
      const response = await request(`/api/marketing/flash-sales/${flashSaleId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update flash sale error:', error);
      return {
        code: 500,
        message: error.message || '更新限时抢购活动失败',
        data: null
      };
    }
  },
  
  // 变更限时抢购活动状?
  updateFlashSaleStatus: async (flashSaleId: string, status: string) => {
    try {
      const response = await request(`/api/marketing/flash-sales/${flashSaleId}/status`, 'POST', { status });
      return response;
    } catch (error: any) {
      console.error('Update flash sale status error:', error);
      return {
        code: 500,
        message: error.message || '变更限时抢购活动状态失败',
        data: null
      };
    }
  },
  
  // ==================== 会员折扣管理 ====================
  
  // 创建会员折扣
  createMemberDiscount: async (data: {
    memberLevel: string;
    discountRate: number;
    startTime: string;
    endTime: string;
    scopeType?: string;
    scopeData?: string;
  }) => {
    try {
      const response = await request('/api/marketing/member-discounts', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create member discount error:', error);
      return {
        code: 500,
        message: error.message || '创建会员折扣失败',
        data: null
      };
    }
  },
  
  // 获取会员折扣列表
  getMemberDiscountList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/marketing/member-discounts', 'GET', queryParams);
      return response;
    } catch (error: any) {
      // 静默处理，Marketing服务可能未启动（可选功能）
      // 不输出错误日志，避免控制台噪?
      if (error.message && !error.message.includes('Failed to fetch')) {
        console.warn('Get member discount list warning:', error.message);
      }
      return {
        code: 500,
        message: error.message || '获取会员折扣列表失败（Marketing服务可能未启动）',
        data: { records: [], total: 0, pages: 0, current: 1, size: 20 }
      };
    }
  },
  
  // 获取会员折扣详情
  getMemberDiscountById: async (discountId: string) => {
    try {
      const response = await request(`/api/marketing/member-discounts/${discountId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get member discount by id error:', error);
      return {
        code: 500,
        message: error.message || '获取会员折扣详情失败',
        data: null
      };
    }
  },
  
  // 更新会员折扣
  updateMemberDiscount: async (discountId: string, data: {
    memberLevel?: string;
    discountRate?: number;
    startTime?: string;
    endTime?: string;
    scopeType?: string;
    scopeData?: string;
  }) => {
    try {
      const response = await request(`/api/marketing/member-discounts/${discountId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update member discount error:', error);
      return {
        code: 500,
        message: error.message || '更新会员折扣失败',
        data: null
      };
    }
  },
  
  // 变更会员折扣状?
  updateMemberDiscountStatus: async (discountId: string, status: string) => {
    try {
      const response = await request(`/api/marketing/member-discounts/${discountId}/status`, 'POST', { status });
      return response;
    } catch (error: any) {
      console.error('Update member discount status error:', error);
      return {
        code: 500,
        message: error.message || '变更会员折扣状态失败',
        data: null
      };
    }
  },
  
  // ==================== 优惠券删除和状态更新 ====================
  
  // 删除优惠券
  deleteCoupon: async (couponId: string) => {
    try {
      const response = await request(`/api/marketing/coupons/${couponId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete coupon error:', error);
      return {
        code: 500,
        message: error.message || '删除优惠券失败',
        data: null
      };
    }
  },
  
  // 更新优惠券状态
  updateCouponStatus: async (couponId: string, status: string) => {
    try {
      const response = await request(`/api/marketing/coupons/${couponId}/status`, 'POST', { status });
      return response;
    } catch (error: any) {
      console.error('Update coupon status error:', error);
      return {
        code: 500,
        message: error.message || '更新优惠券状态失败',
        data: null
      };
    }
  },
  
  // 删除满减活动
  deleteFullDiscount: async (discountId: string) => {
    try {
      const response = await request(`/api/marketing/full-discounts/${discountId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete full discount error:', error);
      return {
        code: 500,
        message: error.message || '删除满减活动失败',
        data: null
      };
    }
  },
  
  // 删除限时抢购
  deleteFlashSale: async (flashSaleId: string) => {
    try {
      const response = await request(`/api/marketing/flash-sales/${flashSaleId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete flash sale error:', error);
      return {
        code: 500,
        message: error.message || '删除限时抢购失败',
        data: null
      };
    }
  },
  
  // 删除会员折扣
  deleteMemberDiscount: async (discountId: string) => {
    try {
      const response = await request(`/api/marketing/member-discounts/${discountId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete member discount error:', error);
      return {
        code: 500,
        message: error.message || '删除会员折扣失败',
        data: null
      };
    }
  }
};

// 客户管理API - 商户端客户管理功能
export const customerAPI = {
  // 获取客户列表
  getCustomerList: async (params?: {
    page?: number;
    limit?: number;
    categoryId?: string;
    level?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.categoryId) {
        queryParams.categoryId = params.categoryId;
      }
      if (params?.level) {
        queryParams.level = params.level;
      }
      
      const response = await request('/api/customer', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get customer list error:', error);
      return {
        code: 500,
        message: error.message || '获取客户列表失败',
        data: null
      };
    }
  },
  
  // 获取客户详情
  getCustomerDetail: async (userId: string) => {
    try {
      const response = await request(`/api/customer/${userId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get customer detail error:', error);
      return {
        code: 500,
        message: error.message || '获取客户详情失败',
        data: null
      };
    }
  },
  
  // 更新客户信息
  updateCustomer: async (userId: string, data: {
    categoryId?: string;
    level?: string;
    preferredContact?: string;
    memo?: string;
  }) => {
    try {
      const response = await request(`/api/customer/${userId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update customer error:', error);
      return {
        code: 500,
        message: error.message || '更新客户信息失败',
        data: null
      };
    }
  },
  
  // 为客户添加标?
  addTagToCustomer: async (userId: string, tagId: string) => {
    try {
      const response = await request(`/api/customer/${userId}/tags`, 'POST', { tagId });
      return response;
    } catch (error: any) {
      console.error('Add tag to customer error:', error);
      return {
        code: 500,
        message: error.message || '添加客户标签失败',
        data: null
      };
    }
  },
  
  // ==================== 客户分类管理 ====================
  
  // 获取客户分类列表
  getCustomerCategories: async () => {
    try {
      const response = await request('/api/customer/categories', 'GET');
      return response;
    } catch (error: any) {
      console.error('Get customer categories error:', error);
      return {
        code: 500,
        message: error.message || '获取客户分类列表失败',
        data: null
      };
    }
  },
  
  // 创建客户分类
  createCustomerCategory: async (data: {
    categoryName: string;
    description?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request('/api/customer/categories', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create customer category error:', error);
      return {
        code: 500,
        message: error.message || '创建客户分类失败',
        data: null
      };
    }
  },
  
  // 更新客户分类
  updateCustomerCategory: async (categoryId: string, data: {
    categoryName?: string;
    description?: string;
    sortOrder?: number;
  }) => {
    try {
      const response = await request(`/api/customer/categories/${categoryId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update customer category error:', error);
      return {
        code: 500,
        message: error.message || '更新客户分类失败',
        data: null
      };
    }
  },
  
  // 删除客户分类
  deleteCustomerCategory: async (categoryId: string) => {
    try {
      const response = await request(`/api/customer/categories/${categoryId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete customer category error:', error);
      return {
        code: 500,
        message: error.message || '删除客户分类失败',
        data: null
      };
    }
  },
  
  // ==================== 客户标签管理 ====================
  
  // 获取客户标签列表
  getCustomerTags: async () => {
    try {
      const response = await request('/api/customer/tags', 'GET');
      return response;
    } catch (error: any) {
      console.error('Get customer tags error:', error);
      return {
        code: 500,
        message: error.message || '获取客户标签列表失败',
        data: null
      };
    }
  },
  
  // 创建客户标签
  createCustomerTag: async (data: {
    tagName: string;
    description?: string;
    color?: string;
  }) => {
    try {
      const response = await request('/api/customer/tags', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create customer tag error:', error);
      return {
        code: 500,
        message: error.message || '创建客户标签失败',
        data: null
      };
    }
  },
  
  // 更新客户标签
  updateCustomerTag: async (tagId: string, data: {
    tagName?: string;
    description?: string;
    color?: string;
  }) => {
    try {
      const response = await request(`/api/customer/tags/${tagId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update customer tag error:', error);
      return {
        code: 500,
        message: error.message || '更新客户标签失败',
        data: null
      };
    }
  },
  
  // 删除客户标签
  deleteCustomerTag: async (tagId: string) => {
    try {
      const response = await request(`/api/customer/tags/${tagId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete customer tag error:', error);
      return {
        code: 500,
        message: error.message || '删除客户标签失败',
        data: null
      };
    }
  },
  
  // ==================== 客户互动记录 ====================
  
  // 创建客户互动记录
  createInteraction: async (data: {
    customerId: string;
    interactionType: string;
    content: string;
  }) => {
    try {
      const response = await request('/api/customer/interactions', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create interaction error:', error);
      return {
        code: 500,
        message: error.message || '创建互动记录失败',
        data: null
      };
    }
  },
  
  // 获取客户互动记录列表
  getInteractionList: async (params: {
    customerId: string;
    page?: number;
    limit?: number;
    interactionType?: string;
  }) => {
    try {
      const queryParams: any = {
        customerId: params.customerId,
        page: params.page || 1,
        limit: params.limit || 20
      };
      
      if (params.interactionType) {
        queryParams.interactionType = params.interactionType;
      }
      
      const response = await request('/api/customer/interactions', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get interaction list error:', error);
      return {
        code: 500,
        message: error.message || '获取互动记录列表失败',
        data: null
      };
    }
  },
  
  // 获取互动记录详情
  getInteractionDetail: async (interactionId: string) => {
    try {
      const response = await request(`/api/customer/interactions/${interactionId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get interaction detail error:', error);
      return {
        code: 500,
        message: error.message || '获取互动记录详情失败',
        data: null
      };
    }
  },
  
  // 更新互动记录
  updateInteraction: async (interactionId: string, content: string) => {
    try {
      const response = await request(`/api/customer/interactions/${interactionId}`, 'PUT', { content });
      return response;
    } catch (error: any) {
      console.error('Update interaction error:', error);
      return {
        code: 500,
        message: error.message || '更新互动记录失败',
        data: null
      };
    }
  }
};

// 物流管理API - 商户端物流管理功能
export const logisticsAPI = {
  // ==================== 物流公司管理 ====================
  
  // 获取物流公司列表
  getCompanyList: async () => {
    try {
      const response = await request('/api/logistics/companies', 'GET');
      return response;
    } catch (error: any) {
      console.error('Get logistics company list error:', error);
      return {
        code: 500,
        message: error.message || '获取物流公司列表失败',
        data: null
      };
    }
  },
  
  // 获取物流公司详情
  getCompanyById: async (companyId: string) => {
    try {
      const response = await request(`/api/logistics/companies/${companyId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get logistics company detail error:', error);
      return {
        code: 500,
        message: error.message || '获取物流公司详情失败',
        data: null
      };
    }
  },
  
  // 添加物流公司（管理员专用接口）
  addCompany: async (data: {
    companyName?: string; // 前端兼容字段
    name?: string; // 后端期望字段
    code?: string;
    contactPerson?: string;
    contactPhone?: string; // 前端兼容字段
    phone?: string; // 后端期望字段
    apiUrl?: string;
    apiKey?: string;
  }) => {
    try {
      // 转换参数以匹配后端期望的字段名
      const requestData: any = {
        code: data.code,
        contactPerson: data.contactPerson,
        apiUrl: data.apiUrl,
        apiKey: data.apiKey,
      };

      // 前端companyName → 后端name
      if (data.name) {
        requestData.name = data.name;
      } else if (data.companyName) {
        requestData.name = data.companyName;
      }

      // 前端contactPhone → 后端phone
      if (data.phone !== undefined) {
        requestData.phone = data.phone;
      } else if (data.contactPhone !== undefined) {
        requestData.phone = data.contactPhone;
      }

      const response = await request('/api/logistics/companies', 'POST', requestData);
      return response;
    } catch (error: any) {
      console.error('Add logistics company error:', error);
      return {
        code: 500,
        message: error.message || '添加物流公司失败',
        data: null
      };
    }
  },

  // 更新物流公司信息（管理员专用接口）
  updateCompany: async (companyId: string, data: {
    companyName?: string; // 前端兼容字段
    name?: string; // 后端期望字段
    contactPerson?: string;
    contactPhone?: string; // 前端兼容字段
    phone?: string; // 后端期望字段
    apiUrl?: string;
    apiKey?: string;
  }) => {
    try {
      // 转换参数以匹配后端期望的字段名
      const requestData: any = {};

      if (data.contactPerson !== undefined) requestData.contactPerson = data.contactPerson;
      if (data.apiUrl !== undefined) requestData.apiUrl = data.apiUrl;
      if (data.apiKey !== undefined) requestData.apiKey = data.apiKey;

      // 前端companyName → 后端name
      if (data.name !== undefined) {
        requestData.name = data.name;
      } else if (data.companyName !== undefined) {
        requestData.name = data.companyName;
      }

      // 前端contactPhone → 后端phone
      if (data.phone !== undefined) {
        requestData.phone = data.phone;
      } else if (data.contactPhone !== undefined) {
        requestData.phone = data.contactPhone;
      }

      const response = await request(`/api/logistics/companies/${companyId}`, 'PUT', requestData);
      return response;
    } catch (error: any) {
      console.error('Update logistics company error:', error);
      return {
        code: 500,
        message: error.message || '更新物流公司失败',
        data: null
      };
    }
  },
  
  // ==================== 物流订单管理 ====================
  
  // 创建物流订单
  createOrder: async (data: {
    orderId: string;
    companyId: string;
    logisticsCost?: number;
    senderName: string;
    senderPhone: string;
    senderAddress: string;
    recipientName: string;
    recipientPhone: string;
    recipientAddress: string;
    weight?: number;
    volume?: number;
  }) => {
    try {
      const response = await request('/api/logistics/orders', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create logistics order error:', error);
      return {
        code: 500,
        message: error.message || '创建物流订单失败',
        data: null
      };
    }
  },
  
  // 获取物流订单列表
  getOrderList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      
      const response = await request('/api/logistics/orders', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get logistics order list error:', error);
      return {
        code: 500,
        message: error.message || '获取物流订单列表失败',
        data: null
      };
    }
  },
  
  // 获取物流订单详情
  getOrderById: async (logisticsOrderId: string) => {
    try {
      const response = await request(`/api/logistics/orders/${logisticsOrderId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get logistics order detail error:', error);
      return {
        code: 500,
        message: error.message || '获取物流订单详情失败',
        data: null
      };
    }
  },
  
  // 更新物流订单信息
  updateOrder: async (logisticsOrderId: string, data: {
    trackingNumber?: string;
    status?: string;
  }) => {
    try {
      const response = await request(`/api/logistics/orders/${logisticsOrderId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update logistics order error:', error);
      return {
        code: 500,
        message: error.message || '更新物流订单失败',
        data: null
      };
    }
  },
  
  // 跟踪物流信息
  trackLogistics: async (trackingNumber: string) => {
    try {
      const response = await request(`/api/logistics/orders/${trackingNumber}/track`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Track logistics error:', error);
      return {
        code: 500,
        message: error.message || '查询物流跟踪信息失败',
        data: null
      };
    }
  },
  
  // ==================== 运费计算 ====================
  
  // 计算运费
  calculateFreight: async (data: {
    templateId: string;
    weight?: number;
    volume?: number;
    orderAmount?: number;
  }) => {
    try {
      const response = await request('/api/logistics/calculate-freight', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Calculate freight error:', error);
      return {
        code: 500,
        message: error.message || '计算运费失败',
        data: null
      };
    }
  },
  
  // ==================== 物流模板管理 ====================
  
  // 获取物流模板列表
  getTemplateList: async (params?: {
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      const response = await request('/api/logistics/templates', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get logistics template list error:', error);
      return {
        code: 500,
        message: error.message || '获取物流模板列表失败',
        data: null
      };
    }
  },
  
  // 获取物流模板详情
  getTemplateById: async (templateId: string) => {
    try {
      const response = await request(`/api/logistics/templates/${templateId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get logistics template detail error:', error);
      return {
        code: 500,
        message: error.message || '获取物流模板详情失败',
        data: null
      };
    }
  },
  
  // 创建物流模板
  createTemplate: async (data: {
    templateName: string;
    templateType: string;
    firstUnit?: number;
    firstFee?: number;
    additionalUnit?: number;
    additionalFee?: number;
    freeThreshold?: number;
    isDefault?: boolean;
  }) => {
    try {
      const response = await request('/api/logistics/templates', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create logistics template error:', error);
      return {
        code: 500,
        message: error.message || '创建物流模板失败',
        data: null
      };
    }
  },
  
  // 更新物流模板
  updateTemplate: async (templateId: string, data: {
    templateName?: string;
    firstUnit?: number;
    firstFee?: number;
    additionalUnit?: number;
    additionalFee?: number;
    freeThreshold?: number;
    isDefault?: boolean;
  }) => {
    try {
      const response = await request(`/api/logistics/templates/${templateId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update logistics template error:', error);
      return {
        code: 500,
        message: error.message || '更新物流模板失败',
        data: null
      };
    }
  },
  
  // 删除物流模板
  deleteTemplate: async (templateId: string) => {
    try {
      const response = await request(`/api/logistics/templates/${templateId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete logistics template error:', error);
      return {
        code: 500,
        message: error.message || '删除物流模板失败',
        data: null
      };
    }
  }
};

// 库存管理API - 机构端仓储部功能
export const inventoryAPI = {
  // ==================== 库存管理接口 ====================
  
  // 获取库存列表
  getInventoryList: async (params?: {
    warehouseId?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.warehouseId) {
        queryParams.warehouseId = params.warehouseId;
      }
      const response = await request('/api/inventory', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get inventory list error:', error);
      return {
        code: 500,
        message: error.message || '获取库存列表失败',
        data: null
      };
    }
  },
  
  // 获取商品库存详情
  getInventoryByProductId: async (productId: string, params?: {
    warehouseId?: string;
    specId?: string;
  }) => {
    try {
      const queryParams: any = {};
      if (params?.warehouseId) {
        queryParams.warehouseId = params.warehouseId;
      }
      if (params?.specId) {
        queryParams.specId = params.specId;
      }
      const response = await request(`/api/inventory/${productId}`, 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get inventory by product id error:', error);
      return {
        code: 500,
        message: error.message || '获取库存详情失败',
        data: null
      };
    }
  },
  
  // 更新商品库存
  updateInventory: async (inventoryId: string, data: {
    currentStock?: number;
    safetyStock?: number;
    lockStock?: number;
    availableStock?: number;
    unit?: string;
  }) => {
    try {
      const response = await request(`/api/inventory/${inventoryId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update inventory error:', error);
      return {
        code: 500,
        message: error.message || '更新库存失败',
        data: null
      };
    }
  },
  
  // 调整库存数量
  adjustInventory: async (data: {
    inventoryId: string;
    quantity: number;
    description?: string;
  }) => {
    try {
      const response = await request('/api/inventory/adjust', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Adjust inventory error:', error);
      return {
        code: 500,
        message: error.message || '调整库存失败',
        data: null
      };
    }
  },
  
  // 获取低库存商?
  getLowStockItems: async (warehouseId?: string) => {
    try {
      const queryParams: any = {};
      if (warehouseId) {
        queryParams.warehouseId = warehouseId;
      }
      const response = await request('/api/inventory/low-stock', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get low stock items error:', error);
      return {
        code: 500,
        message: error.message || '获取低库存商品失败',
        data: null
      };
    }
  },
  
  // 设置库存预警
  setWarningSetting: async (data: {
    productId: string;
    warehouseId?: string;
    warningThreshold: number;
    warningMethod?: string;
  }) => {
    try {
      const response = await request('/api/inventory/warning-settings', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Set warning setting error:', error);
      return {
        code: 500,
        message: error.message || '设置库存预警失败',
        data: null
      };
    }
  },
  
  // ==================== 库存盘点接口 ====================
  
  // 创建盘点?
  createCheck: async (data: {
    warehouseId: string;
    checkName: string;
  }) => {
    try {
      const response = await request('/api/inventory/checks', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create check error:', error);
      return {
        code: 500,
        message: error.message || '创建盘点单失败',
        data: null
      };
    }
  },
  
  // 获取盘点单列?
  getCheckList: async (params?: {
    warehouseId?: string;
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.warehouseId) {
        queryParams.warehouseId = params.warehouseId;
      }
      if (params?.status) {
        queryParams.status = params.status;
      }
      const response = await request('/api/inventory/checks', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get check list error:', error);
      return {
        code: 500,
        message: error.message || '获取盘点单列表失败',
        data: null
      };
    }
  },
  
  // 获取盘点单详?
  getCheckById: async (checkId: string) => {
    try {
      const response = await request(`/api/inventory/checks/${checkId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get check by id error:', error);
      return {
        code: 500,
        message: error.message || '获取盘点单详情失败',
        data: null
      };
    }
  },
  
  // 添加盘点明细
  addCheckDetail: async (checkId: string, data: {
    productId: string;
    specId?: string;
    actualQuantity: number;
    unitCost?: number;
    remark?: string;
  }) => {
    try {
      const response = await request(`/api/inventory/checks/${checkId}/details`, 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Add check detail error:', error);
      return {
        code: 500,
        message: error.message || '添加盘点明细失败',
        data: null
      };
    }
  },
  
  // 确认盘点结果
  confirmCheck: async (checkId: string) => {
    try {
      const response = await request(`/api/inventory/checks/${checkId}/confirm`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Confirm check error:', error);
      return {
        code: 500,
        message: error.message || '确认盘点结果失败',
        data: null
      };
    }
  },
  
  // ==================== 出入库管理接?====================
  
  // 创建出入库记?
  createTransaction: async (data: {
    productId: string;
    warehouseId?: string;
    specId?: string;
    transactionType: 'in' | 'out' | 'adjust'; // in: 入库, out: 出库, adjust: 调整
    quantity: number;
    sourceType?: string;
    sourceId?: string;
    description?: string;
  }) => {
    try {
      const response = await request('/api/inventory/transactions', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create transaction error:', error);
      return {
        code: 500,
        message: error.message || '创建出入库记录失败',
        data: null
      };
    }
  },
  
  // 获取出入库记录列?
  getTransactionList: async (params?: {
    productId?: string;
    warehouseId?: string;
    transactionType?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.productId) {
        queryParams.productId = params.productId;
      }
      if (params?.warehouseId) {
        queryParams.warehouseId = params.warehouseId;
      }
      if (params?.transactionType) {
        queryParams.transactionType = params.transactionType;
      }
      const response = await request('/api/inventory/transactions', 'GET', queryParams);
      return response;
    } catch (error: any) {
      console.error('Get transaction list error:', error);
      return {
        code: 500,
        message: error.message || '获取出入库记录列表失败',
        data: null
      };
    }
  },
  
  // 获取出入库记录详?
  getTransactionById: async (transactionId: string) => {
    try {
      const response = await request(`/api/inventory/transactions/${transactionId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get transaction by id error:', error);
      return {
        code: 500,
        message: error.message || '获取出入库记录详情失败',
        data: null
      };
    }
  },
  
  // ==================== 仓库管理接口 ====================
  
  // 获取仓库列表
  getWarehouseList: async () => {
    try {
      const response = await request('/api/inventory/warehouses', 'GET');
      return response;
    } catch (error: any) {
      console.error('Get warehouse list error:', error);
      return {
        code: 500,
        message: error.message || '获取仓库列表失败',
        data: null
      };
    }
  },
  
  // 创建仓库
  createWarehouse: async (data: {
    warehouseName: string;
    address: string;
    contactPerson?: string;
    contactPhone?: string;
  }) => {
    try {
      const response = await request('/api/inventory/warehouses', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create warehouse error:', error);
      return {
        code: 500,
        message: error.message || '创建仓库失败',
        data: null
      };
    }
  },
  
  // 获取仓库详情
  getWarehouseById: async (warehouseId: string) => {
    try {
      const response = await request(`/api/inventory/warehouses/${warehouseId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get warehouse by id error:', error);
      return {
        code: 500,
        message: error.message || '获取仓库详情失败',
        data: null
      };
    }
  },
  
  // 更新仓库信息
  updateWarehouse: async (warehouseId: string, data: {
    warehouseName?: string;
    address?: string;
    contactPerson?: string;
    contactPhone?: string;
  }) => {
    try {
      const response = await request(`/api/inventory/warehouses/${warehouseId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update warehouse error:', error);
      return {
        code: 500,
        message: error.message || '更新仓库信息失败',
        data: null
      };
    }
  },
  
  // 删除仓库
  deleteWarehouse: async (warehouseId: string) => {
    try {
      const response = await request(`/api/inventory/warehouses/${warehouseId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete warehouse error:', error);
      return {
        code: 500,
        message: error.message || '删除仓库失败',
        data: null
      };
    }
  },

  // ========== 调拨转移 ==========

  // 库存调拨转移
  transferInventory: async (data: {
    productId: string;
    specId?: string;
    fromWarehouseId: string;
    toWarehouseId: string;
    quantity: number;
    reason?: string;
    applicantId?: string;
  }) => {
    try {
      const response = await request('/api/inventory/transfer', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Transfer inventory error:', error);
      return {
        code: 500,
        message: error.message || '调拨转移失败',
        data: null
      };
    }
  }
};

// 管理端专用API
export const adminAPI = {
  // 获取用户列表
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    status?: string;
    realNameStatus?: string;
    sourceChannel?: string;
  }) => {
    try {
      const response = await request('/api/admin/users', 'GET', params);
      if (response.code === 200 && response.data) {
        // 后端返回的是Page对象，格式化为前端需要的格式
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get users error:', error);
      return {
        code: 500,
        message: error.message || '获取用户列表失败',
        data: null
      };
    }
  },

  // 获取用户详情
  getUserDetail: async (userId: string) => {
    try {
      const response = await request(`/api/admin/users/${userId}`, 'GET');
      return response;
    } catch (error: any) {
      // 404错误是预期的（adminAPI可能需要管理员权限，普通用户无法访问）
      if (error?.status === 404 || error?.message?.includes('404')) {
        return {
          code: 404,
          message: 'API未实现或权限不足',
          data: null
        };
      }
      console.error('Get user detail error:', error);
      return {
        code: 500,
        message: error.message || '获取用户详情失败',
        data: null
      };
    }
  },

  // 获取用户实名认证详情
  getUserRealNameDetail: async (userId: string) => {
    try {
      const response = await request(`/api/admin/real-name/user/${userId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get user real name detail error:', error);
      return {
        code: 500,
        message: error.message || '获取实名认证详情失败',
        data: null
      };
    }
  },

  // 更新用户状态
  updateUserStatus: async (userId: string, statusOrData: string | { status: string; reason?: string }) => {
    try {
      const body = typeof statusOrData === 'string' ? { status: statusOrData } : statusOrData;
      const response = await request(`/api/admin/users/${userId}/status`, 'PATCH', body);
      return response;
    } catch (error: any) {
      console.error('Update user status error:', error);
      return {
        code: 500,
        message: error.message || '更新用户状态失败',
        data: null
      };
    }
  },

  // 获取实名认证列表 §5
  getRealNameList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    countryCode?: string;
  }) => {
    try {
      const response = await request('/api/admin/real-name', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get real name list error:', error);
      return { code: 500, message: error.message || '获取实名认证列表失败', data: { records: [], total: 0 } };
    }
  },

  // 获取实名认证详情 §5
  getRealNameDetail: async (realNameId: string) => {
    try {
      return await request(`/api/admin/real-name/${realNameId}`, 'GET');
    } catch (error: any) {
      console.error('Get real name detail error:', error);
      return { code: 500, message: error.message || '获取实名认证详情失败', data: null };
    }
  },

  // 机构资质认证列表 §6
  getInstitutionCertifications: async (params?: { page?: number; limit?: number; status?: string }) => {
    try {
      const response = await request('/api/admin/institution/certifications', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get institution certifications error:', error);
      return { code: 500, message: error.message || '获取机构资质列表失败', data: { records: [], total: 0 } };
    }
  },

  approveInstitutionCertification: async (id: string, body?: { remark?: string }) => {
    try {
      return await request(`/api/admin/institution/certifications/${id}/approve`, 'POST', body ?? {});
    } catch (error: any) {
      console.error('Approve institution certification error:', error);
      return { code: 500, message: error.message || '审核通过失败', data: null };
    }
  },

  rejectInstitutionCertification: async (id: string, body?: { remark?: string }) => {
    try {
      return await request(`/api/admin/institution/certifications/${id}/reject`, 'POST', body ?? {});
    } catch (error: any) {
      console.error('Reject institution certification error:', error);
      return { code: 500, message: error.message || '审核拒绝失败', data: null };
    }
  },

  // 系统参数 §15.2
  getSystemParams: async () => {
    try {
      return await request('/api/admin/system/params', 'GET');
    } catch (error: any) {
      console.error('Get system params error:', error);
      return { code: 500, message: error.message || '获取系统参数失败', data: null };
    }
  },

  updateSystemParam: async (key: string, value: unknown) => {
    try {
      return await request(`/api/admin/system/params/${key}`, 'PATCH', { value });
    } catch (error: any) {
      console.error('Update system param error:', error);
      return { code: 500, message: error.message || '更新系统参数失败', data: null };
    }
  },

  // 订单发货 §10
  shipOrder: async (orderId: string, body?: { trackingNumber?: string; carrier?: string; remark?: string }) => {
    try {
      return await request(`/api/admin/orders/${orderId}/ship`, 'POST', body ?? {});
    } catch (error: any) {
      console.error('Ship order error:', error);
      return { code: 500, message: error.message || '发货失败', data: null };
    }
  },

  // 标记原住民
  markOriginalResident: async (userId: string, isOriginalResident: boolean) => {
    try {
      const response = await request(`/api/admin/users/${userId}/original-resident`, 'PATCH', { isOriginalResident });
      return response;
    } catch (error: any) {
      console.error('Mark original resident error:', error);
      return {
        code: 500,
        message: error.message || '标记原住民失败',
        data: null
      };
    }
  },
  
  // 实名认证审核
  verifyRealName: async (data: {
    realNameId: string;
    verifyStatus: string;
    verifyNotes?: string;
  }) => {
    try {
      const response = await request('/api/admin/real-name/verify', 'POST', {
        realNameId: data.realNameId,
        status: data.verifyStatus,
        notes: data.verifyNotes || ''
      });
      return response;
    } catch (error: any) {
      console.error('Verify real name error:', error);
      return {
        code: 500,
        message: error.message || '审核失败',
        data: null
      };
    }
  },
  
  // 获取订单列表
  getOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    paymentMethod?: string;
    startDate?: string;
    endDate?: string;
    userId?: string;
    merchantId?: string;
  }) => {
    try {
      const response = await request('/api/admin/orders', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get orders error:', error);
      return {
        code: 500,
        message: error.message || '获取订单列表失败',
        data: null
      };
    }
  },
  
  // 获取订单详情
  getOrderDetail: async (orderId: string) => {
    try {
      const response = await request(`/api/admin/orders/${orderId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get order detail error:', error);
      return {
        code: 500,
        message: error.message || '获取订单详情失败',
        data: null
      };
    }
  },
  
  // 更新订单状态
  updateOrderStatus: async (orderId: string, data: {
    status: string;
    remark?: string;
    refundPoints?: number;
  }) => {
    try {
      const response = await request(`/api/admin/orders/${orderId}/status`, 'PATCH', data);
      return response;
    } catch (error: any) {
      console.error('Update order status error:', error);
      return {
        code: 500,
        message: error.message || '更新订单状态失败',
        data: null
      };
    }
  },

  // ========== 订单退货/退款（管理端） ==========
  getOrderReturns: async (params?: { page?: number; limit?: number; orderId?: string; status?: string }) => {
    try {
      const response = await request('/api/admin/orders/returns', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total ?? 0,
            pages: pageData.pages ?? 0,
            current: pageData.current ?? params?.page ?? 1,
            size: pageData.size ?? params?.limit ?? 20,
            records: pageData.records ?? []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get order returns error:', error);
      return { code: 500, message: error.message || '获取退款列表失败', data: { records: [], total: 0 } };
    }
  },
  approveOrderReturn: async (returnId: string, body?: { reviewNotes?: string }) => {
    try {
      return await request(`/api/admin/orders/returns/${returnId}/approve`, 'POST', body ?? {});
    } catch (error: any) {
      console.error('Approve return error:', error);
      return { code: 500, message: error.message || '审批失败', data: null };
    }
  },
  rejectOrderReturn: async (returnId: string, body?: { reviewNotes?: string }) => {
    try {
      return await request(`/api/admin/orders/returns/${returnId}/reject`, 'POST', body ?? {});
    } catch (error: any) {
      console.error('Reject return error:', error);
      return { code: 500, message: error.message || '拒绝失败', data: null };
    }
  },
  processOrderRefund: async (returnId: string) => {
    try {
      return await request(`/api/admin/orders/returns/${returnId}/refund`, 'POST');
    } catch (error: any) {
      console.error('Process refund error:', error);
      return { code: 500, message: error.message || '执行退款失败', data: null };
    }
  },

  // ========== 机构订单交易维权（申诉/投诉） ==========
  getOrderAppeals: async (params?: {
    page?: number;
    limit?: number;
    merchantId?: string;
    status?: string;
    type?: string;
    orderId?: string;
    keyword?: string;
  }) => {
    try {
      const response = await request('/api/admin/orders/appeals', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total ?? 0,
            pages: pageData.pages ?? 0,
            current: pageData.current ?? params?.page ?? 1,
            size: pageData.size ?? params?.limit ?? 20,
            records: pageData.records ?? []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get order appeals error:', error);
      return { code: 500, message: error.message || '获取维权列表失败', data: { records: [], total: 0 } };
    }
  },

  getOrderAppealDetail: async (appealId: string) => {
    try {
      return await request(`/api/admin/orders/appeals/${appealId}`, 'GET');
    } catch (error: any) {
      console.error('Get order appeal detail error:', error);
      return { code: 500, message: error.message || '获取维权详情失败', data: null };
    }
  },

  processOrderAppeal: async (appealId: string, data: {
    status: string;
    reviewNotes?: string;
    refundAmount?: number;
    supportSide?: 'user' | 'merchant';
  }) => {
    try {
      return await request(`/api/admin/orders/appeals/${appealId}/process`, 'POST', data);
    } catch (error: any) {
      console.error('Process order appeal error:', error);
      return { code: 500, message: error.message || '处理维权失败', data: null };
    }
  },

  processOrderDispute: async (orderId: string, data: {
    disputeStatus: string;
    remark?: string;
    refundAmount?: number;
  }) => {
    try {
      return await request(`/api/admin/orders/${orderId}/dispute`, 'PATCH', data);
    } catch (error: any) {
      console.error('Process order dispute error:', error);
      return { code: 500, message: error.message || '处理订单维权失败', data: null };
    }
  },
  
  // 调整用户积分
  adjustUserPoints: async (userId: string, data: {
    amount: number;
    reason: string;
  }) => {
    try {
      const response = await request(`/api/admin/points/users/${userId}`, 'POST', {
        amount: data.amount,
        reason: data.reason
      });
      return response;
    } catch (error: any) {
      console.error('Adjust user points error:', error);
      return {
        code: 500,
        message: error.message || '操作失败',
        data: null
      };
    }
  },
  
  // 获取社区列表
  getCommunities: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    status?: string;
    category?: string;
  }) => {
    try {
      const response = await request('/api/admin/communities', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get communities error:', error);
      return {
        code: 500,
        message: error.message || '获取社区列表失败',
        data: null
      };
    }
  },
  
  // 获取社区详情
  getCommunityDetail: async (communityId: string) => {
    try {
      const response = await request(`/api/admin/communities/${communityId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get community detail error:', error);
      return {
        code: 500,
        message: error.message || '获取社区详情失败',
        data: null
      };
    }
  },
  
  // 更新社区状态
  updateCommunityStatus: async (communityId: string, data: {
    status: string;
    remark?: string;
  }) => {
    try {
      const response = await request(`/api/admin/communities/${communityId}/status`, 'PATCH', data);
      return response;
    } catch (error: any) {
      console.error('Update community status error:', error);
      return {
        code: 500,
        message: error.message || '更新社区状态失败',
        data: null
      };
    }
  },

  // 更新社区设置
  updateCommunitySettings: async (communityId: string, settings: Record<string, unknown>) => {
    try {
      return await request(`/api/admin/communities/${communityId}/settings`, 'PATCH', settings);
    } catch (error: any) {
      console.error('Update community settings error:', error);
      return {
        code: 500,
        message: error.message || '更新社区设置失败',
        data: null
      };
    }
  },
  
  // 获取社区列表 - 根据管理端接口文?
  getAdminCommunities: async (_data: {
    searchType: number;
    userId: string;
    pageIndex: number;
    pageSize: number;
    content?: string;
    groupType?: number;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/group_info/group_search', 'POST', _data);
      
      // 返回模拟数据
      return {
        code: 200,
        message: '操作成功',
        data: [
        {
          createUserId: "2c918aee69a3176e0169a326645a0001",
          groupName: "科技爱好者社区",
          groupDescription: "分享科技资讯和知识",
          createTime: "2019-03-27 23:28:56",
          groupId: "402881e469bfc0ff0169bfc4beb10000",
          groupTagCode: "科技",
          updateTime: "2019-03-27 23:28:56",
          id: "402881e469bfc0ff0169bfc4beb10000",
          memberCount: 150,
          status: "active"
        }
      ]
      };
    } catch (error) {
      console.error('Get admin communities error:', error);
      return { code: 200, message: '获取社区列表失败', data: null };
    }
  },
  
  // 系统注册用户搜索 - 根据管理端接口文?
  searchUsers: async (data: {
    token: string;
    searchType: number; // 1搜索本村村民 2搜索非本村村?3搜索没有村属的村?4根据用户ids查询
    groupId?: string;
    pageIndex: number;
    pageSize: number;
  }) => {
    try {
      // 在实际项目中，这里应该调用真实的API
      // const response = await request('/home/users_info/user_all_search', 'POST', data);
      
      // 返回模拟数据（page/pageSize/total 预留分页，待后端实现）
      const response = await request('/home/users_info/user_all_search', 'POST', data);
      return response;
    } catch (error) {
      console.error('Search users error:', error);
      return { code: 200, message: '搜索用户失败', data: null };
    }
  },
  
  // ==================== 商户管理API ====================
  
  // 获取商户列表
  getMerchants: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    status?: string;
    verifyStatus?: string;
  }) => {
    try {
      const response = await request('/api/admin/merchants', 'GET', params);
      if (response.code === 200 && response.data) {
        // 后端返回的是Page对象，格式化为前端需要的格式
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get merchants error:', error);
      return {
        code: 500,
        message: error.message || '获取商户列表失败',
        data: null
      };
    }
  },
  
  // 获取商户详情
  getMerchantDetail: async (merchantId: string) => {
    try {
      // 注意：后端可能没有单独的详情接口，可以先用列表接口获取单个商?      // 或者需要从商户列表中查?
      const response = await request(`/api/admin/merchants/${merchantId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get merchant detail error:', error);
      return {
        code: 500,
        message: error.message || '获取商户详情失败',
        data: null
      };
    }
  },
  
  // 审核商户（通过/拒绝?
  verifyMerchant: async (merchantId: string, data: {
    status: string; // approved/rejected
    remark?: string;
  }) => {
    try {
      const response = await request(`/api/admin/merchants/${merchantId}/verify`, 'POST', {
        status: data.status,
        remark: data.remark || ''
      });
      return response;
    } catch (error: any) {
      console.error('Verify merchant error:', error);
      return {
        code: 500,
        message: error.message || '审核商户失败',
        data: null
      };
    }
  },
  
  // 更新商户状态（暂停/恢复）
  updateMerchantStatus: async (merchantId: string, status: string) => {
    try {
      // 使用审核接口来更新状态，因为后端目前只有审核接口
      // status可以是 active/suspended
      const response = await request(`/api/admin/merchants/${merchantId}/verify`, 'POST', {
        status: status,
        remark: status === 'suspended' ? '商户已被暂停' : '商户已恢复'
      });
      return response;
    } catch (error: any) {
      console.error('Update merchant status error:', error);
      return {
        code: 500,
        message: error.message || '更新商户状态失败',
        data: null
      };
    }
  },

  // ========== 商品管理相关接口 ==========
  // 后端响应字段映射：将后端 snake_case 字段转为前端 camelCase
  _mapProductResponse: (record: any) => {
    if (!record) return record;
    return {
      ...record,
      id: record.product_id || record.id,
      name: record.product_name || record.name,
      merchantId: record.merchant_id || record.merchantId,
      categoryId: record.category_id || record.categoryId,
      categoryName: record.category_name || record.categoryName,
      merchantName: record.merchant_name || record.merchantName,
      merchantPhone: record.merchant_phone || record.merchantPhone,
      merchantAddress: record.merchant_address || record.merchantAddress,
      video: record.video_url || record.video,
      createTime: record.create_time || record.createTime,
      updateTime: record.update_time || record.updateTime,
      originalPrice: record.original_price || record.originalPrice,
      viewCount: record.view_count || record.viewCount,
      reviewStatus: record.review_status || record.reviewStatus,
      reviewRemark: record.review_remark || record.reviewRemark,
      reviewTime: record.review_time || record.reviewTime,
      productType: record.product_type || record.productType,
      isVirtual: record.is_virtual !== undefined ? record.is_virtual : record.isVirtual,
      deliveryType: record.delivery_type || record.deliveryType
    };
  },

  // 获取商品列表（管理端）- 对齐产品服务 /api/product/products
  getProducts: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    category?: string;
    status?: string;
    merchantId?: string;
    source?: 'admin' | 'merchant' | 'all';
  }) => {
    try {
      const queryParams: any = {};
      if (params?.page !== undefined) queryParams.page = params.page;
      if (params?.limit !== undefined) queryParams.limit = params.limit;
      if (params?.keyword) queryParams.keyword = params.keyword;
      if (params?.category) queryParams.category = params.category;
      if (params?.status) queryParams.status = params.status;
      if (params?.merchantId) queryParams.merchantId = params.merchantId;
      if (params?.source && params.source !== 'all') queryParams.source = params.source;

      // 管理端调产品服务获取全部商品
      const response = await request('/api/product/products', 'GET', queryParams);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: (pageData.records || []).map((r: any) => adminAPI._mapProductResponse(r))
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get admin products error:', error);
      return {
        code: 500,
        message: error.message || '获取商品列表失败',
        data: null
      };
    }
  },

  // 获取商品详情 - 对齐产品服务 /api/product/products/{productId}
  getProductDetail: async (productId: string) => {
    try {
      const response = await request(`/api/product/products/${productId}`, 'GET');
      if (response.code === 200 && response.data) {
        return {
          ...response,
          data: adminAPI._mapProductResponse(response.data)
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get product detail error:', error);
      return {
        code: 500,
        message: error.message || '获取商品详情失败',
        data: null
      };
    }
  },

  // 审核商品（仅用于机构端商品）- 管理端专用接口
  reviewProduct: async (productId: string, data: {
    status: 'approved' | 'rejected' | 'need_more_info';
    remark?: string;
  }) => {
    try {
      const response = await request(`/api/admin/products/${productId}/review`, 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Review product error:', error);
      return {
        code: 500,
        message: error.message || '审核商品失败',
        data: null
      };
    }
  },

  // 下架商品 - 对齐产品服务 /api/product/products/{productId}/unpublish
  offlineProduct: async (productId: string, reason?: string) => {
    try {
      const response = await request(`/api/product/products/${productId}/unpublish`, 'POST', {
        reason: reason || '商品违规，已下架'
      });
      return response;
    } catch (error: any) {
      console.error('Offline product error:', error);
      return {
        code: 500,
        message: error.message || '下架商品失败',
        data: null
      };
    }
  },

  // 上架商品 - 对齐产品服务 /api/product/products/{productId}/publish
  publishProduct: async (productId: string) => {
    try {
      const response = await request(`/api/product/products/${productId}/publish`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Publish product error:', error);
      return {
        code: 500,
        message: error.message || '上架商品失败',
        data: null
      };
    }
  },

  // 新增商品（管理端创建）- 对齐产品服务 POST /api/product/products
  createProduct: async (data: {
    name: string;
    description?: string;
    price: number;
    originalPrice?: number;
    categoryId?: string;
    images?: string[];
    video?: string;
    stock?: number;
    status?: string;
    spec?: string;
    skus?: Array<{ specComb: string; price: number; stock: number }>;
    promotionPrice?: number;
    promotionStartTime?: string;
    promotionEndTime?: string;
    tags?: string[];
    unit?: string;
    weight?: string;
    deliveryType?: string;
    [key: string]: any;
  }) => {
    try {
      // 映射前端字段到后端 snake_case 字段
      const requestData: any = {
        product_name: data.name,
        description: data.description,
        price: data.price,
        original_price: data.originalPrice,
        category_id: data.categoryId,
        video_url: data.video,
        stock: data.stock,
        status: data.status || 'active',
        source: 'admin', // 标记管理端创建
        product_type: data.productType || 'product'
      };

      // 可选的扩展字段
      if (data.spec) requestData.spec = data.spec;
      if (data.skus) requestData.skus = data.skus;
      if (data.promotionPrice) requestData.promotion_price = data.promotionPrice;
      if (data.promotionStartTime) requestData.promotion_start_time = data.promotionStartTime;
      if (data.promotionEndTime) requestData.promotion_end_time = data.promotionEndTime;
      if (data.tags && data.tags.length > 0) requestData.tags = data.tags;
      if (data.unit) requestData.unit = data.unit;
      if (data.weight) requestData.weight = data.weight;
      if (data.deliveryType) requestData.delivery_type = data.deliveryType;

      // 传递 images 数组
      if (data.images && data.images.length > 0) requestData.images = data.images;

      const response = await request('/api/product/products', 'POST', requestData);
      return response;
    } catch (error: any) {
      console.error('Create product error:', error);
      return {
        code: 500,
        message: error.message || '创建商品失败',
        data: null
      };
    }
  },

  // 更新商品 - 对齐产品服务 PUT /api/product/products/{productId}
  updateProduct: async (productId: string, data: {
    name?: string;
    description?: string;
    price?: number;
    originalPrice?: number;
    categoryId?: string;
    images?: string[];
    video?: string;
    stock?: number;
    status?: string;
    spec?: string;
    skus?: Array<{ specComb: string; price: number; stock: number }>;
    promotionPrice?: number;
    promotionStartTime?: string;
    promotionEndTime?: string;
    tags?: string[];
    unit?: string;
    weight?: string;
    deliveryType?: string;
    [key: string]: any;
  }) => {
    try {
      const requestData: any = {};
      
      if (data.name !== undefined) requestData.product_name = data.name;
      if (data.description !== undefined) requestData.description = data.description;
      if (data.price !== undefined) requestData.price = data.price;
      if (data.originalPrice !== undefined) requestData.original_price = data.originalPrice;
      if (data.categoryId !== undefined) requestData.category_id = data.categoryId;
      if (data.video !== undefined) requestData.video_url = data.video;
      if (data.stock !== undefined) requestData.stock = data.stock;
      if (data.status !== undefined) requestData.status = data.status;
      if (data.images !== undefined) requestData.images = data.images;
      if (data.spec !== undefined) requestData.spec = data.spec;
      if (data.skus !== undefined) requestData.skus = data.skus;
      if (data.promotionPrice !== undefined) requestData.promotion_price = data.promotionPrice;
      if (data.promotionStartTime !== undefined) requestData.promotion_start_time = data.promotionStartTime;
      if (data.promotionEndTime !== undefined) requestData.promotion_end_time = data.promotionEndTime;
      if (data.tags !== undefined) requestData.tags = data.tags;
      if (data.unit !== undefined) requestData.unit = data.unit;
      if (data.weight !== undefined) requestData.weight = data.weight;
      if (data.deliveryType !== undefined) requestData.delivery_type = data.deliveryType;

      const response = await request(`/api/product/products/${productId}`, 'PUT', requestData);
      return response;
    } catch (error: any) {
      console.error('Update product error:', error);
      return {
        code: 500,
        message: error.message || '更新商品失败',
        data: null
      };
    }
  },

  // 删除商品 - 管理端专用接口
  deleteProduct: async (productId: string) => {
    try {
      const response = await request(`/api/product/products/${productId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete product error:', error);
      return {
        code: 500,
        message: error.message || '删除商品失败',
        data: null
      };
    }
  },

  // 获取商品规格列表 - 对齐产品服务 /api/product/products/{productId}/specs
  getProductSpecs: async (productId: string) => {
    try {
      const response = await request(`/api/product/products/${productId}/specs`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get product specs error:', error);
      return {
        code: 500,
        message: error.message || '获取规格列表失败',
        data: null
      };
    }
  },

  // 创建商品规格 - 对齐产品服务 POST /api/product/specs
  createProductSpec: async (data: {
    productId: string;
    specName: string;
    specValue: string;
    price: number;
    originalPrice?: number;
    stock: number;
    costPrice?: number;
    sortOrder?: number;
  }) => {
    try {
      const requestData: any = {
        product_id: data.productId,
        spec_name: data.specName,
        spec_value: data.specValue,
        price: data.price,
        original_price: data.originalPrice,
        stock: data.stock,
        cost_price: data.costPrice || 0,
        sort_order: data.sortOrder || 0
      };
      const response = await request('/api/product/specs', 'POST', requestData);
      return response;
    } catch (error: any) {
      console.error('Create product spec error:', error);
      return {
        code: 500,
        message: error.message || '创建规格失败',
        data: null
      };
    }
  },

  // 更新商品规格 - 对齐产品服务 PUT /api/product/specs/{specId}
  updateProductSpec: async (specId: string, data: {
    specName?: string;
    specValue?: string;
    price?: number;
    originalPrice?: number;
    stock?: number;
    costPrice?: number;
    sortOrder?: number;
  }) => {
    try {
      const requestData: any = {};
      if (data.specName !== undefined) requestData.spec_name = data.specName;
      if (data.specValue !== undefined) requestData.spec_value = data.specValue;
      if (data.price !== undefined) requestData.price = data.price;
      if (data.originalPrice !== undefined) requestData.original_price = data.originalPrice;
      if (data.stock !== undefined) requestData.stock = data.stock;
      if (data.costPrice !== undefined) requestData.cost_price = data.costPrice;
      if (data.sortOrder !== undefined) requestData.sort_order = data.sortOrder;
      
      const response = await request(`/api/product/specs/${specId}`, 'PUT', requestData);
      return response;
    } catch (error: any) {
      console.error('Update product spec error:', error);
      return {
        code: 500,
        message: error.message || '更新规格失败',
        data: null
      };
    }
  },

  // 删除商品规格 - 对齐产品服务 DELETE /api/product/specs/{specId}
  deleteProductSpec: async (specId: string) => {
    try {
      const response = await request(`/api/product/specs/${specId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete product spec error:', error);
      return {
        code: 500,
        message: error.message || '删除规格失败',
        data: null
      };
    }
  },

  // ========== 套餐管理接口 ==========
  // 获取套餐列表
  getProductPlans: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    keyword?: string;
    targetRole?: string;
  }) => {
    try {
      const queryParams: any = {};
      if (params?.page !== undefined) queryParams.page = params.page;
      if (params?.limit !== undefined) queryParams.limit = params.limit;
      if (params?.status) queryParams.status = params.status;
      if (params?.keyword) queryParams.keyword = params.keyword;
      if (params?.targetRole) queryParams.targetRole = params.targetRole;

      const response = await request('/api/admin/products/plans', 'GET', queryParams);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get product plans error:', error);
      return {
        code: 500,
        message: error.message || '获取套餐列表失败',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    }
  },

  // 获取套餐详情
  getProductPlanDetail: async (planId: string) => {
    try {
      const response = await request(`/api/admin/products/plans/${planId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get product plan detail error:', error);
      return {
        code: 500,
        message: error.message || '获取套餐详情失败',
        data: null
      };
    }
  },

  // 创建套餐
  createProductPlan: async (data: {
    planCode: string;
    planName: string;
    description?: string;
    price: number;
    durationDays: number;
    targetRole: string;
    features?: string[];
    sortOrder?: number;
  }) => {
    try {
      const requestData: any = {
        planCode: data.planCode,
        planName: data.planName,
        description: data.description,
        price: data.price,
        durationDays: data.durationDays,
        targetRole: data.targetRole,
        sortOrder: data.sortOrder || 10
      };
      
      // features 如果是数组，转换为 JSON 字符串
      if (data.features) {
        requestData.features = Array.isArray(data.features) 
          ? JSON.stringify(data.features) 
          : data.features;
      }
      
      const response = await request('/api/admin/products/plans', 'POST', requestData);
      return response;
    } catch (error: any) {
      console.error('Create product plan error:', error);
      return {
        code: 500,
        message: error.message || '创建套餐失败',
        data: null
      };
    }
  },

  // 更新套餐
  updateProductPlan: async (planId: string, data: {
    planName?: string;
    description?: string;
    price?: number;
    durationDays?: number;
    features?: string[];
    sortOrder?: number;
  }) => {
    try {
      const requestData: any = { ...data };
      
      // features 如果是数组，转换为 JSON 字符串
      if (data.features) {
        requestData.features = Array.isArray(data.features) 
          ? JSON.stringify(data.features) 
          : data.features;
      }
      
      const response = await request(`/api/admin/products/plans/${planId}`, 'PATCH', requestData);
      return response;
    } catch (error: any) {
      console.error('Update product plan error:', error);
      return {
        code: 500,
        message: error.message || '更新套餐失败',
        data: null
      };
    }
  },

  // 删除套餐
  deleteProductPlan: async (planId: string) => {
    try {
      const response = await request(`/api/admin/products/plans/${planId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete product plan error:', error);
      return {
        code: 500,
        message: error.message || '删除套餐失败',
        data: null
      };
    }
  },

  // 启用套餐
  activateProductPlan: async (planId: string) => {
    try {
      const response = await request(`/api/admin/products/plans/${planId}/activate`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Activate product plan error:', error);
      return {
        code: 500,
        message: error.message || '启用套餐失败',
        data: null
      };
    }
  },

  // 停用套餐
  deactivateProductPlan: async (planId: string) => {
    try {
      const response = await request(`/api/admin/products/plans/${planId}/deactivate`, 'POST');
      return response;
    } catch (error: any) {
      console.error('Deactivate product plan error:', error);
      return {
        code: 500,
        message: error.message || '停用套餐失败',
        data: null
      };
    }
  },

  // ========== 统计数据接口 ==========
  // 获取用户统计
  getUserStats: async (range?: 'day' | 'week' | 'month' | 'year') => {
    try {
      const params = range ? { range } : undefined;
      const response = await request('/api/admin/stats/users', 'GET', params);
      return response;
    } catch (error: any) {
      console.error('Get user stats error:', error);
      return {
        code: 500,
        message: error.message || '获取用户统计失败',
        data: null
      };
    }
  },

  // 获取订单统计
  getOrderStats: async (range?: 'day' | 'week' | 'month' | 'year') => {
    try {
      const params = range ? { range } : undefined;
      const response = await request('/api/admin/stats/orders', 'GET', params);
      return response;
    } catch (error: any) {
      console.error('Get order stats error:', error);
      return {
        code: 500,
        message: error.message || '获取订单统计失败',
        data: null
      };
    }
  },

  // 获取积分统计
  getPointsStats: async (range?: 'day' | 'week' | 'month' | 'year') => {
    try {
      const params = range ? { range } : undefined;
      const response = await request('/api/admin/stats/points', 'GET', params);
      return response;
    } catch (error: any) {
      console.error('Get points stats error:', error);
      return {
        code: 500,
        message: error.message || '获取积分统计失败',
        data: null
      };
    }
  },

  // 获取用户积分记录
  getUserPointsTransactions: async (userId: string, params?: {
    page?: number;
    limit?: number;
    type?: 'income' | 'expense';
  }) => {
    try {
      const response = await request(`/api/admin/points/users/${userId}/transactions`, 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get user points transactions error:', error);
      return {
        code: 500,
        message: error.message || '获取积分记录失败',
        data: null
      };
    }
  },

  getPricingConfig: async () => {
    try {
      return await request('/api/admin/points/pricing-config', 'GET');
    } catch (error: any) {
      console.error('Get pricing config error:', error);
      return { code: 500, message: error.message || '获取定价配置失败', data: null };
    }
  },

  getPricingConfigSchema: async () => {
    try {
      return await request('/api/admin/points/pricing-config/schema', 'GET');
    } catch (error: any) {
      console.error('Get pricing config schema error:', error);
      return { code: 500, message: error.message || '获取表单配置失败', data: null };
    }
  },

  updatePricingConfig: async (body: Record<string, unknown>) => {
    try {
      return await request('/api/admin/points/pricing-config', 'PUT', body);
    } catch (error: any) {
      console.error('Update pricing config error:', error);
      return { code: 500, message: error.message || '保存定价配置失败', data: null };
    }
  },

  // ========== 投诉管理接口 ==========
  // 获取用户投诉列表
  getUserComplaints: async (userId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const response = await request(`/api/admin/complaints/user/${userId}`, 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get user complaints error:', error);
      return {
        code: 500,
        message: error.message || '获取投诉列表失败',
        data: { records: [], total: 0 }
      };
    }
  },

  // 处理投诉
  processComplaint: async (complaintId: string, data: {
    status: string;
    remark?: string;
    replyContent?: string;
  }) => {
    try {
      const response = await request(`/api/admin/complaints/${complaintId}/process`, 'PATCH', {
        status: data.status,
        replyContent: data.replyContent || data.remark
      });
      return response;
    } catch (error: any) {
      console.error('Process complaint error:', error);
      return {
        code: 500,
        message: error.message || '处理投诉失败',
        data: null
      };
    }
  },

  // ========== 内容审核（社区 moderation 队列）==========
  getModerationQueue: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const response = await request('/api/community/admin/moderation/queue', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get moderation queue error:', error);
      return {
        code: 500,
        message: error.message || '获取审核队列失败',
        data: { records: [], total: 0 }
      };
    }
  },

  moderationDecision: async (
    contentType: string,
    contentId: string,
    data: {
      decision: 'approved' | 'restricted' | 'rejected';
      reason?: string;
      message?: string;
      notifyAuthor?: boolean;
    }
  ) => {
    try {
      return await request(
        `/api/community/admin/moderation/${contentType}/${contentId}/decision`,
        'POST',
        data
      );
    } catch (error: any) {
      console.error('Moderation decision error:', error);
      return {
        code: 500,
        message: error.message || '审核操作失败',
        data: null
      };
    }
  },

  // ========== 客服反馈 ==========
  getCustomerFeedback: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    startTime?: string;
    endTime?: string;
  }) => {
    try {
      const response = await request('/api/admin/customer-service/feedback', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get customer feedback error:', error);
      return {
        code: 500,
        message: error.message || '获取用户反馈失败',
        data: { records: [], total: 0 }
      };
    }
  },

  replyCustomerFeedback: async (feedbackId: string, content: string) => {
    try {
      return await request(`/api/admin/customer-service/feedback/${feedbackId}/reply`, 'POST', { content });
    } catch (error: any) {
      console.error('Reply customer feedback error:', error);
      return {
        code: 500,
        message: error.message || '回复失败',
        data: null
      };
    }
  },

  // ========== 风控 ==========
  getRiskTransactions: async (params?: {
    page?: number;
    limit?: number;
    riskLevel?: string;
    status?: string;
    startTime?: string;
    endTime?: string;
  }) => {
    try {
      const response = await request('/api/admin/risk-control/transactions', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get risk transactions error:', error);
      return {
        code: 500,
        message: error.message || '获取异常交易失败',
        data: { records: [], total: 0 }
      };
    }
  },

  handleRiskTransaction: async (riskTransactionId: string, data: {
    status: string;
    remark?: string;
    refundPoints?: number;
  }) => {
    try {
      return await request(`/api/admin/risk-control/transactions/${riskTransactionId}/handle`, 'POST', data);
    } catch (error: any) {
      console.error('Handle risk transaction error:', error);
      return {
        code: 500,
        message: error.message || '处理异常交易失败',
        data: null
      };
    }
  },

  // ==================== 管理员管理API ====================

  // 获取管理员列表
  getAdmins: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    role?: string;
    status?: string;
  }) => {
    try {
      const response = await request('/api/admin/admins', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get admins error:', error);
      const isNotImplemented = error?.status === 404 || error?.code === 404;
      return {
        code: isNotImplemented ? 501 : 500,
        message: isNotImplemented
          ? '管理员 CRUD 接口尚未实现（文档 §15.4 /api/admin/admins）'
          : (error.message || '获取管理员列表失败'),
        data: isNotImplemented ? { total: 0, pages: 0, current: 1, size: 20, records: [] } : null
      };
    }
  },

  // 获取管理员详情
  getAdminDetail: async (adminId: string) => {
    try {
      const response = await request(`/api/admin/admins/${adminId}`, 'GET');
      return response;
    } catch (error: any) {
      console.error('Get admin detail error:', error);
      return {
        code: 500,
        message: error.message || '获取管理员详情失败',
        data: null
      };
    }
  },

  // 创建管理员
  createAdmin: async (data: {
    username: string;
    name: string;
    email: string;
    phone?: string;
    password: string;
    role: string;
    status?: string;
    permissions?: any;
  }) => {
    try {
      const response = await request('/api/admin/admins', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create admin error:', error);
      return {
        code: 500,
        message: error.message || '创建管理员失败',
        data: null
      };
    }
  },

  // 更新管理员
  updateAdmin: async (adminId: string, data: {
    username?: string;
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    status?: string;
    permissions?: any;
  }) => {
    try {
      const response = await request(`/api/admin/admins/${adminId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update admin error:', error);
      return {
        code: 500,
        message: error.message || '更新管理员失败',
        data: null
      };
    }
  },

  // 删除管理员
  deleteAdmin: async (adminId: string) => {
    try {
      const response = await request(`/api/admin/admins/${adminId}`, 'DELETE');
      return response;
    } catch (error: any) {
      console.error('Delete admin error:', error);
      return {
        code: 500,
        message: error.message || '删除管理员失败',
        data: null
      };
    }
  },

  // 切换管理员状态
  toggleAdminStatus: async (adminId: string, status: string) => {
    try {
      const response = await request(`/api/admin/admins/${adminId}/status`, 'PATCH', { status });
      return response;
    } catch (error: any) {
      console.error('Toggle admin status error:', error);
      return {
        code: 500,
        message: error.message || '更新管理员状态失败',
        data: null
      };
    }
  },

  // ==================== 角色管理API ====================

  // 获取角色列表
  getRoles: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
  }) => {
    try {
      const response = await request('/api/admin/roles', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get roles error:', error);
      return {
        code: 500,
        message: error.message || '获取角色列表失败',
        data: null
      };
    }
  },

  // 创建角色
  createRole: async (data: {
    name: string;
    description: string;
    permissions: any;
  }) => {
    try {
      const response = await request('/api/admin/roles', 'POST', data);
      return response;
    } catch (error: any) {
      console.error('Create role error:', error);
      return {
        code: 500,
        message: error.message || '创建角色失败',
        data: null
      };
    }
  },

  // 更新角色
  updateRole: async (roleId: string, data: {
    name?: string;
    description?: string;
    permissions?: any;
  }) => {
    try {
      const response = await request(`/api/admin/roles/${roleId}`, 'PUT', data);
      return response;
    } catch (error: any) {
      console.error('Update role error:', error);
      return {
        code: 500,
        message: error.message || '更新角色失败',
        data: null
      };
    }
  },

  // 删除角色（文档 §15.1：后端暂未实现 DELETE）
  deleteRole: async (_roleId: string) => {
    return {
      code: 501,
      message: '删除角色接口尚未实现（文档 §15.1，后端无 DELETE /api/admin/roles/{roleId}）',
      data: null
    };
  },

  /**
   * 私域分享漏斗总览（App share_journey 埋点 + user_referral）
   * GET /api/app/telemetry/admin/share-funnel?range=7d
   */
  getShareFunnelOverview: async (params?: {
    range?: '7d' | '30d';
    referrerId?: string;
  }) => {
    try {
      return await request('/api/app/telemetry/admin/share-funnel', 'GET', params);
    } catch (error: any) {
      console.error('Get share funnel overview error:', error);
      return {
        code: 500,
        message: error.message || '获取私域分享漏斗失败',
        data: null
      };
    }
  },

  /**
   * 邀请人排行（可选独立接口，或由 share-funnel 返回 topReferrers）
   * GET /api/app/telemetry/admin/share-funnel/referrers?range=7d
   */
  getShareFunnelTopReferrers: async (params?: {
    range?: '7d' | '30d';
    limit?: number;
  }) => {
    try {
      return await request('/api/app/telemetry/admin/share-funnel/referrers', 'GET', params);
    } catch (error: any) {
      console.error('Get share funnel referrers error:', error);
      return {
        code: 500,
        message: error.message || '获取邀请人排行失败',
        data: null
      };
    }
  },

  // ========== 一村一品PK评选管理 ==========
  getVillageProducts: async (communityId: string) => {
    try {
      return await request(`/api/community/village-product/${communityId}`, 'GET');
    } catch (error: any) {
      console.error('Get village products error:', error);
      return { code: 500, message: error.message || '获取一村一品失败', data: null };
    }
  },

  getVillageProductByVariety: async (communityId: string, varietyCode: string) => {
    try {
      return await request(`/api/community/village-product/${communityId}/${varietyCode}`, 'GET');
    } catch (error: any) {
      console.error('Get village product by variety error:', error);
      return { code: 500, message: error.message || '获取品种详情失败', data: null };
    }
  },

  triggerVillageProductElection: async (communityId: string) => {
    try {
      return await request(`/api/community/village-product/${communityId}/election`, 'POST');
    } catch (error: any) {
      console.error('Trigger election error:', error);
      return { code: 500, message: error.message || '触发评选失败', data: null };
    }
  },

  getVillageElectionResults: async (communityId: string) => {
    try {
      return await request(`/api/community/village-product/${communityId}/results`, 'GET');
    } catch (error: any) {
      console.error('Get election results error:', error);
      return { code: 500, message: error.message || '获取评选结果失败', data: null };
    }
  },

  getVillageOptOutList: async (communityId: string) => {
    try {
      return await request(`/api/community/village-product/${communityId}/opt-out/list`, 'GET');
    } catch (error: any) {
      console.error('Get opt-out list error:', error);
      return { code: 500, message: error.message || '获取退出列表失败', data: null };
    }
  },

  getVillageOptOutByVariety: async (communityId: string, varietyCode: string) => {
    try {
      return await request(`/api/community/village-product/${communityId}/opt-out/variety/${varietyCode}`, 'GET');
    } catch (error: any) {
      console.error('Get opt-out by variety error:', error);
      return { code: 500, message: error.message || '获取退出商家失败', data: null };
    }
  },

  // ========== 财务管理 ==========
  getFinanceAccounts: async (params?: { page?: number; limit?: number }) => {
    try {
      const response = await request('/api/finance/accounts', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            records: pageData.records || [],
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get finance accounts error:', error);
      return { code: 500, message: error.message || '获取账户列表失败', data: null };
    }
  },

  getFinanceTransactions: async (params?: {
    page?: number; limit?: number;
    type?: string; startDate?: string; endDate?: string;
    accountId?: string;
  }) => {
    try {
      const response = await request('/api/finance/transactions', 'GET', params);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            records: pageData.records || [],
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get finance transactions error:', error);
      return { code: 500, message: error.message || '获取交易记录失败', data: null };
    }
  },

  getFinanceBalance: async () => {
    try {
      return await request('/api/finance/balance', 'GET');
    } catch (error: any) {
      console.error('Get finance balance error:', error);
      return { code: 500, message: error.message || '获取余额失败', data: null };
    }
  },

  processWithdrawal: async (withdrawId: string, data: { status: string; remark?: string }) => {
    try {
      return await request(`/api/finance/withdraw/${withdrawId}`, 'POST', data);
    } catch (error: any) {
      console.error('Process withdrawal error:', error);
      return { code: 500, message: error.message || '处理提现失败', data: null };
    }
  },

  // ========== 数据分析增强 ==========
  getAnalyticsOverview: async (params?: { range?: 'day' | 'week' | 'month' | 'year' }) => {
    try {
      return await request('/api/analytics/overview', 'GET', params);
    } catch (error: any) {
      console.error('Get analytics overview error:', error);
      return { code: 500, message: error.message || '获取数据概览失败', data: null };
    }
  },

  getAnalyticsUsers: async (params?: { range?: 'day' | 'week' | 'month' | 'year' }) => {
    try {
      return await request('/api/analytics/users', 'GET', params);
    } catch (error: any) {
      console.error('Get analytics users error:', error);
      return { code: 500, message: error.message || '获取用户分析失败', data: null };
    }
  },

  getAnalyticsOrders: async (params?: { range?: 'day' | 'week' | 'month' | 'year' }) => {
    try {
      return await request('/api/analytics/orders', 'GET', params);
    } catch (error: any) {
      console.error('Get analytics orders error:', error);
      return { code: 500, message: error.message || '获取订单分析失败', data: null };
    }
  },

  getAnalyticsRevenue: async (params?: { range?: 'day' | 'week' | 'month' | 'year' }) => {
    try {
      return await request('/api/analytics/revenue', 'GET', params);
    } catch (error: any) {
      console.error('Get analytics revenue error:', error);
      return { code: 500, message: error.message || '获取收入分析失败', data: null };
    }
  }
};

// 合同管理API - 商户端合同管理功能
export const contractAPI = {
  // 获取合同列表
  getContractList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    buyerId?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      if (params?.buyerId) {
        queryParams.buyerId = params.buyerId;
      }
      
      // TODO: 后端接口待实现，暂时返回mock数据
      console.warn('合同管理API待实现，返回mock数据');
      return {
        code: 200,
        message: '获取合同列表成功',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    } catch (error: any) {
      console.error('Get contract list error:', error);
      return {
        code: 500,
        message: error.message || '获取合同列表失败',
        data: null
      };
    }
  },
  
  // 获取合同详情
  getContractDetail: async (_contractId: string) => {
    try {
      // TODO: 后端接口待实现
      console.warn('合同详情API待实现');
      return {
        code: 200,
        message: '获取合同详情成功',
        data: null
      };
    } catch (error: any) {
      console.error('Get contract detail error:', error);
      return {
        code: 500,
        message: error.message || '获取合同详情失败',
        data: null
      };
    }
  },
  
  // 创建合同
  createContract: async (_data: {
    title: string;
    buyerId: string;
    buyerName: string;
    buyerOrgId?: string;
    products: Array<{
      productId: string;
      productName: string;
      specName?: string;
      quantity: number;
      unitPrice: number;
    }>;
    installments?: {
      enabled: boolean;
      periods: number;
      downPaymentPercentage: number;
      startDate: string;
      paymentFrequency: string;
    };
    promotions?: {
      discount: number;
      minimumOrderAmount: number;
      couponCode: string;
      promotionEndDate: string;
    };
    notes?: string;
  }) => {
    try {
      // TODO: 后端接口待实现
      console.warn('创建合同API待实现');
      return {
        code: 200,
        message: '创建合同成功',
        data: {
          contractId: `CONTRACT${Date.now()}`,
          contractNumber: `CONTRACT${Date.now()}`
        }
      };
    } catch (error: any) {
      console.error('Create contract error:', error);
      return {
        code: 500,
        message: error.message || '创建合同失败',
        data: null
      };
    }
  },
  
  // 更新合同
  updateContract: async (_contractId: string, _data: {
    title?: string;
    products?: Array<{
      productId: string;
      productName: string;
      specName?: string;
      quantity: number;
      unitPrice: number;
    }>;
    notes?: string;
  }) => {
    try {
      // TODO: 后端接口待实现
      console.warn('更新合同API待实现');
      return {
        code: 200,
        message: '更新合同成功',
        data: null
      };
    } catch (error: any) {
      console.error('Update contract error:', error);
      return {
        code: 500,
        message: error.message || '更新合同失败',
        data: null
      };
    }
  },
  
  // 邀请买家确认合同
  inviteBuyerConfirmation: async (_contractId: string) => {
    try {
      // TODO: 后端接口待实现
      console.warn('邀请买家确认合同API待实现');
      return {
        code: 200,
        message: '邀请买家确认成功',
        data: null
      };
    } catch (error: any) {
      console.error('Invite buyer confirmation error:', error);
      return {
        code: 500,
        message: error.message || '邀请买家确认失败',
        data: null
      };
    }
  },
  
  // 上传合同文件
  uploadContractFile: async (contractId: string, _file: File) => {
    try {
      // TODO: 后端接口待实现
      console.warn('上传合同文件API待实现');
      return {
        code: 200,
        message: '上传合同文件成功',
        data: {
          fileUrl: `https://example.com/contracts/${contractId}.pdf`
        }
      };
    } catch (error: any) {
      console.error('Upload contract file error:', error);
      return {
        code: 500,
        message: error.message || '上传合同文件失败',
        data: null
      };
    }
  }
};

// 采购订单API - 采购部功能
export const purchaseOrderAPI = {
  // 获取采购订单列表
  getPurchaseOrderList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    supplierId?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      if (params?.supplierId) {
        queryParams.supplierId = params.supplierId;
      }
      
      // TODO: 后端接口待实现，暂时返回mock数据
      console.warn('采购订单列表API待实现，返回mock数据');
      return {
        code: 200,
        message: '获取采购订单列表成功',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    } catch (error: any) {
      console.error('Get purchase order list error:', error);
      return {
        code: 500,
        message: error.message || '获取采购订单列表失败',
        data: null
      };
    }
  },
  
  // 创建采购订单
  createPurchaseOrder: async (_data: {
    supplierId: string;
    products: Array<{
      productId: string;
      productName: string;
      quantity: number;
      unitPrice: number;
    }>;
    expectedDeliveryDate?: string;
    notes?: string;
  }) => {
    try {
      // TODO: 后端接口待实现
      console.warn('创建采购订单API待实现');
      return {
        code: 200,
        message: '创建采购订单成功',
        data: {
          orderId: `PO${Date.now()}`,
          orderNumber: `PO${Date.now()}`
        }
      };
    } catch (error: any) {
      console.error('Create purchase order error:', error);
      return {
        code: 500,
        message: error.message || '创建采购订单失败',
        data: null
      };
    }
  },
  
  // 更新采购订单
  updatePurchaseOrder: async (_orderId: string, _data: {
    status?: string;
    products?: Array<{
      productId: string;
      quantity: number;
      unitPrice: number;
    }>;
    notes?: string;
  }) => {
    try {
      // TODO: 后端接口待实现
      console.warn('更新采购订单API待实现');
      return {
        code: 200,
        message: '更新采购订单成功',
        data: null
      };
    } catch (error: any) {
      console.error('Update purchase order error:', error);
      return {
        code: 500,
        message: error.message || '更新采购订单失败',
        data: null
      };
    }
  }
};

// 供应商管理API - 采购部功能
export const supplierAPI = {
  // 获取供应商列?
  getSupplierList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      
      if (params?.status) {
        queryParams.status = params.status;
      }
      if (params?.category) {
        queryParams.category = params.category;
      }
      
      // TODO: 后端接口待实现，暂时返回mock数据
      console.warn('供应商列表API待实现，返回mock数据');
      return {
        code: 200,
        message: '获取供应商列表成功',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: params?.page || 1,
          size: params?.limit || 20
        }
      };
    } catch (error: any) {
      console.error('Get supplier list error:', error);
      return {
        code: 500,
        message: error.message || '获取供应商列表失败',
        data: null
      };
    }
  },
  
  // 创建供应?
  createSupplier: async (_data: {
    name: string;
    contactPerson: string;
    contactPhone: string;
    email?: string;
    address?: string;
    category?: string;
    orgId?: string;
  }) => {
    try {
      // TODO: 后端接口待实现
      console.warn('创建供应商API待实现');
      return {
        code: 200,
        message: '创建供应商成功',
        data: {
          supplierId: `SUP${Date.now()}`
        }
      };
    } catch (error: any) {
      console.error('Create supplier error:', error);
      return {
        code: 500,
        message: error.message || '创建供应商失败',
        data: null
      };
    }
  },
  
  // 更新供应?
  updateSupplier: async (_supplierId: string, _data: {
    name?: string;
    contactPerson?: string;
    contactPhone?: string;
    email?: string;
    address?: string;
    category?: string;
    status?: string;
  }) => {
    try {
      // TODO: 后端接口待实现
      console.warn('更新供应商API待实现');
      return {
        code: 200,
        message: '更新供应商成功',
        data: null
      };
    } catch (error: any) {
      console.error('Update supplier error:', error);
      return {
        code: 500,
        message: error.message || '更新供应商失败',
        data: null
      };
    }
  },
  
  // 获取供应商详?
  getSupplierDetail: async (_supplierId: string) => {
    try {
      // TODO: 后端接口待实现
      console.warn('供应商详情API待实现');
      return {
        code: 200,
        message: '获取供应商详情成功',
        data: null
      };
    } catch (error: any) {
      console.error('Get supplier detail error:', error);
      return {
        code: 500,
        message: error.message || '获取供应商详情失败',
        data: null
      };
    }
  }
};

// ==================== 数字人API - 闪剪AI平台 ====================

export const avatarAPI = {
  // 购买积分套餐
  purchasePackage: async (data: {
    packageId: string;
    packageType: string;
    points: number;
    price: number;
  }) => {
    try {
      return await request('/api/avatar/purchase-package', 'POST', data);
    } catch (error: any) {
      console.error('Purchase package error:', error);
      return {
        code: 500,
        message: error.message || '购买套餐失败',
        data: null
      };
    }
  },
  
  // 扣除积分
  deductPoints: async (videoId: string, points: number) => {
    try {
      return await request('/api/avatar/deduct-points', 'POST', {
        videoId,
        points
      });
    } catch (error: any) {
      console.error('Deduct points error:', error);
      return {
        code: 500,
        message: error.message || '扣除积分失败',
        data: null
      };
    }
  },
  
  // 创建2D数字人形象
  create2DAvatar: async (data: {
    avatarName: string;
    photoBase64: string;
    voiceId?: string;
    description?: string;
  }) => {
    try {
      return await request('/api/avatar/2d/create', 'POST', data);
    } catch (error: any) {
      console.error('Create 2D avatar error:', error);
      return {
        code: 500,
        message: error.message || '创建2D数字人失败',
        data: null
      };
    }
  },
  
  // 生成视频
  generateVideo: async (data: {
    avatarId: string;
    text: string;
    voiceId: string;
    videoFormat?: string;
  }) => {
    try {
      return await request('/api/avatar/video/generate', 'POST', data);
    } catch (error: any) {
      console.error('Generate video error:', error);
      return {
        code: 500,
        message: error.message || '生成视频失败',
        data: null
      };
    }
  },
  
  // 获取视频生成状态
  getVideoStatus: async (taskId: string) => {
    try {
      return await request(`/api/avatar/video/status/${taskId}`, 'GET');
    } catch (error: any) {
      console.error('Get video status error:', error);
      return {
        code: 500,
        message: error.message || '获取视频状态失败',
        data: null
      };
    }
  },
  
  // 获取用户的数字人分身列表
  getAvatars: async () => {
    try {
      return await request('/api/avatar/list', 'GET');
    } catch (error: any) {
      // 如果是404错误，说明API未实现，静默返回空列表
      if (error?.status === 404 || error?.response?.status === 404) {
        return {
          code: 200,
          message: 'API未实现，返回空列表',
          data: []
        };
      }
      console.error('Get avatars error:', error);
      return {
        code: 500,
        message: error.message || '获取数字人分身列表失败',
        data: []
      };
    }
  },
  
  // 创建数字人分身（从视频或图片+音频）
  createAvatar: async (data: {
    avatarName?: string;
    videoFile?: File;
    imageFile?: File;
    audioFile?: File;
    description?: string;
  }) => {
    try {
      const formData = new FormData();
      if (data.avatarName) formData.append('avatarName', data.avatarName);
      if (data.videoFile) formData.append('videoFile', data.videoFile);
      if (data.imageFile) formData.append('imageFile', data.imageFile);
      if (data.audioFile) formData.append('audioFile', data.audioFile);
      if (data.description) formData.append('description', data.description);
      
      return await request('/api/avatar/create', 'POST', formData, true);
    } catch (error: any) {
      console.error('Create avatar error:', error);
      return {
        code: 500,
        message: error.message || '创建数字人分身失败',
        data: null
      };
    }
  },
  
  // 获取数字人分身生成状态
  getAvatarGenerationStatus: async (avatarId: string) => {
    try {
      return await request(`/api/avatar/status/${avatarId}`, 'GET');
    } catch (error: any) {
      console.error('Get avatar generation status error:', error);
      return {
        code: 500,
        message: error.message || '获取生成状态失败',
        data: null
      };
    }
  }
};

// ==================== 文件 URL 规范化 ====================
/** 文件服务在站点上的路径前缀，生产可配置 VITE_FILE_SERVE_PATH（默认 /api/file） */
function getFileServePath(): string {
  return (import.meta.env.VITE_FILE_SERVE_PATH as string) || '/api/file';
}

/**
 * 将后端返回的文件 URL 规范化为当前站点可用的相对路径（用于存储与展示）。
 * 后端常返回 http://localhost:8090/files/...，需转为 /api/file/files/... 以便同源访问。
 * 约定：注册/用户中心保存头像时统一用此函数处理后再写入 profile.avatar，与查询、展示路径一致。
 */
export function normalizeFileUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') return '';
  let u = url.replace(/\\/g, '/').trim();
  const base = getFileServePath();
  // 已是规范路径
  if (u.startsWith(base + '/') || u === base) return u;
  // 后端返回的直连地址（localhost:8090/8087）
  if (u.includes('localhost:8090') || u.includes(':8090/') || u.includes('localhost:8087') || u.includes(':8087/')) {
    const m = u.match(/\/files\/(.+)$/);
    if (m) return `${base}/files/${m[1]}`;
    return u.replace(/localhost:8090/g, 'localhost:8080').replace(/localhost:8087/g, 'localhost:8080').replace(/\/files\//g, `${base}/files/`);
  }
  // 相对路径 /files/...
  if (u.startsWith('/files/') && !u.startsWith('/api/')) return base + u;
  // 生产环境：后端返回的完整接口域名 URL（如 https://api.globewe.com/files/...），转为同源相对路径以便 admin 站 Nginx 代理
  if ((u.startsWith('http://') || u.startsWith('https://')) && u.includes('/files/')) {
    const m = u.match(/\/files\/(.+)$/);
    if (m) return `${base}/files/${m[1]}`;
  }
  // 其他相对路径（以 / 开头且非 http/data）
  if (u.startsWith('/') && !u.startsWith('http') && !u.startsWith('data:')) return base + (u.startsWith(base) ? u.slice(base.length) : u);
  // 非 / 开头的相对路径（如 "2026/01/30/xxx.jpg"）
  if (!u.startsWith('http://') && !u.startsWith('https://') && !u.startsWith('data:')) return `${base}/files/${u}`;
  return u;
}

// ==================== 文件API ====================

export const fileAPI = {
  // 上传图片
  uploadImage: async (formData: FormData) => {
    try {
      // 获取token，与统一请求处理逻辑保持一致
      // 优先从authToken键获取token（这是标准存储方式）
      let token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      // 如果authToken不存在，尝试从auth对象中获取
      if (!token) {
        try {
          const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
          if (authData) {
            const auth = JSON.parse(authData);
            // 尝试多种可能的字段名
            token = auth.token || auth.authToken || auth.accessToken;
          }
        } catch (e) {
          // 如果解析失败，记录警告
          if (import.meta.env.DEV) {
            console.warn('[FileAPI] 解析auth对象失败:', e);
          }
        }
      }
      
      if (!token) {
        // 调试日志：记录token缺失情况
        if (import.meta.env.DEV) {
          console.error('[FileAPI] Token获取失败:', {
            hasLocalStorageToken: !!localStorage.getItem('authToken'),
            hasSessionStorageToken: !!sessionStorage.getItem('authToken'),
            hasLocalStorageAuth: !!localStorage.getItem('auth'),
            hasSessionStorageAuth: !!sessionStorage.getItem('auth')
          });
        }
        throw new Error('未找到认证token，请先登录');
      }
      
      // 调试日志（仅开发环境）
      if (import.meta.env.DEV) {
        console.log('[FileAPI] Token已获取，准备上传图片');
      }
      
      // 强制使用相对路径，通过Vite代理转发到Gateway（避免CORS问题）
      // 统一使用 Gateway 地址进行文件上传
      // 开发环境：通过 Vite 代理转发到 Gateway
      // 生产环境：通过 Gateway 访问（如果配置了 Gateway URL，否则使用相对路径）
      const gatewayUrl = import.meta.env.VITE_GATEWAY_URL;
      const uploadUrl = gatewayUrl 
        ? `${gatewayUrl}/api/file/upload`  // 如果配置了 Gateway URL，使用完整路径
        : '/api/file/upload';  // 否则使用相对路径（通过 Vite 代理或 Gateway）
      
      // 如果没有指定category，默认使用image
      if (!formData.has('category')) {
        formData.append('category', 'image');
      }
      
      // 调试日志
      if (import.meta.env.DEV) {
        console.log('[FileAPI] 准备上传文件:', {
          url: uploadUrl,
          hasToken: !!token,
          tokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
          formDataKeys: Array.from(formData.keys())
        });
      }
      
      // 注意：使用FormData时，不要手动设置Content-Type，浏览器会自动设置（包括boundary）
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // 不要设置Content-Type，让浏览器自动设置multipart/form-data和boundary
        },
        body: formData
      });
      
      // 检查响应状态
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `上传失败: HTTP ${response.status}`;
        
        // 如果是401错误，提供更详细的提示
        if (response.status === 401) {
          errorMessage = '认证失败，请重新登录';
          // 尝试清除无效的token（同时清除localStorage和sessionStorage）
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('authToken');
          try {
            // 清除localStorage中的auth
            const authStr = localStorage.getItem('auth');
            if (authStr) {
              const auth = JSON.parse(authStr);
              delete auth.token;
              delete auth.accessToken;
              localStorage.setItem('auth', JSON.stringify(auth));
            }
            // 清除sessionStorage中的auth
            const sessionAuthStr = sessionStorage.getItem('auth');
            if (sessionAuthStr) {
              const sessionAuth = JSON.parse(sessionAuthStr);
              delete sessionAuth.token;
              delete sessionAuth.accessToken;
              sessionStorage.setItem('auth', JSON.stringify(sessionAuth));
            }
          } catch (e) {
            // 忽略
          }
        } else {
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.code === 200 && result.data) {
        const rawUrl = result.data.fileUrl || result.data.url || (typeof result.data === 'string' ? result.data : '');
        // 返回规范化路径，与用户中心存储/展示一致（/api/file/files/...）
        const url = normalizeFileUrl(rawUrl) || rawUrl;
        return {
          code: 200,
          message: '上传成功',
          data: {
            url,
            fileId: result.data.fileId || result.data.id
          }
        };
      } else {
        return {
          code: result.code || 500,
          message: result.message || '上传失败',
          data: null
        };
      }
    } catch (error: any) {
      console.error('Upload image error:', error);
      
      // 提供更详细的错误信息
      let errorMessage = '上传图片失败，请稍后重试';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.name === 'TypeError' && error.message?.includes('Failed to fetch')) {
        errorMessage = '无法连接到文件服务，请检查：1) 文件服务是否已启动（端口8090） 2) 网络连接是否正常 3) CORS配置是否正确';
      }
      
      return {
        code: 500,
        message: errorMessage,
        data: null
      };
    }
  }
};

// 招聘相关API
export const jobAPI = {
  // 获取招聘列表
  getJobList: async (params?: {
    page?: number;
    limit?: number;
    keyword?: string;
    type?: string;
    location?: string;
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.keyword) queryParams.append('keyword', params.keyword);
      if (params?.type) queryParams.append('type', params.type);
      if (params?.location) queryParams.append('location', params.location);
      
      const url = `/api/job/list${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await request(url, 'GET');
      
      // 如果返回非200状态码，检查是否是服务不可用
      if (response.code !== 200 && response.code !== 0) {
        const isServiceUnavailable = 
          response.code === 404 || 
          response.message?.includes('Service not found') || 
          response.message?.includes('Nacos') ||
          response.message?.includes('服务未找到') ||
          response.message?.includes('服务暂不可用');
        
        if (isServiceUnavailable) {
          console.warn('[JobAPI] 招聘服务不可用，返回降级响应');
          return {
            code: 404,
            message: '招聘服务暂不可用，请稍后重试',
            data: null,
            serviceUnavailable: true
          };
        }
      }
      
      return response;
    } catch (error: any) {
      console.error('Get job list error:', error);
      
      // 检查是否是 404 错误或服务不可用错误
      const isServiceUnavailable = 
        error.isServiceUnavailable || // 从request函数传递的标记
        error.status === 404 || 
        error.message?.includes('Service not found') || 
        error.message?.includes('Nacos') ||
        error.response?.includes('Service not found') ||
        error.response?.includes('Nacos');
      
      if (isServiceUnavailable) {
        return {
          code: 404,
          message: '招聘服务暂不可用，请稍后重试',
          data: null,
          serviceUnavailable: true
        };
      }
      
      return {
        code: 500,
        message: error.message || '获取招聘列表失败',
        data: null
      };
    }
  },
  
  // 获取招聘详情
  getJobDetail: async (jobId: string) => {
    try {
      const response = await request(`/api/job/${jobId}`, 'GET');
      if (response.code !== 200 && response.code !== 0) {
        const isServiceUnavailable =
          response.code === 404 ||
          response.message?.includes('Service not found') ||
          response.message?.includes('Nacos') ||
          response.message?.includes('服务未找到') ||
          response.message?.includes('服务暂不可用');
        if (isServiceUnavailable) {
          return {
            code: 404,
            message: '招聘服务暂不可用，请稍后重试',
            data: null,
            serviceUnavailable: true
          };
        }
      }
      return response;
    } catch (error: any) {
      console.error('Get job detail error:', error);
      const isServiceUnavailable =
        error.isServiceUnavailable ||
        error.status === 404 ||
        error.message?.includes('Service not found') ||
        error.message?.includes('Nacos') ||
        error.response?.includes('Service not found') ||
        error.response?.includes('Nacos');
      if (isServiceUnavailable) {
        return {
          code: 404,
          message: '招聘服务暂不可用，请稍后重试',
          data: null,
          serviceUnavailable: true
        };
      }
      return {
        code: 500,
        message: error.message || '获取招聘详情失败',
        data: null
      };
    }
  },
  
  // 申请职位
  applyJob: async (data: {
    jobId: string;
    resume?: string;
    coverLetter?: string;
  }) => {
    try {
      return await request('/api/job/apply', 'POST', data);
    } catch (error: any) {
      console.error('Apply job error:', error);
      return {
        code: 500,
        message: error.message || '申请职位失败',
        data: null
      };
    }
  }
};

// ==================== 用户信誉分 API（对接 globewe-credit-score）====================
export const creditScoreAPI = {
  // 查询用户信誉分
  getCreditScore: async (userId?: string) => {
    try {
      const path = userId ? `/api/credit-score/${userId}` : '/api/credit-score/me';
      return await request(path, 'GET');
    } catch (error: any) {
      console.error('Get credit score error:', error);
      return {
        code: 500,
        message: error.message || '获取信誉分失败',
        data: null
      };
    }
  },

  // 获取信誉分历史记录（当前仅支持登录用户本人，管理端查他人待后端扩展）
  getCreditScoreHistory: async (params?: {
    userId?: string;
    page?: number;
    limit?: number;
  }) => {
    if (params?.userId) {
      return {
        code: 501,
        message: '管理端查询他人信誉分历史接口尚未实现',
        data: { records: [], total: 0 }
      };
    }
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request('/api/credit-score/history', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get credit score history error:', error);
      return {
        code: 500,
        message: error.message || '获取信誉分历史失败',
        data: null
      };
    }
  },

  // 管理员调整信誉分
  adjustCreditScore: async (data: {
    userId: string;
    adjustmentAmount: number;
    reason: string;
  }) => {
    try {
      return await request('/api/credit-score/admin/adjust', 'POST', {
        userId: data.userId,
        amount: data.adjustmentAmount,
        reason: data.reason
      });
    } catch (error: any) {
      console.error('Adjust credit score error:', error);
      return {
        code: 500,
        message: error.message || '调整信誉分失败',
        data: null
      };
    }
  },

  // 用户信誉分列表（管理端分页 API 未实现，暂用排行榜 ranking）
  getUserListByCredit: async (params?: {
    page?: number;
    limit?: number;
    minScore?: number;
    maxScore?: number;
    level?: string;
  }) => {
    try {
      const pageSize = params?.limit || 20;
      const response = await request('/api/credit-score/ranking', 'GET', {
        page: params?.page || 1,
        limit: Math.max(pageSize * (params?.page || 1), pageSize),
        type: params?.level
      });
      if (response.code === 200 && Array.isArray(response.data)) {
        let records = response.data.map((item: any) => ({
          userId: item.user_id || item.userId,
          username: item.username,
          phone: item.phone,
          score: item.score ?? 0,
          level: item.level || 'normal',
          levelName: item.levelName,
          createTime: item.create_time || item.createTime || '',
          updateTime: item.last_update_time || item.lastUpdateTime || ''
        }));
        if (params?.minScore !== undefined) {
          records = records.filter((r: any) => r.score >= params.minScore!);
        }
        if (params?.maxScore !== undefined) {
          records = records.filter((r: any) => r.score <= params.maxScore!);
        }
        if (params?.level) {
          records = records.filter((r: any) => r.level === params.level);
        }
        const page = params?.page || 1;
        const start = (page - 1) * pageSize;
        const paged = records.slice(start, start + pageSize);
        return {
          code: 200,
          message: '获取成功（数据来源：信誉分排行榜，完整分页列表待后端实现）',
          data: {
            records: paged,
            total: records.length,
            current: page,
            size: pageSize,
            pages: Math.ceil(records.length / pageSize) || 1
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get users by credit error:', error);
      return {
        code: 500,
        message: error.message || '获取用户列表失败',
        data: null
      };
    }
  }
};

// ==================== 订单评价 API ====================
export const reviewAPI = {
  // 提交订单评价
  submitReview: async (data: {
    orderId: string;
    reviewerId: string;
    revieweeId: string;
    rating: number;
    comment?: string;
    reviewType: 'passenger_to_driver' | 'driver_to_passenger';
  }) => {
    try {
      return await request('/api/review/submit', 'POST', data);
    } catch (error: any) {
      console.error('Submit review error:', error);
      return {
        code: 500,
        message: error.message || '提交评价失败',
        data: null
      };
    }
  },

  // 获取订单评价（对接 globewe-order §10 关联评价）
  getOrderReviews: async (orderId: string) => {
    try {
      const response = await request(`/api/order/reviews/order/${orderId}`, 'GET');
      if (response.code === 200 && response.data) {
        const data = response.data;
        return {
          ...response,
          data: Array.isArray(data) ? data : [data]
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get order reviews error:', error);
      return {
        code: 500,
        message: error.message || '获取订单评价失败',
        data: null
      };
    }
  },

  // 获取用户收到的评价
  getUserReceivedReviews: async (userId: string, params?: {
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request(`/api/review/user/${userId}/received`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Get user received reviews error:', error);
      return {
        code: 500,
        message: error.message || '获取用户评价失败',
        data: null
      };
    }
  }
};

// ==================== 社区积分 API ====================
export const communityPointsAPI = {
  // 获取社区积分池信息
  getPointsPool: async (communityId: string) => {
    try {
      return await request(`/api/community/${communityId}/points-pool`, 'GET');
    } catch (error: any) {
      console.error('Get points pool error:', error);
      return {
        code: 500,
        message: error.message || '获取积分池信息失败',
        data: null
      };
    }
  },

  // 获取积分收支明细
  getPointsHistory: async (communityId: string, params?: {
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request(`/api/community/${communityId}/points-history`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Get points history error:', error);
      return {
        code: 500,
        message: error.message || '获取积分历史失败',
        data: null
      };
    }
  },

  // 月度自动积分分配（通常由系统定时任务调用）
  autoDistributePoints: async (communityId: string) => {
    try {
      return await request(`/api/community/${communityId}/points/auto-distribute`, 'POST');
    } catch (error: any) {
      console.error('Auto distribute points error:', error);
      return {
        code: 500,
        message: error.message || '积分分配失败',
        data: null
      };
    }
  }
};

// ==================== 社区视频 API ====================
export const communityVideoAPI = {
  // 上传视频
  uploadVideo: async (formData: FormData) => {
    try {
      // 获取token
      let token = null;
      try {
        const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
        if (authData) {
          const auth = JSON.parse(authData);
          token = auth.token || auth.authToken;
        }
      } catch (e) {
        // 忽略
      }
      
      if (!token) {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      }
      
      if (!token) {
        throw new Error('未授权，请先登录');
      }

      const response = await fetch('/api/video/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = '视频上传失败';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error: any) {
      console.error('Upload video error:', error);
      return {
        code: 500,
        message: error.message || '视频上传失败',
        data: null
      };
    }
  },

  // 获取视频列表
  getVideoList: async (params?: {
    communityId?: string;
    page?: number;
    limit?: number;
    category?: string;
    accessType?: string;
    sortBy?: string;
    order?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.communityId) queryParams.communityId = params.communityId;
      if (params?.category) queryParams.category = params.category;
      if (params?.accessType) queryParams.accessType = params.accessType;
      if (params?.sortBy) queryParams.sortBy = params.sortBy;
      if (params?.order) queryParams.order = params.order;
      return await request('/api/video/list', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get video list error:', error);
      return {
        code: 500,
        message: error.message || '获取视频列表失败',
        data: null
      };
    }
  },

  // 获取视频详情
  getVideoDetail: async (videoId: string) => {
    try {
      return await request(`/api/video/${videoId}`, 'GET');
    } catch (error: any) {
      console.error('Get video detail error:', error);
      return {
        code: 500,
        message: error.message || '获取视频详情失败',
        data: null
      };
    }
  },

  // 购买视频观看权限
  purchaseVideo: async (data: {
    videoId: string;
    pointsRequired: number;
  }) => {
    try {
      return await request('/api/video/purchase', 'POST', data);
    } catch (error: any) {
      console.error('Purchase video error:', error);
      return {
        code: 500,
        message: error.message || '购买视频失败',
        data: null
      };
    }
  },

  // 点赞视频
  likeVideo: async (videoId: string) => {
    try {
      return await request(`/api/video/${videoId}/like`, 'POST');
    } catch (error: any) {
      console.error('Like video error:', error);
      return {
        code: 500,
        message: error.message || '点赞失败',
        data: null
      };
    }
  },

  // 取消点赞
  unlikeVideo: async (videoId: string) => {
    try {
      return await request(`/api/video/${videoId}/like`, 'DELETE');
    } catch (error: any) {
      console.error('Unlike video error:', error);
      return {
        code: 500,
        message: error.message || '取消点赞失败',
        data: null
      };
    }
  },

  // 获取用户视频列表
  getUserVideos: async (params?: {
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request('/api/video/user/videos', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get user videos error:', error);
      return {
        code: 500,
        message: error.message || '获取用户视频失败',
        data: null
      };
    }
  },

  // 删除视频
  deleteVideo: async (videoId: string) => {
    try {
      return await request(`/api/video/${videoId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete video error:', error);
      return {
        code: 500,
        message: error.message || '删除视频失败',
        data: null
      };
    }
  },

  // 获取扣点配置
  getDeductionConfig: async () => {
    try {
      return await request('/api/video/config/deduction', 'GET');
    } catch (error: any) {
      console.error('Get deduction config error:', error);
      return {
        code: 500,
        message: error.message || '获取扣点配置失败',
        data: null
      };
    }
  },

  // 获取积分交易记录
  getPointsTransactions: async (params?: {
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      return await request('/api/video/points/transactions', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get points transactions error:', error);
      return {
        code: 500,
        message: error.message || '获取交易记录失败',
        data: null
      };
    }
  },

  // 获取作者收入统计
  getAuthorStats: async () => {
    try {
      return await request('/api/video/points/stats', 'GET');
    } catch (error: any) {
      console.error('Get author stats error:', error);
      return {
        code: 500,
        message: error.message || '获取收入统计失败',
        data: null
      };
    }
  },

  // 购买个人私享会员
  purchaseMembership: async (data: {
    membershipType: string;
    duration: number;
  }) => {
    try {
      return await request('/api/video/membership/purchase', 'POST', data);
    } catch (error: any) {
      console.error('Purchase membership error:', error);
      return {
        code: 500,
        message: error.message || '购买会员失败',
        data: null
      };
    }
  },

  // 检查会员状态
  getMembershipStatus: async () => {
    try {
      return await request('/api/video/membership/status', 'GET');
    } catch (error: any) {
      console.error('Get membership status error:', error);
      return {
        code: 500,
        message: error.message || '获取会员状态失败',
        data: null
      };
    }
  }
};

// ==================== 认证 API ====================
export const authAPI = {
  // 用户登录
  login: async (data: {
    loginType: 'password' | 'sms';
    username?: string;
    password?: string;
    phone?: string;
    code?: string;
  }) => {
    try {
      return await request('/api/auth/login', 'POST', data, false);
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        code: 500,
        message: error.message || '登录失败',
        data: null
      };
    }
  },

  // 用户登出
  logout: async () => {
    try {
      return await request('/api/auth/logout', 'POST');
    } catch (error: any) {
      console.error('Logout error:', error);
      return {
        code: 500,
        message: error.message || '登出失败',
        data: null
      };
    }
  },

  // 刷新Token
  refreshToken: async () => {
    try {
      return await request('/api/auth/refresh', 'POST');
    } catch (error: any) {
      console.error('Refresh token error:', error);
      return {
        code: 500,
        message: error.message || '刷新Token失败',
        data: null
      };
    }
  }
};

// ==================== 会议 API ====================
export const meetingAPI = {
  // 获取会议列表
  getMeetingList: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.status) queryParams.status = params.status;
      return await request('/api/meeting/list', 'POST', queryParams);
    } catch (error: any) {
      console.error('Get meeting list error:', error);
      return {
        code: 500,
        message: error.message || '获取会议列表失败',
        data: null
      };
    }
  },

  // 创建会议
  createMeeting: async (data: {
    meetingName: string;
    callType: 'audio' | 'video';
    maxParticipants?: number;
    plannedDuration?: number; // 分钟
  }) => {
    try {
      return await request('/api/meeting/create', 'POST', data);
    } catch (error: any) {
      console.error('Create meeting error:', error);
      return {
        code: 500,
        message: error.message || '创建会议失败',
        data: null
      };
    }
  },

  // 加入会议
  joinMeeting: async (data: {
    roomId: string;
  }) => {
    try {
      return await request('/api/meeting/join', 'POST', data);
    } catch (error: any) {
      console.error('Join meeting error:', error);
      return {
        code: 500,
        message: error.message || '加入会议失败',
        data: null
      };
    }
  },

  // 结束会议
  finishMeeting: async (data: {
    roomId: string;
  }) => {
    try {
      return await request('/api/meeting/finish', 'POST', data);
    } catch (error: any) {
      console.error('Finish meeting error:', error);
      return {
        code: 500,
        message: error.message || '结束会议失败',
        data: null
      };
    }
  },

  // 获取会议详情
  getMeetingDetail: async (data: {
    roomId: string;
  }) => {
    try {
      return await request('/api/meeting/detail', 'POST', data);
    } catch (error: any) {
      console.error('Get meeting detail error:', error);
      return {
        code: 500,
        message: error.message || '获取会议详情失败',
        data: null
      };
    }
  },

  // 生成会议ID
  generateMeetingId: async () => {
    try {
      return await request('/api/meeting/generate-id', 'POST');
    } catch (error: any) {
      console.error('Generate meeting ID error:', error);
      return {
        code: 500,
        message: error.message || '生成会议ID失败',
        data: null
      };
    }
  },

  // 查询历史会议
  getMeetingHistory: async (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.startDate) queryParams.startDate = params.startDate;
      if (params?.endDate) queryParams.endDate = params.endDate;
      return await request('/api/meeting/history', 'POST', queryParams);
    } catch (error: any) {
      console.error('Get meeting history error:', error);
      return {
        code: 500,
        message: error.message || '获取历史会议失败',
        data: null
      };
    }
  },

  // 获取会议时长配置
  getDurationConfig: async () => {
    try {
      return await request('/api/meeting/duration-config', 'POST');
    } catch (error: any) {
      console.error('Get duration config error:', error);
      return {
        code: 500,
        message: error.message || '获取时长配置失败',
        data: null
      };
    }
  },

  // 获取会议时长信息
  getDurationInfo: async (data: {
    roomId: string;
  }) => {
    try {
      return await request('/api/meeting/duration-info', 'POST', data);
    } catch (error: any) {
      console.error('Get duration info error:', error);
      return {
        code: 500,
        message: error.message || '获取时长信息失败',
        data: null
      };
    }
  },

  // 扩展会议时长
  extendMeeting: async (data: {
    roomId: string;
    extendMinutes: number;
  }) => {
    try {
      return await request('/api/meeting/extend', 'POST', data);
    } catch (error: any) {
      console.error('Extend meeting error:', error);
      return {
        code: 500,
        message: error.message || '扩展会议时长失败',
        data: null
      };
    }
  }
};

// ==================== 会议直播 API ====================
export const meetingLiveAPI = {
  // 开启会议直播
  startLive: async (roomId: string) => {
    try {
      return await request(`/api/call/room/${roomId}/live/start`, 'POST');
    } catch (error: any) {
      console.error('Start live error:', error);
      return {
        code: 500,
        message: error.message || '开启直播失败',
        data: null
      };
    }
  },

  // 停止会议直播
  stopLive: async (roomId: string) => {
    try {
      return await request(`/api/call/room/${roomId}/live/stop`, 'POST');
    } catch (error: any) {
      console.error('Stop live error:', error);
      return {
        code: 500,
        message: error.message || '停止直播失败',
        data: null
      };
    }
  },

  // 获取直播状态
  getLiveStatus: async (roomId: string) => {
    try {
      return await request(`/api/call/room/${roomId}/live/status`, 'GET');
    } catch (error: any) {
      console.error('Get live status error:', error);
      return {
        code: 500,
        message: error.message || '获取直播状态失败',
        data: null
      };
    }
  },

  // 观众进入直播
  enterLive: async (roomId: string) => {
    try {
      return await request(`/api/call/room/${roomId}/live/enter`, 'POST');
    } catch (error: any) {
      console.error('Enter live error:', error);
      return {
        code: 500,
        message: error.message || '进入直播失败',
        data: null
      };
    }
  },

  // 观众离开直播
  leaveLive: async (roomId: string) => {
    try {
      return await request(`/api/call/room/${roomId}/live/leave`, 'POST');
    } catch (error: any) {
      console.error('Leave live error:', error);
      return {
        code: 500,
        message: error.message || '离开直播失败',
        data: null
      };
    }
  }
};

// ==================== 消息表情回应 API ====================
export const messageReactionAPI = {
  // 添加表情回应
  addReaction: async (data: {
    messageId: string;
    emoji: string;
  }) => {
    try {
      return await request('/api/im/messages/reactions', 'POST', data);
    } catch (error: any) {
      console.error('Add reaction error:', error);
      return {
        code: 500,
        message: error.message || '添加表情回应失败',
        data: null
      };
    }
  },

  // 移除表情回应
  removeReaction: async (data: {
    messageId: string;
    emoji: string;
  }) => {
    try {
      return await request('/api/im/messages/reactions', 'DELETE', data);
    } catch (error: any) {
      console.error('Remove reaction error:', error);
      return {
        code: 500,
        message: error.message || '移除表情回应失败',
        data: null
      };
    }
  },

  // 获取消息的所有回应
  getMessageReactions: async (messageId: string) => {
    try {
      return await request(`/api/im/messages/reactions/${messageId}`, 'GET');
    } catch (error: any) {
      console.error('Get message reactions error:', error);
      return {
        code: 500,
        message: error.message || '获取表情回应失败',
        data: null
      };
    }
  },

  // 批量获取消息回应
  getBatchReactions: async (messageIds: string[]) => {
    try {
      return await request('/api/im/messages/reactions/batch', 'POST', { messageIds });
    } catch (error: any) {
      console.error('Get batch reactions error:', error);
      return {
        code: 500,
        message: error.message || '批量获取表情回应失败',
        data: null
      };
    }
  }
};

// ==================== 消息草稿 API ====================
export const messageDraftAPI = {
  // 保存草稿
  saveDraft: async (data: {
    conversationId: string;
    content: string;
  }) => {
    try {
      return await request('/api/im/drafts', 'POST', data);
    } catch (error: any) {
      console.error('Save draft error:', error);
      return {
        code: 500,
        message: error.message || '保存草稿失败',
        data: null
      };
    }
  },

  // 获取草稿
  getDraft: async (conversationId: string) => {
    try {
      return await request(`/api/im/drafts?conversationId=${conversationId}`, 'GET');
    } catch (error: any) {
      console.error('Get draft error:', error);
      return {
        code: 500,
        message: error.message || '获取草稿失败',
        data: null
      };
    }
  },

  // 删除草稿
  deleteDraft: async (conversationId: string) => {
    try {
      return await request(`/api/im/drafts?conversationId=${conversationId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete draft error:', error);
      return {
        code: 500,
        message: error.message || '删除草稿失败',
        data: null
      };
    }
  },

  // 获取所有草稿
  getMyDrafts: async () => {
    try {
      return await request('/api/im/drafts/my-drafts', 'GET');
    } catch (error: any) {
      console.error('Get my drafts error:', error);
      return {
        code: 500,
        message: error.message || '获取我的草稿失败',
        data: null
      };
    }
  }
};

// ==================== 消息已读回执 API ====================
export const readReceiptAPI = {
  // 标记已读
  markAsRead: async (data: {
    messageId: string;
    conversationId?: string;
  }) => {
    try {
      return await request('/api/im/messages/read-receipts', 'POST', data);
    } catch (error: any) {
      console.error('Mark as read error:', error);
      return {
        code: 500,
        message: error.message || '标记已读失败',
        data: null
      };
    }
  },

  // 批量标记已读
  batchMarkAsRead: async (data: {
    messageIds: string[];
    conversationId?: string;
  }) => {
    try {
      return await request('/api/im/messages/read-receipts/batch', 'POST', data);
    } catch (error: any) {
      console.error('Batch mark as read error:', error);
      return {
        code: 500,
        message: error.message || '批量标记已读失败',
        data: null
      };
    }
  },

  // 获取已读回执列表
  getReadReceipts: async (messageId: string) => {
    try {
      return await request(`/api/im/messages/read-receipts/${messageId}`, 'GET');
    } catch (error: any) {
      console.error('Get read receipts error:', error);
      return {
        code: 500,
        message: error.message || '获取已读回执失败',
        data: null
      };
    }
  },

  // 获取已读统计
  getReadStats: async (messageId: string) => {
    try {
      return await request(`/api/im/messages/read-receipts/${messageId}/stats`, 'GET');
    } catch (error: any) {
      console.error('Get read stats error:', error);
      return {
        code: 500,
        message: error.message || '获取已读统计失败',
        data: null
      };
    }
  },

  // 检查是否已读
  checkReadStatus: async (data: {
    messageId: string;
    userId: string;
  }) => {
    try {
      return await request('/api/im/messages/read-receipts/check', 'GET', data);
    } catch (error: any) {
      console.error('Check read status error:', error);
      return {
        code: 500,
        message: error.message || '检查已读状态失败',
        data: null
      };
    }
  }
};

// ==================== 消息搜索 API ====================
export const messageSearchAPI = {
  // 全局搜索消息
  searchMessages: async (params: {
    keyword: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        keyword: params.keyword,
        page: params.page || 1,
        limit: params.limit || 20
      };
      return await request('/api/im/messages/search', 'GET', queryParams);
    } catch (error: any) {
      console.error('Search messages error:', error);
      return {
        code: 500,
        message: error.message || '搜索消息失败',
        data: null
      };
    }
  },

  // 会话内搜索
  searchInConversation: async (data: {
    conversationId: string;
    keyword: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        keyword: data.keyword,
        page: data.page || 1,
        limit: data.limit || 20
      };
      return await request(`/api/im/messages/search/conversation/${data.conversationId}`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Search in conversation error:', error);
      return {
        code: 500,
        message: error.message || '会话内搜索失败',
        data: null
      };
    }
  },

  // 按类型搜索
  searchByType: async (data: {
    messageType: string;
    keyword?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: data.page || 1,
        limit: data.limit || 20
      };
      if (data.keyword) queryParams.keyword = data.keyword;
      return await request(`/api/im/messages/search/type/${data.messageType}`, 'GET', queryParams);
    } catch (error: any) {
      console.error('Search by type error:', error);
      return {
        code: 500,
        message: error.message || '按类型搜索失败',
        data: null
      };
    }
  },

  // 获取发送的消息
  getSentMessages: async (params?: {
    keyword?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.keyword) queryParams.keyword = params.keyword;
      return await request('/api/im/messages/search/sent', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get sent messages error:', error);
      return {
        code: 500,
        message: error.message || '获取发送的消息失败',
        data: null
      };
    }
  },

  // 获取接收的消息
  getReceivedMessages: async (params?: {
    keyword?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.keyword) queryParams.keyword = params.keyword;
      return await request('/api/im/messages/search/received', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get received messages error:', error);
      return {
        code: 500,
        message: error.message || '获取接收的消息失败',
        data: null
      };
    }
  }
};

// ========== 法律协议文档 API ==========
export const legalDocumentAPI = {
  getDocuments: async (params?: { page?: number; limit?: number; documentType?: string; status?: string }) => {
    try {
      return await request('/api/admin/legal-documents', 'GET', params);
    } catch (error: any) {
      console.error('Get legal documents error:', error);
      return { code: 500, message: error.message || '获取协议文档失败', data: null };
    }
  },
  createDocument: async (data: Record<string, unknown>) => {
    try {
      return await request('/api/admin/legal-documents', 'POST', data);
    } catch (error: any) {
      console.error('Create legal document error:', error);
      return { code: 500, message: error.message || '创建协议文档失败', data: null };
    }
  },
  updateDocument: async (id: string, data: Record<string, unknown>) => {
    try {
      return await request(`/api/admin/legal-documents/${id}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update legal document error:', error);
      return { code: 500, message: error.message || '更新协议文档失败', data: null };
    }
  },
  deleteDocument: async (id: string) => {
    try {
      return await request(`/api/admin/legal-documents/${id}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete legal document error:', error);
      return { code: 500, message: error.message || '删除协议文档失败', data: null };
    }
  },
  publishDocument: async (id: string) => {
    try {
      return await request(`/api/admin/legal-documents/${id}/publish`, 'POST');
    } catch (error: any) {
      console.error('Publish legal document error:', error);
      return { code: 500, message: error.message || '发布协议文档失败', data: null };
    }
  },
};

// ========== 文档管理 API ==========
export const documentAPI = {
  // 获取文档列表
  getDocuments: async (params?: { page?: number; limit?: number; keyword?: string; type?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.keyword) queryParams.keyword = params.keyword;
      if (params?.type) queryParams.type = params.type;
      return await request('/api/documents', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get documents error:', error);
      return {
        code: 500,
        message: error.message || '获取文档列表失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },

  // 创建文档
  createDocument: async (data: any) => {
    try {
      return await request('/api/documents', 'POST', data);
    } catch (error: any) {
      console.error('Create document error:', error);
      return { code: 500, message: error.message || '创建文档失败', data: null };
    }
  },

  // 更新文档
  updateDocument: async (id: string, data: any) => {
    try {
      return await request(`/api/documents/${id}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update document error:', error);
      return { code: 500, message: error.message || '更新文档失败', data: null };
    }
  },

  // 删除文档
  deleteDocument: async (id: string) => {
    try {
      return await request(`/api/documents/${id}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete document error:', error);
      return { code: 500, message: error.message || '删除文档失败', data: null };
    }
  }
};

// ========== 订阅管理 API（对接 /api/admin/products/plans）==========
export const subscriptionAPI = {
  // 获取套餐列表
  getPlans: async (params?: { page?: number; limit?: number; type?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.type) queryParams.type = params.type;
      const response = await request('/api/admin/products/plans', 'GET', queryParams);
      if (response.code === 200 && response.data) {
        const pageData = response.data;
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: pageData.total || 0,
            pages: pageData.pages || 0,
            current: pageData.current || params?.page || 1,
            size: pageData.size || params?.limit || 20,
            records: pageData.records || []
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get plans error:', error);
      return {
        code: 500,
        message: error.message || '获取套餐列表失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },

  // 创建套餐
  createPlan: async (data: any) => {
    try {
      return await request('/api/admin/products/plans', 'POST', data);
    } catch (error: any) {
      console.error('Create plan error:', error);
      return { code: 500, message: error.message || '创建套餐失败', data: null };
    }
  },

  // 更新套餐
  updatePlan: async (id: string, data: any) => {
    try {
      return await request(`/api/admin/products/plans/${id}`, 'PATCH', data);
    } catch (error: any) {
      console.error('Update plan error:', error);
      return { code: 500, message: error.message || '更新套餐失败', data: null };
    }
  },

  // 删除套餐
  deletePlan: async (id: string) => {
    try {
      return await request(`/api/admin/products/plans/${id}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete plan error:', error);
      return { code: 500, message: error.message || '删除套餐失败', data: null };
    }
  },

  activatePlan: async (id: string) => {
    try {
      return await request(`/api/admin/products/plans/${id}/activate`, 'POST');
    } catch (error: any) {
      console.error('Activate plan error:', error);
      return { code: 500, message: error.message || '启用套餐失败', data: null };
    }
  },

  deactivatePlan: async (id: string) => {
    try {
      return await request(`/api/admin/products/plans/${id}/deactivate`, 'POST');
    } catch (error: any) {
      console.error('Deactivate plan error:', error);
      return { code: 500, message: error.message || '停用套餐失败', data: null };
    }
  }
};

// ========== 姓氏文化审核 API ==========
export const surnameAPI = {
  // 获取待审核内容
  getPendingContributions: async (params?: { page?: number; limit?: number; type?: string }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20
      };
      if (params?.type) queryParams.type = params.type;
      return await request('/api/surname/contributions/pending', 'GET', queryParams);
    } catch (error: any) {
      console.error('Get pending contributions error:', error);
      return {
        code: 500,
        message: error.message || '获取待审核内容失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },

  // 审核贡献
  reviewContribution: async (contributionId: string, action: 'approve' | 'reject', reviewNotes?: string) => {
    try {
      return await request(`/api/surname/contributions/${contributionId}/review`, 'POST', {
        action,
        reviewNotes
      });
    } catch (error: any) {
      console.error('Review contribution error:', error);
      return { code: 500, message: error.message || '审核失败', data: null };
    }
  }
};

// ========== 社区视频审核 API（对接 moderation 队列）==========
export const videoReviewAPI = {
  // 获取待审核视频
  getPendingVideos: async (params?: { page?: number; limit?: number }) => {
    try {
      const queryParams: any = {
        page: params?.page || 1,
        limit: params?.limit || 20,
        status: 'reviewing'
      };
      const response = await request('/api/community/admin/moderation/queue', 'GET', queryParams);
      if (response.code === 200 && response.data) {
        const records = (response.data.records || []).filter(
          (item: any) => item.contentType === 'video'
        );
        return {
          code: 200,
          message: response.message || '获取成功',
          data: {
            total: records.length,
            pages: Math.ceil(records.length / (params?.limit || 20)),
            current: params?.page || 1,
            size: params?.limit || 20,
            records
          }
        };
      }
      return response;
    } catch (error: any) {
      console.error('Get pending videos error:', error);
      return {
        code: 500,
        message: error.message || '获取待审核视频失败',
        data: { total: 0, pages: 0, current: 1, size: 20, records: [] }
      };
    }
  },

  // 审核视频
  reviewVideo: async (videoId: string, action: 'approve' | 'reject', reason?: string) => {
    try {
      const decision = action === 'approve' ? 'approved' : 'rejected';
      return await request(`/api/community/admin/moderation/video/${videoId}/decision`, 'POST', {
        decision,
        reason: reason || (action === 'reject' ? 'manual_reject' : 'manual_approve'),
        message: reason,
        notifyAuthor: true
      });
    } catch (error: any) {
      console.error('Review video error:', error);
      return { code: 500, message: error.message || '审核失败', data: null };
    }
  }
};

// ========== OPC创业社区 API (v3.4新增) ==========
export const opcAPI = {
  // ========== 学院模块 - 课程相关 ==========

  // 获取课程列表
  getCourses: async (params?: { category?: string; keyword?: string; sort?: string; page?: number; limit?: number }) => {
    try {
      return await request('/api/opc/courses', 'GET', params);
    } catch (error: any) {
      console.error('Get courses error:', error);
      return {
        code: 500,
        message: error.message || '获取课程列表失败',
        data: { list: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 获取课程详情
  getCourseDetail: async (courseId: string) => {
    try {
      return await request(`/api/opc/courses/${courseId}`, 'GET');
    } catch (error: any) {
      console.error('Get course detail error:', error);
      return {
        code: 500,
        message: error.message || '获取课程详情失败',
        data: null
      };
    }
  },

  // 课程报名/购买
  enrollCourse: async (data: { courseId: string; paymentMethod?: string }) => {
    try {
      return await request('/api/opc/courses/enroll', 'POST', data);
    } catch (error: any) {
      console.error('Enroll course error:', error);
      return {
        code: 500,
        message: error.message || '课程报名失败',
        data: null
      };
    }
  },

  // 获取我的课程
  getMyCourses: async (params?: { page?: number; limit?: number }) => {
    try {
      return await request('/api/opc/my-courses', 'GET', params);
    } catch (error: any) {
      console.error('Get my courses error:', error);
      return {
        code: 500,
        message: error.message || '获取我的课程失败',
        data: { list: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // ========== AI工坊模块 ==========

  // 获取AI工具列表
  getAITools: async () => {
    try {
      return await request('/api/opc/ai-tools', 'GET');
    } catch (error: any) {
      console.error('Get AI tools error:', error);
      return {
        code: 500,
        message: error.message || '获取AI工具列表失败',
        data: []
      };
    }
  },

  // 使用AI工具
  useAITool: async (toolId: string, data: { input: string; params?: any }) => {
    try {
      return await request(`/api/opc/ai-tools/${toolId}/use`, 'POST', data);
    } catch (error: any) {
      console.error('Use AI tool error:', error);
      return {
        code: 500,
        message: error.message || '使用AI工具失败',
        data: null
      };
    }
  },

  // AI使用记录
  getAIUsageHistory: async (params?: { page?: number; limit?: number }) => {
    try {
      return await request('/api/opc/ai-usage-history', 'GET', params);
    } catch (error: any) {
      console.error('Get AI usage history error:', error);
      return {
        code: 500,
        message: error.message || '获取AI使用记录失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // ========== 服务市场模块 ==========

  // 获取服务列表
  getServices: async (params?: { category?: string; page?: number; limit?: number }) => {
    try {
      return await request('/api/opc/services', 'GET', params);
    } catch (error: any) {
      console.error('Get services error:', error);
      return {
        code: 500,
        message: error.message || '获取服务列表失败',
        data: { list: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 发布服务
  publishService: async (data: { title: string; description: string; category: string; price: number; deliveryDays: number; images?: string[] }) => {
    try {
      return await request('/api/opc/services', 'POST', data);
    } catch (error: any) {
      console.error('Publish service error:', error);
      return {
        code: 500,
        message: error.message || '发布服务失败',
        data: null
      };
    }
  },

  // 服务下单
  orderService: async (serviceId: string, data?: any) => {
    try {
      return await request(`/api/opc/services/${serviceId}/order`, 'POST', data);
    } catch (error: any) {
      console.error('Order service error:', error);
      return {
        code: 500,
        message: error.message || '服务下单失败',
        data: null
      };
    }
  },

  // 评价服务
  reviewService: async (serviceId: string, data: { rating: number; comment?: string; serviceRating?: number; deliveryRating?: number }) => {
    try {
      return await request(`/api/opc/services/${serviceId}/review`, 'POST', data);
    } catch (error: any) {
      console.error('Review service error:', error);
      return {
        code: 500,
        message: error.message || '评价服务失败',
        data: null
      };
    }
  },

  // ========== 创业中心模块 ==========

  // 创业数据看板
  getDashboard: async () => {
    try {
      return await request('/api/opc/dashboard', 'GET');
    } catch (error: any) {
      console.error('Get dashboard error:', error);
      return {
        code: 500,
        message: error.message || '获取数据看板失败',
        data: null
      };
    }
  },

  // 收入明细
  getIncome: async (params?: { type?: string; month?: string; page?: number; limit?: number }) => {
    try {
      return await request('/api/opc/income', 'GET', params);
    } catch (error: any) {
      console.error('Get income error:', error);
      return {
        code: 500,
        message: error.message || '获取收入明细失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 我的订单
  getOrders: async (params?: { type?: string; status?: string; page?: number; limit?: number }) => {
    try {
      return await request('/api/opc/orders', 'GET', params);
    } catch (error: any) {
      console.error('Get orders error:', error);
      return {
        code: 500,
        message: error.message || '获取订单列表失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 用户等级信息
  getLevelInfo: async () => {
    try {
      return await request('/api/opc/level', 'GET');
    } catch (error: any) {
      console.error('Get level info error:', error);
      return {
        code: 500,
        message: error.message || '获取等级信息失败',
        data: null
      };
    }
  }
};

// ========== 打车服务 API (v3.4新增) ==========
export const taxiAPI = {
  // ========== 订单管理 ==========

  // 创建订单
  createOrder: async (data: { userId: string; startLocation: string; endLocation: string }) => {
    try {
      return await request('/api/order/create', 'POST', data);
    } catch (error: any) {
      console.error('Create taxi order error:', error);
      return {
        code: 500,
        message: error.message || '创建订单失败',
        data: null
      };
    }
  },

  // 司机接单
  acceptOrder: async (orderId: string, driverId: string) => {
    try {
      return await request(`/api/order/${orderId}/accept`, 'POST', { driverId });
    } catch (error: any) {
      console.error('Accept order error:', error);
      return {
        code: 500,
        message: error.message || '司机接单失败',
        data: null
      };
    }
  },

  // 完成订单
  completeOrder: async (orderId: string, driverId: string) => {
    try {
      return await request(`/api/order/${orderId}/complete`, 'POST', { driverId });
    } catch (error: any) {
      console.error('Complete order error:', error);
      return {
        code: 500,
        message: error.message || '完成订单失败',
        data: null
      };
    }
  },

  // 取消订单
  cancelOrder: async (orderId: string) => {
    try {
      return await request(`/api/order/${orderId}/cancel`, 'POST');
    } catch (error: any) {
      console.error('Cancel order error:', error);
      return {
        code: 500,
        message: error.message || '取消订单失败',
        data: null
      };
    }
  },

  // ========== 评价管理 ==========

  // 提交订单评价
  submitReview: async (data: { orderId: string; reviewerId: string; revieweeId: string; rating: number; comment?: string; reviewType: string }) => {
    try {
      return await request('/api/review/submit', 'POST', data);
    } catch (error: any) {
      console.error('Submit review error:', error);
      return {
        code: 500,
        message: error.message || '提交评价失败',
        data: null
      };
    }
  }
};

// ========== 机构管理 API (v4.6新增) ==========
export const institutionAPI = {
  // 创建机构
  createInstitution: async (data: any) => {
    try {
      return await request('/api/user/institutions', 'POST', data);
    } catch (error: any) {
      console.error('Create institution error:', error);
      return {
        code: 500,
        message: error.message || '创建机构失败',
        data: null
      };
    }
  },

  // 获取机构详情
  getInstitutionDetail: async (id: string) => {
    try {
      return await request(`/api/user/institutions/${id}`, 'GET');
    } catch (error: any) {
      console.error('Get institution detail error:', error);
      return {
        code: 500,
        message: error.message || '获取机构详情失败',
        data: null
      };
    }
  },

  // 更新机构信息
  updateInstitution: async (id: string, data: any) => {
    try {
      return await request(`/api/user/institutions/${id}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update institution error:', error);
      return {
        code: 500,
        message: error.message || '更新机构信息失败',
        data: null
      };
    }
  },

  // 删除机构
  deleteInstitution: async (id: string) => {
    try {
      return await request(`/api/user/institutions/${id}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete institution error:', error);
      return {
        code: 500,
        message: error.message || '删除机构失败',
        data: null
      };
    }
  },

  // 搜索机构
  searchInstitutions: async (params?: { keyword?: string; page?: number; limit?: number }) => {
    try {
      return await request('/api/user/institutions/search', 'GET', params);
    } catch (error: any) {
      console.error('Search institutions error:', error);
      return {
        code: 500,
        message: error.message || '搜索机构失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 提交机构认证
  submitCertification: async (id: string, data: any) => {
    try {
      return await request(`/api/user/institutions/${id}/certification`, 'PUT', data);
    } catch (error: any) {
      console.error('Submit certification error:', error);
      return {
        code: 500,
        message: error.message || '提交认证失败',
        data: null
      };
    }
  },

  // 获取机构成员列表
  getInstitutionMembers: async (id: string, params?: { page?: number; limit?: number }) => {
    try {
      return await request(`/api/user/institutions/${id}/members`, 'GET', params);
    } catch (error: any) {
      console.error('Get institution members error:', error);
      return {
        code: 500,
        message: error.message || '获取成员列表失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 添加机构成员
  addMember: async (id: string, data: any) => {
    try {
      return await request(`/api/user/institutions/${id}/members`, 'POST', data);
    } catch (error: any) {
      console.error('Add member error:', error);
      return {
        code: 500,
        message: error.message || '添加成员失败',
        data: null
      };
    }
  },

  // 更新成员角色
  updateMemberRole: async (id: string, memberId: string, data: any) => {
    try {
      return await request(`/api/user/institutions/${id}/members/${memberId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update member role error:', error);
      return {
        code: 500,
        message: error.message || '更新成员角色失败',
        data: null
      };
    }
  },

  // 移除机构成员
  removeMember: async (id: string, memberId: string) => {
    try {
      return await request(`/api/user/institutions/${id}/members/${memberId}`, 'DELETE');
    } catch (error: any) {
      console.error('Remove member error:', error);
      return {
        code: 500,
        message: error.message || '移除成员失败',
        data: null
      };
    }
  },

  // 获取机构门店列表
  getInstitutionStores: async (id: string, params?: { page?: number; limit?: number }) => {
    try {
      return await request(`/api/user/institutions/${id}/stores`, 'GET', params);
    } catch (error: any) {
      console.error('Get institution stores error:', error);
      return {
        code: 500,
        message: error.message || '获取门店列表失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  },

  // 创建门店
  createStore: async (id: string, data: any) => {
    try {
      return await request(`/api/user/institutions/${id}/stores`, 'POST', data);
    } catch (error: any) {
      console.error('Create store error:', error);
      return {
        code: 500,
        message: error.message || '创建门店失败',
        data: null
      };
    }
  },

  // 获取门店详情
  getStoreDetail: async (storeId: string) => {
    try {
      return await request(`/api/user/institutions/stores/${storeId}`, 'GET');
    } catch (error: any) {
      console.error('Get store detail error:', error);
      return {
        code: 500,
        message: error.message || '获取门店详情失败',
        data: null
      };
    }
  },

  // 更新门店信息
  updateStore: async (storeId: string, data: any) => {
    try {
      return await request(`/api/user/institutions/stores/${storeId}`, 'PUT', data);
    } catch (error: any) {
      console.error('Update store error:', error);
      return {
        code: 500,
        message: error.message || '更新门店信息失败',
        data: null
      };
    }
  },

  // 删除门店
  deleteStore: async (storeId: string) => {
    try {
      return await request(`/api/user/institutions/stores/${storeId}`, 'DELETE');
    } catch (error: any) {
      console.error('Delete store error:', error);
      return {
        code: 500,
        message: error.message || '删除门店失败',
        data: null
      };
    }
  },

  // 获取我的机构列表
  getMyInstitutions: async () => {
    try {
      return await request('/api/user/me/institutions', 'GET');
    } catch (error: any) {
      console.error('Get my institutions error:', error);
      return {
        code: 500,
        message: error.message || '获取我的机构列表失败',
        data: []
      };
    }
  },

  // 获取默认机构
  getDefaultInstitution: async () => {
    try {
      return await request('/api/user/me/default-institution', 'GET');
    } catch (error: any) {
      console.error('Get default institution error:', error);
      return {
        code: 500,
        message: error.message || '获取默认机构失败',
        data: null
      };
    }
  },

  // 设置默认机构
  setDefaultInstitution: async (id: string) => {
    try {
      return await request('/api/user/me/default-institution', 'POST', { institutionId: id });
    } catch (error: any) {
      console.error('Set default institution error:', error);
      return {
        code: 500,
        message: error.message || '设置默认机构失败',
        data: null
      };
    }
  },

  // 申请加入机构
  joinInstitution: async (id: string) => {
    try {
      return await request(`/api/user/me/institutions/${id}/join`, 'POST');
    } catch (error: any) {
      console.error('Join institution error:', error);
      return {
        code: 500,
        message: error.message || '申请加入机构失败',
        data: null
      };
    }
  },

  // 离开机构
  leaveInstitution: async (id: string) => {
    try {
      return await request(`/api/user/me/institutions/${id}/leave`, 'POST');
    } catch (error: any) {
      console.error('Leave institution error:', error);
      return {
        code: 500,
        message: error.message || '离开机构失败',
        data: null
      };
    }
  },

  // 获取机构订单
  getInstitutionOrders: async (institutionId: string, params?: { page?: number; limit?: number; status?: string }) => {
    try {
      return await request(`/api/order/orders/institution/${institutionId}`, 'GET', params);
    } catch (error: any) {
      console.error('Get institution orders error:', error);
      return {
        code: 500,
        message: error.message || '获取机构订单失败',
        data: { records: [], total: 0, page: 1, limit: 20 }
      };
    }
  }
};